import React, { useState, useEffect, useRef } from 'react';
import { colors, glassTextures, windowTypes, raamtypes, getColorById } from '../data/mockData';
import ColorPicker from './ColorPicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { CardDescription } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { useTranslation } from '../context/TranslationContext';
import { getDimensionInputWidth, getDimensionLabelBoxWidth } from '../lib/utils';
import {
  MIN_SIDE_PANEL_WIDTH_MM,
  MIN_DOOR_SECTION_WIDTH_MM,
} from '../lib/partWidthDistribution';
import {
  FRAME_EDGE_STROKE_DARK,
  FRAME_EDGE_STROKE_DEFAULT,
  isLightFrameColor,
  shouldUsePrintableFrameEdges,
} from '../lib/configuratorColors';
import { renderOverheadOpeningVisual } from '../lib/overheadOpeningVisual';

const DEFAULT_OVERHEAD_PART_WINDOW_TYPES = ['fixed-sash', 'fixed-sash'];

const DEFAULT_VENTILATOR_SLIDING = {
  left: false,
  right: false,
  centerLeft: false,
  centerRight: false,
  centerCenter: false,
  centerThird: false,
};

const syncStringArrayState = (setter, next) => {
  setter((prev) => {
    if (prev.length === next.length && prev.every((value, index) => value === next[index])) {
      return prev;
    }
    return next;
  });
};

const DoorDrawingBoard = ({ 
  width, 
  height, 
  designType = 'no-panel',
  doorType = 'normal',
  doorStyle, // For voordeur: 'style-1', 'style-2', etc.
  numberOfParts: numberOfPartsProp,
  onNumberOfPartsChange,
  slidingPosition = 'left', // For sliding doors: 'left', 'right', or 'center'
  handlePosition = 'left',
  exteriorColor = 'anthracite',
  interiorColor = 'white',
  glassTexture = 'helder-glas',
  leftPanelGlassTexture,
  rightPanelGlassTexture,
  leftGlassTexture, // For inner left frame glass in sliding doors
  rightGlassTexture, // For inner right frame glass in sliding doors
  woodJoint = 'joint-45',
  // Parapet options
  parapet = 'no-parapet',
  parapetHeight = 250,
  panelType = 'sandwich-no-mullions', // Panel type for parapet (glass, sandwich-no-mullions, horizontal-mullions, vertical-mullions)
  singlePane = false, // If true, render only one inner frame instead of two
  onWidthChange,
  onHeightChange,
  leftPanelWidth: leftPanelWidthProp,
  rightPanelWidth: rightPanelWidthProp,
  onLeftPanelWidthChange,
  onRightPanelWidthChange,
  onDoorSectionWidthChange,
  leftPanelWindowType: leftPanelWindowTypeProp,
  rightPanelWindowType: rightPanelWindowTypeProp,
  onLeftPanelWindowTypeChange,
  onRightPanelWindowTypeChange,
  leftPanelParapet: leftPanelParapetProp = false,
  rightPanelParapet: rightPanelParapetProp = false,
  leftPanelParapetHeight: leftPanelParapetHeightProp,
  rightPanelParapetHeight: rightPanelParapetHeightProp,
  leftPanelParapetColor: leftPanelParapetColorProp,
  rightPanelParapetColor: rightPanelParapetColorProp,
  leftPanelParapetPanelType: leftPanelParapetPanelTypeProp,
  rightPanelParapetPanelType: rightPanelParapetPanelTypeProp,
  onLeftPanelParapetChange,
  onRightPanelParapetChange,
  onLeftPanelParapetHeightChange,
  onRightPanelParapetHeightChange,
  onLeftPanelParapetColorChange,
  onRightPanelParapetColorChange,
  onLeftPanelParapetPanelTypeChange,
  onRightPanelParapetPanelTypeChange,
  sidePanelOverheadEnabled = false,
  leftPanelOverhead: leftPanelOverheadProp = false,
  rightPanelOverhead: rightPanelOverheadProp = false,
  leftPanelOverheadWindowType: leftPanelOverheadWindowTypeProp = 'fixed-sash',
  rightPanelOverheadWindowType: rightPanelOverheadWindowTypeProp = 'fixed-sash',
  leftPanelOverheadHeight: leftPanelOverheadHeightProp,
  rightPanelOverheadHeight: rightPanelOverheadHeightProp,
  onLeftPanelOverheadChange,
  onRightPanelOverheadChange,
  onLeftPanelOverheadWindowTypeChange,
  onRightPanelOverheadWindowTypeChange,
  onLeftPanelOverheadHeightChange,
  onRightPanelOverheadHeightChange,
  overheadPartWindowTypes = DEFAULT_OVERHEAD_PART_WINDOW_TYPES,
  overheadPartWidths = [],
  onOverheadPartWindowTypeChange,
  onOverheadPartWidthChange,
  overheadHeight: overheadHeightProp,
  onOverheadHeightChange,
  onParapetHeightChange,
  parapetColor,
  minDimension = 500, // Optional: minimum dimension constraint (default for Schuifpui)
  maxDimension = 6000, // Optional: maximum dimension constraint (default for Schuifpui)
  minWidth, // Optional: width-specific min (falls back to minDimension)
  maxWidth, // Optional: width-specific max (falls back to maxDimension)
  minHeight, // Optional: height-specific min (falls back to minDimension)
  maxHeight, // Optional: height-specific max (falls back to maxDimension)
  maxDoorLeafWidth, // Optional: max width for the main door leaf section (falls back to maxWidth)
  minDoorLeafWidth, // Optional: min width for the main door leaf section (no extra limit when unset)
  minSidePanelWidth, // Optional: min width for each side panel (falls back to MIN_SIDE_PANEL_WIDTH_MM)
  maxDoorHeight, // Optional: max height for the main door section (excludes top light; falls back to maxHeight)
  minDoorHeight, // Optional: min height for the main door section when top light is on; top light fills the remainder
  minOverheadHeight, // Optional: fixed min for top light (adjustments go to main panel instead)
  maxSidePanelOverheadHeight, // Optional: max height (mm) for terrasdeur side-panel overhead
  onGlassTextureChange,
  ventilatorSliding: ventilatorSlidingProp,
  onVentilatorSlidingChange,
}) => {
  const { t, language } = useTranslation();
  
  // Helper function to translate window type names
  const translateWindowTypeName = (windowTypeId) => {
    const translationMap = {
      'fixed-sash': t('options.fixedSash'),
      'fixed-glass': t('options.fixedGlass'),
      'tilt-turn-left': t('options.tiltTurnLeft'),
      'tilt-turn-right': t('options.tiltTurnRight'),
      'tilt-only': t('options.tiltOnly'),
      'fixed-panel': t('options.fixedPanel')
    };
    return translationMap[windowTypeId] || windowTypeId;
  };
  
  // Local state for editable dimension inputs
  const [widthInput, setWidthInput] = useState(String(width));
  const [heightInput, setHeightInput] = useState(String(height));
  const [dimensionError, setDimensionError] = useState(null);
  const [inputOverheadPartWidths, setInputOverheadPartWidths] = useState([]);
  
  // Refs to prevent infinite loops when updating from props
  const isUpdatingWidthRef = useRef(false);
  const isUpdatingHeightRef = useRef(false);
  
  // Panel width constraints (effective max also limited by width - MIN_DOOR_SECTION_WIDTH so door section stays >= 400)
  const MIN_PANEL_WIDTH = minSidePanelWidth ?? MIN_SIDE_PANEL_WIDTH_MM;
  const MAX_PANEL_WIDTH = 1000;
  const MIN_DOOR_SECTION_WIDTH = MIN_DOOR_SECTION_WIDTH_MM;
  
  // Determine panel states
  const hasLeftPanel = designType === 'left-panel' || designType === 'both-panels';
  const hasRightPanel = designType === 'right-panel' || designType === 'both-panels';
  
  // Calculate default panel widths if not provided
  const defaultLeftPanelWidth = hasLeftPanel ? (hasRightPanel ? width * 0.25 : width * 0.3) : 0;
  const defaultRightPanelWidth = hasRightPanel ? (hasLeftPanel ? width * 0.25 : width * 0.3) : 0;
  const leftPanelWidthMm = leftPanelWidthProp !== undefined ? leftPanelWidthProp : defaultLeftPanelWidth;
  const rightPanelWidthMm = rightPanelWidthProp !== undefined ? rightPanelWidthProp : defaultRightPanelWidth;
  
  // Calculate door section width for initial state
  const initialDoorSectionWidthMm = (hasLeftPanel || hasRightPanel) 
    ? width - (hasLeftPanel ? leftPanelWidthMm : 0) - (hasRightPanel ? rightPanelWidthMm : 0)
    : 0;
  
  // Local state for panel width inputs
  const [leftPanelWidthInput, setLeftPanelWidthInput] = useState(String(Math.round(leftPanelWidthMm)));
  const [rightPanelWidthInput, setRightPanelWidthInput] = useState(String(Math.round(rightPanelWidthMm)));
  
  // Local state for door section width input (when panels are present)
  const [doorSectionWidthInput, setDoorSectionWidthInput] = useState(String(Math.round(initialDoorSectionWidthMm)));
  
  // Local state for parapet height input (in mm)
  const [parapetHeightInput, setParapetHeightInput] = useState(String(Math.round(parapetHeight || 250)));
  
  // Local state for side panel parapet height inputs (in mm)
  const defaultSidePanelParapetHeight = 250;
  const leftPanelParapetHeightMm = leftPanelParapetHeightProp !== undefined ? leftPanelParapetHeightProp : defaultSidePanelParapetHeight;
  const rightPanelParapetHeightMm = rightPanelParapetHeightProp !== undefined ? rightPanelParapetHeightProp : defaultSidePanelParapetHeight;
  const [leftPanelParapetHeightInput, setLeftPanelParapetHeightInput] = useState(String(Math.round(leftPanelParapetHeightMm)));
  const [rightPanelParapetHeightInput, setRightPanelParapetHeightInput] = useState(String(Math.round(rightPanelParapetHeightMm)));
  const [leftPanelOverheadHeightInput, setLeftPanelOverheadHeightInput] = useState('300');
  const [rightPanelOverheadHeightInput, setRightPanelOverheadHeightInput] = useState('300');
  
  // Overhead height constraints
  const MIN_OVERHEAD_HEIGHT = 200;
  const MAX_OVERHEAD_HEIGHT = 500;
  const defaultOverheadHeight = 300;
  const overheadHeightMm = overheadHeightProp !== undefined ? overheadHeightProp : defaultOverheadHeight;
  const [overheadHeightInput, setOverheadHeightInput] = useState(String(Math.round(overheadHeightMm)));
  // Inner-frame (main door section) height = total height - overhead height
  const [innerFrameHeightInput, setInnerFrameHeightInput] = useState(String(Math.round(height - overheadHeightMm)));
  
  // State for overhead part hover - must be declared at top level
  const [hoveredOverheadPartIndex, setHoveredOverheadPartIndex] = useState(null);
  const [selectedOverheadPartIndex, setSelectedOverheadPartIndex] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  // State for panel hover and selection
  const [hoveredLeftPanel, setHoveredLeftPanel] = useState(false);
  const [hoveredRightPanel, setHoveredRightPanel] = useState(false);
  const [selectedLeftPanel, setSelectedLeftPanel] = useState(false);
  const [selectedRightPanel, setSelectedRightPanel] = useState(false);
  const [leftPanelSettingsOpen, setLeftPanelSettingsOpen] = useState(false);
  const [rightPanelSettingsOpen, setRightPanelSettingsOpen] = useState(false);
  
  // Panel window types - default to fixed-sash if not provided
  const leftPanelWindowType = leftPanelWindowTypeProp || 'fixed-sash';
  const rightPanelWindowType = rightPanelWindowTypeProp || 'fixed-sash';
  
  // Panel parapet states - default to false if not provided
  const leftPanelHasParapet = leftPanelParapetProp === true || leftPanelParapetProp === 'with-parapet';
  const rightPanelHasParapet = rightPanelParapetProp === true || rightPanelParapetProp === 'with-parapet';
  
  // Panel parapet panel types - default to main panelType if not provided
  const leftPanelParapetPanelType = leftPanelParapetPanelTypeProp || panelType;
  const rightPanelParapetPanelType = rightPanelParapetPanelTypeProp || panelType;
  
  // Side-panel window types with inner sash use the same visual path as overhead (via overheadPartHasInnerFrame).
  
  // State for inner frame sliding section selection (when slidingPosition === 'left' or 'right')
  // Simplified approach - modal open is directly controlled by selectedInnerFrame
  const [innerFrameSlidingSection, setInnerFrameSlidingSection] = useState('left'); // 'left' or 'right'
  const [hoveredInnerFrame, setHoveredInnerFrame] = useState(null); // 'left' or 'right'
  const [selectedInnerFrame, setSelectedInnerFrame] = useState(null); // 'left' or 'right' - when not null, modal is open
  const [localVentilatorSliding, setLocalVentilatorSliding] = useState(DEFAULT_VENTILATOR_SLIDING);
  const ventilatorSliding = ventilatorSlidingProp ?? localVentilatorSliding;
  const setVentilatorSlidingKey = (key, value) => {
    const next = { ...ventilatorSliding, [key]: value };
    if (onVentilatorSlidingChange) onVentilatorSlidingChange(next);
    else setLocalVentilatorSliding(next);
  };
  const ventilatorSlidingLeft = ventilatorSliding.left;
  const ventilatorSlidingRight = ventilatorSliding.right;
  const setVentilatorSlidingLeft = (value) => setVentilatorSlidingKey('left', value);
  const setVentilatorSlidingRight = (value) => setVentilatorSlidingKey('right', value);
  
  // State for inner frame sliding section selection (when slidingPosition === 'center')
  // Left section (first 2 frames) and right section (last 2 frames) have separate states
  const [innerFrameSlidingSectionLeft, setInnerFrameSlidingSectionLeft] = useState('left'); // 'left' or 'right' for left section
  const [innerFrameSlidingSectionRight, setInnerFrameSlidingSectionRight] = useState('right'); // 'left' or 'right' for right section (default 'right')
  const [hoveredInnerFrameCenter, setHoveredInnerFrameCenter] = useState(null); // 'left' or 'right' to indicate which section
  const [selectedInnerFrameCenter, setSelectedInnerFrameCenter] = useState(null); // 'left' or 'right' - when not null, modal is open
  const ventilatorSlidingCenterLeft = ventilatorSliding.centerLeft;
  const ventilatorSlidingCenterRight = ventilatorSliding.centerRight;
  const ventilatorSlidingCenterCenter = ventilatorSliding.centerCenter;
  const ventilatorSlidingCenterThird = ventilatorSliding.centerThird;
  const setVentilatorSlidingCenterLeft = (value) => setVentilatorSlidingKey('centerLeft', value);
  const setVentilatorSlidingCenterRight = (value) => setVentilatorSlidingKey('centerRight', value);
  const setVentilatorSlidingCenterCenter = (value) => setVentilatorSlidingKey('centerCenter', value);
  const setVentilatorSlidingCenterThird = (value) => setVentilatorSlidingKey('centerThird', value);
  
  // State for voordeur style-1 and style-3 middle section parts (3 or 4)
  const [middleSectionParts, setMiddleSectionParts] = useState(
    numberOfPartsProp ?? 3
  ); // 3 or 4
  const [hoveredMiddleSection, setHoveredMiddleSection] = useState(false);
  const [selectedMiddleSection, setSelectedMiddleSection] = useState(false); // When true, dialog is open
  const [middleSectionSettingsOpen, setMiddleSectionSettingsOpen] = useState(false);

  // Auto-clear dimension validation message after a few seconds
  useEffect(() => {
    if (!dimensionError) return;
    const timer = setTimeout(() => {
      setDimensionError(null);
    }, 7000);
    return () => clearTimeout(timer);
  }, [dimensionError]);

  useEffect(() => {
    const parts = Number(numberOfPartsProp);
    if (parts === 3 || parts === 4) {
      setMiddleSectionParts(parts);
      setStyle6Parts(parts);
    }
  }, [numberOfPartsProp, doorStyle]);
  
  // State for voordeur style-2 (glass or panel)
  const [style2Type, setStyle2Type] = useState('glass'); // 'glass' or 'panel'
  const lastGlassTextureRef = useRef(glassTexture);
  const [hoveredStyle2, setHoveredStyle2] = useState(false);
  const [selectedStyle2, setSelectedStyle2] = useState(false); // When true, dialog is open
  const [style2SettingsOpen, setStyle2SettingsOpen] = useState(false);
  
  // State for voordeur style-3 (glass or panel)
  const [style3Type, setStyle3Type] = useState('glass'); // 'glass' or 'panel'
  
  // State for voordeur style-4 left section (glass or panel)
  const [style4LeftType, setStyle4LeftType] = useState('glass'); // 'glass' or 'panel'
  const [hoveredStyle4Left, setHoveredStyle4Left] = useState(false);
  const [selectedStyle4Left, setSelectedStyle4Left] = useState(false); // When true, dialog is open
  const [style4LeftSettingsOpen, setStyle4LeftSettingsOpen] = useState(false);
  
  // State for voordeur style-6 (number of parts)
  const [style6Parts, setStyle6Parts] = useState(3); // 3 or 4 parts
  const [hoveredStyle6, setHoveredStyle6] = useState(false);
  const [selectedStyle6, setSelectedStyle6] = useState(false); // When true, dialog is open
  const [style6SettingsOpen, setStyle6SettingsOpen] = useState(false);
  
  // Keep panel width inputs in sync when props change or when designType/width changes (to update defaults)
  useEffect(() => {
    if (leftPanelWidthProp !== undefined) {
      setLeftPanelWidthInput(String(Math.round(leftPanelWidthProp)));
    } else if (hasLeftPanel) {
      // Update input when prop is undefined but panel is selected (use calculated default)
      setLeftPanelWidthInput(String(Math.round(leftPanelWidthMm)));
    }
  }, [leftPanelWidthProp, hasLeftPanel, leftPanelWidthMm]);

  useEffect(() => {
    if (rightPanelWidthProp !== undefined) {
      setRightPanelWidthInput(String(Math.round(rightPanelWidthProp)));
    } else if (hasRightPanel) {
      // Update input when prop is undefined but panel is selected (use calculated default)
      setRightPanelWidthInput(String(Math.round(rightPanelWidthMm)));
    }
  }, [rightPanelWidthProp, hasRightPanel, rightPanelWidthMm]);

  // Keep local inputs in sync when props change
  useEffect(() => {
    setWidthInput(String(width));
  }, [width]);

  useEffect(() => {
    setHeightInput(String(height));
  }, [height]);
  
  // Keep door section width input in sync when calculated value changes
  useEffect(() => {
    if (hasLeftPanel || hasRightPanel) {
      const calculated = width - (hasLeftPanel ? leftPanelWidthMm : 0) - (hasRightPanel ? rightPanelWidthMm : 0);
      setDoorSectionWidthInput(String(Math.round(calculated)));
    }
  }, [width, leftPanelWidthMm, rightPanelWidthMm, hasLeftPanel, hasRightPanel]);

  useEffect(() => {
    if (overheadHeightProp !== undefined) {
      setOverheadHeightInput(String(Math.round(overheadHeightProp)));
    }
  }, [overheadHeightProp]);

  // Keep inner-frame (main door section) height input in sync (total height - overhead)
  useEffect(() => {
    setInnerFrameHeightInput(String(Math.round(height - overheadHeightMm)));
  }, [height, overheadHeightMm]);

  // Keep parapet height input in sync when prop changes
  useEffect(() => {
    setParapetHeightInput(String(Math.round(parapetHeight || 250)));
  }, [parapetHeight]);
  
  // Keep side panel parapet height inputs in sync when props change
  useEffect(() => {
    if (leftPanelParapetHeightProp !== undefined) {
      setLeftPanelParapetHeightInput(String(Math.round(leftPanelParapetHeightProp)));
    }
  }, [leftPanelParapetHeightProp]);
  
  useEffect(() => {
    if (rightPanelParapetHeightProp !== undefined) {
      setRightPanelParapetHeightInput(String(Math.round(rightPanelParapetHeightProp)));
    }
  }, [rightPanelParapetHeightProp]);

  useEffect(() => {
    if (leftPanelOverheadHeightProp !== undefined) {
      setLeftPanelOverheadHeightInput(String(Math.round(leftPanelOverheadHeightProp)));
    }
  }, [leftPanelOverheadHeightProp]);

  useEffect(() => {
    if (rightPanelOverheadHeightProp !== undefined) {
      setRightPanelOverheadHeightInput(String(Math.round(rightPanelOverheadHeightProp)));
    }
  }, [rightPanelOverheadHeightProp]);

  // Keep local inputs in sync when props change for overhead part widths
  useEffect(() => {
    const hasOverheadLocal = doorType === 'top-light';
    const overheadPartsLocal = overheadPartWindowTypes && overheadPartWindowTypes.length > 0 ? overheadPartWindowTypes.length : 1;
    const hasLeftPanelLocal = designType === 'left-panel' || designType === 'both-panels';
    const hasRightPanelLocal = designType === 'right-panel' || designType === 'both-panels';
    const leftPanelWidthMmLocal = leftPanelWidthProp !== undefined ? leftPanelWidthProp : (hasLeftPanelLocal ? (hasRightPanelLocal ? width * 0.25 : width * 0.3) : 0);
    const rightPanelWidthMmLocal = rightPanelWidthProp !== undefined ? rightPanelWidthProp : (hasRightPanelLocal ? (hasLeftPanelLocal ? width * 0.25 : width * 0.3) : 0);
    
    if (hasOverheadLocal && overheadPartsLocal > 0) {
      // Calculate normalized widths here to sync with props
      const doorSectionWidthMmLocal = width - (hasLeftPanelLocal ? leftPanelWidthMmLocal : 0) - (hasRightPanelLocal ? rightPanelWidthMmLocal : 0);
      const defaultOverheadPartWidth = Math.floor(doorSectionWidthMmLocal / overheadPartsLocal);
      const normalized = overheadPartWidths && overheadPartWidths.length === overheadPartsLocal
        ? overheadPartWidths
        : Array(overheadPartsLocal).fill(defaultOverheadPartWidth);
      const nextInputWidths = normalized.map((w) => (Number.isFinite(w) ? String(w) : ''));
      syncStringArrayState(setInputOverheadPartWidths, nextInputWidths);
    } else {
      syncStringArrayState(setInputOverheadPartWidths, []);
    }
  }, [
    doorType,
    designType,
    width,
    leftPanelWidthProp,
    rightPanelWidthProp,
    JSON.stringify(overheadPartWindowTypes),
    JSON.stringify(overheadPartWidths),
  ]);

  // Dimension constraints for door - use props if provided, otherwise defaults
  // Width/height-specific constraints (fall back to the shared min/max dimension)
  const MIN_WIDTH = minWidth ?? minDimension;
  const MAX_WIDTH = maxWidth ?? maxDimension;
  const MIN_HEIGHT = minHeight ?? minDimension;
  const MAX_HEIGHT = maxHeight ?? maxDimension;
  // Max/min width for the main door leaf (the section between side panels)
  const MAX_DOOR_LEAF = maxDoorLeafWidth ?? MAX_WIDTH;
  const MIN_DOOR_LEAF = minDoorLeafWidth ?? 0;
  // Max/min height for the main door section (the part below the top light)
  const MAX_DOOR_HEIGHT = maxDoorHeight ?? MAX_HEIGHT;
  const MIN_DOOR_HEIGHT = minDoorHeight ?? 0;
  const FIXED_MIN_OVERHEAD = minOverheadHeight ?? 0;
  // When minDoorHeight is set, overhead bounds are derived from total height (door + top light = total).
  const useDynamicOverheadBounds = MIN_DOOR_HEIGHT > 0;
  const effectiveMinOverhead = useDynamicOverheadBounds
    ? (FIXED_MIN_OVERHEAD > 0 ? FIXED_MIN_OVERHEAD : MIN_OVERHEAD_HEIGHT)
    : MIN_OVERHEAD_HEIGHT;
  const effectiveMaxOverhead = useDynamicOverheadBounds
    ? Math.round(height - MIN_DOOR_HEIGHT)
    : MAX_OVERHEAD_HEIGHT;
  const maxDoorHeightForTotal = useDynamicOverheadBounds
    ? Math.min(MAX_DOOR_HEIGHT, Math.round(height - effectiveMinOverhead))
    : MAX_DOOR_HEIGHT;
  
  // Check if overhead / parapet are active (must be defined early)
  const hasOverhead = doorType === 'top-light';
  const hasParapet = parapet === 'with-parapet';
  
  // Base SVG dimensions
  const baseSvgWidth = 900;
  const baseSvgHeight = 800;

  // Calculate scale to fit door dimensions in SVG (using base dimensions)
  const scale = Math.min(baseSvgWidth / width, baseSvgHeight / height) * 0.8;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;
  
  // Frame thickness - increased a bit more
  const frameThickness = 54 * scale;
  // Vertical divider should be very thin, matching the outer frame border line width
  const verticalDividerWidth = 2 * scale; // Very thin divider matching frame border line
  
  // Panel calculations - Use panel widths in mm, convert to scaled
  const leftPanelWidth = hasLeftPanel ? (leftPanelWidthMm * scale) : 0;
  const rightPanelWidth = hasRightPanel ? (rightPanelWidthMm * scale) : 0;
  const doorSectionWidth = scaledWidth - leftPanelWidth - rightPanelWidth - (hasLeftPanel ? verticalDividerWidth : 0) - (hasRightPanel ? verticalDividerWidth : 0);
  
  // Control whether to render single or double pane
  // For voordeur (all styles 1-6), always use single pane (1 inner frame) - base design
  // Determine if it's voordeur by checking if doorStyle prop exists (only voordeur has doorStyle)
  const isVoordeur = doorStyle !== undefined && doorStyle !== null;
  
  // Identify achterdeur (back door) - uses singlePane=true (regardless of designType to support side panels)
  const isAchterdeur = singlePane === true && !isVoordeur && doorType !== 'sliding';
  const achterdeurRulerOffset = (isAchterdeur && designType === 'no-panel') ? 3 : 0; // Add 3px offset for achterdeur only when no side panels
  // Identify terrasdeur (terrace door) - not voordeur, not achterdeur, not sliding
  const isTerrasdeur = !isVoordeur && !isAchterdeur && doorType !== 'sliding';
  const doorHasSidePanels = hasLeftPanel || hasRightPanel;

  // Calculate ruler positions
  // When normal (no overhead), main ruler is closer to frame (original position ~30px)
  // When overhead is active, main ruler is further right (~83px) to accommodate sub rulers
  // When parapet is active (without overhead), shift main ruler right to give space for parapet sub ruler
  const mainRulerOffset = hasOverhead ? 88 : (hasParapet && !isTerrasdeur ? 50 : 35);
  
  // Main-door parapet height ruler: achterdeur without side panels only (not terrasdeur — Met Boswering)
  const showMainParapetHeightRuler =
    hasParapet && !isTerrasdeur && !(isAchterdeur && doorHasSidePanels);
  const terrasdeurRulerOffset = isTerrasdeur ? 20 : 0; // Add 20px offset for terrasdeur to shift ruler right
  
  // For voordeur style-1, style-5 and style-6, calculate separate offsets for main ruler (shifted right) and sub rulers (slightly shifted right)
  const isVoordeurStyle1Or3 = isVoordeur && doorStyle === 'style-1';
  const isVoordeurStyle5Or6 = isVoordeur && (doorStyle === 'style-5' || doorStyle === 'style-6');
  const isVoordeurWithSubRulers = isVoordeurStyle1Or3 || isVoordeurStyle5Or6;
  // Vertical ruler columns on the right — equal horizontal gap between parallel columns
  const RULER_COLUMN_GAP = 42;
  const TRIPLE_HEIGHT_RULER_GAP = 58; // parapet + overhead/door + total — room for 4-digit labels
  const baseSubRulerOffset = 35 - 15; // Original position: 20px from frame
  const voordeurSubRulersSubRulerOffset = isVoordeurWithSubRulers ? baseSubRulerOffset : (mainRulerOffset - 15);
  const voordeurSubRulersMainRulerOffset = isVoordeurWithSubRulers
    ? (hasOverhead ? baseSubRulerOffset + RULER_COLUMN_GAP * 2 : 92)
    : mainRulerOffset;
  const subRulerOffset = 30; // Sub rulers position when overhead is active (shifted right)
  const overheadSubRulerOffset = (isVoordeurWithSubRulers && hasOverhead)
    ? baseSubRulerOffset + RULER_COLUMN_GAP
    : 48;

  // Resolve column positions for non-voordeur-style layouts (achterdeur/terrasdeur parapet + overhead)
  let parapetHeightRulerOffset = subRulerOffset - 20;
  let resolvedOverheadSubRulerOffset = overheadSubRulerOffset;
  let resolvedMainHeightRulerOffset = voordeurSubRulersMainRulerOffset;

  if (!isVoordeurWithSubRulers) {
    if (hasOverhead && hasParapet && showMainParapetHeightRuler) {
      const gap = TRIPLE_HEIGHT_RULER_GAP;
      parapetHeightRulerOffset = baseSubRulerOffset;
      resolvedOverheadSubRulerOffset = baseSubRulerOffset + gap;
      resolvedMainHeightRulerOffset = baseSubRulerOffset + gap * 2;
    } else if (hasOverhead) {
      resolvedOverheadSubRulerOffset = baseSubRulerOffset;
      resolvedMainHeightRulerOffset = baseSubRulerOffset + RULER_COLUMN_GAP;
    } else if (hasParapet && showMainParapetHeightRuler) {
      parapetHeightRulerOffset = baseSubRulerOffset;
      resolvedMainHeightRulerOffset = baseSubRulerOffset + RULER_COLUMN_GAP;
    }
  }

  const mainDimFontScale = 1;
  const subDimFontScale = 0.75;
  const mainDimLabelWidth = 145;
  const heightDimLabelWidth = 160;
  const mainRulerRightExtent =
    resolvedMainHeightRulerOffset +
    achterdeurRulerOffset +
    terrasdeurRulerOffset +
    20 +
    heightDimLabelWidth;
  const rulerSpace = Math.max(
    hasOverhead && hasParapet && showMainParapetHeightRuler && !isVoordeurWithSubRulers ? 200 : hasOverhead ? 150 : 120,
    mainRulerRightExtent + 24
  );

  const svgWidth = Math.max(baseSvgWidth, scaledWidth + rulerSpace * 2 + 40);
  const svgHeight = baseSvgHeight;

  const startX = (svgWidth - scaledWidth) / 2;
  const startY = (svgHeight - scaledHeight) / 2;
  const doorSectionX = startX + leftPanelWidth + (hasLeftPanel ? verticalDividerWidth : 0);
  
  // Get colors - use getColorById to handle both regular colors and RAL colors
  const exteriorColorData = getColorById(exteriorColor);
  const exteriorColorHex = exteriorColorData?.hex || '#3D3D3D';
  const interiorColorData = getColorById(interiorColor);
  const interiorColorHex = interiorColorData?.hex || '#FFFFFF';
  const usePrintableFrameEdges = shouldUsePrintableFrameEdges(exteriorColorHex, interiorColorHex);
  const frameEdgeStroke = usePrintableFrameEdges ? FRAME_EDGE_STROKE_DARK : FRAME_EDGE_STROKE_DEFAULT;
  const glazingBeadFill = isLightFrameColor(interiorColorHex) ? FRAME_EDGE_STROKE_DEFAULT : '#282C30';
  
  // Get parapet color - use parapetColor if set and not 'interior', otherwise use interiorColor
  const parapetColorHex = (() => {
    if (!parapetColor || parapetColor === 'interior') {
      return interiorColorHex;
    }
    // Try to get color using getColorById first (handles RAL colors and all color IDs)
    const parapetColorData = getColorById(parapetColor);
    if (parapetColorData?.hex) {
      return parapetColorData.hex;
    }
    // Fallback to hardcoded colors for backwards compatibility
    if (parapetColor === 'anthracite') return '#3D3D3D';
    if (parapetColor === 'black') return '#1A1A1A';
    if (parapetColor === 'white') return '#FFFFFF';
    if (parapetColor === 'brown') return '#5C4033';
    if (parapetColor === 'beige') return '#C9B896';
    if (parapetColor === 'green') return '#2C5F2D';
    if (parapetColor === 'blue') return '#4A7C8C';
    if (parapetColor === 'wood') return '#A5784C';
    // Final fallback to interior color
    return interiorColorHex;
  })();
  
  // Helper function to convert color ID to hex
  const getColorHex = (colorId) => {
    if (!colorId || colorId === 'interior') return interiorColorHex;
    if (colorId === 'anthracite') return '#3D3D3D';
    if (colorId === 'black') return '#1A1A1A';
    if (colorId === 'white') return '#FFFFFF';
    if (colorId === 'brown') return '#5C4033';
    if (colorId === 'beige') return '#C9B896';
    if (colorId === 'green') return '#2C5F2D';
    if (colorId === 'blue') return '#4A7C8C';
    if (colorId === 'wood') return '#A5784C';
    const colorData = getColorById(colorId);
    return colorData?.hex || interiorColorHex;
  };
  
  // Calculate side panel parapet colors
  const leftPanelParapetColorHex = leftPanelParapetColorProp 
    ? getColorHex(leftPanelParapetColorProp)
    : parapetColorHex;
  const rightPanelParapetColorHex = rightPanelParapetColorProp
    ? getColorHex(rightPanelParapetColorProp)
    : parapetColorHex;

  // Get glass fill and texture for main door
  const selectedGlassTexture = glassTextures.find(gt => gt.id === glassTexture);
  let glassFill = 'url(#glass-gradient)';
  let glassTexturePattern = null;
  
  // Handle both 'clear' and 'helder-glas' as clear glass
  if (selectedGlassTexture && selectedGlassTexture.id !== 'helder-glas' && selectedGlassTexture.id !== 'clear') {
    glassTexturePattern = `url(#glass-pattern-${selectedGlassTexture.id})`;
  }
  
  // Get glass textures for inner left and right frames (for sliding doors)
  const leftInnerGlassTextureId = leftGlassTexture || glassTexture;
  const rightInnerGlassTextureId = rightGlassTexture || glassTexture;
  const selectedLeftInnerGlassTexture = glassTextures.find(gt => gt.id === leftInnerGlassTextureId);
  const selectedRightInnerGlassTexture = glassTextures.find(gt => gt.id === rightInnerGlassTextureId);
  
  let leftInnerGlassTexturePattern = null;
  let rightInnerGlassTexturePattern = null;
  
  // Handle both 'clear' and 'helder-glas' as clear glass for left inner frame
  if (selectedLeftInnerGlassTexture && selectedLeftInnerGlassTexture.id !== 'helder-glas' && selectedLeftInnerGlassTexture.id !== 'clear') {
    leftInnerGlassTexturePattern = `url(#glass-pattern-${selectedLeftInnerGlassTexture.id})`;
  }
  
  // Handle both 'clear' and 'helder-glas' as clear glass for right inner frame
  if (selectedRightInnerGlassTexture && selectedRightInnerGlassTexture.id !== 'helder-glas' && selectedRightInnerGlassTexture.id !== 'clear') {
    rightInnerGlassTexturePattern = `url(#glass-pattern-${selectedRightInnerGlassTexture.id})`;
  }

  const isDoublePane = !singlePane && !isVoordeur; // If singlePane is true or voordeur, render single pane
  const dividerWidth = isDoublePane ? (6 * scale) : 0; // Central divider only for double pane
  
  // Inner frame calculations - define these first
  const innerFrameThickness = frameThickness * 1.1; // Made wider than outer frame (10% larger)
  const innerFrameInset = frameThickness;
  const innerGlassInset = innerFrameThickness * 0.15; // Further reduced to make black border even smaller
  
  // Overhead (top light) calculations - now innerFrameInset is defined
  // hasOverhead is already defined above
  // Use number of parts from windowTypes array length, default to 1
  const overheadParts = overheadPartWindowTypes && overheadPartWindowTypes.length > 0 ? overheadPartWindowTypes.length : 1;
  
  // Calculate overhead divider Y position directly from overheadHeightMm (in pixels)
  // Overhead starts at startY + innerFrameInset (after the top frame)
  // The divider is positioned at: startY + innerFrameInset + (overheadHeightMm * scale)
  const overheadDividerY = hasOverhead 
    ? (startY + innerFrameInset + (overheadHeightMm * scale))
    : null;
  
  // Calculate inner frame height (reduced when overhead is active)
  // Inner frame starts at overheadDividerY and goes to the bottom
  const innerFrameHeight = hasOverhead && overheadDividerY
    ? (scaledHeight - (overheadDividerY - startY) - innerFrameInset - (frameThickness / 2))
    : (scaledHeight - innerFrameInset - (frameThickness / 2));
  
  // Inner frames always stay at the bottom - calculate Y position from bottom
  const innerFrameY = startY + scaledHeight - innerFrameHeight - (frameThickness / 2);
  // Center mullion: start below transom head rail when overhead is on (avoid vertical line in horizontal bar)
  const doorCenterDividerStartY = hasOverhead ? innerFrameY : startY + frameThickness;
  const doorCenterDividerEndY = startY + scaledHeight - frameThickness;
  const doorCenterDividerHeight = doorCenterDividerEndY - doorCenterDividerStartY;
  
  // Calculate door section width in mm
  const doorSectionWidthMm = width - (hasLeftPanel ? leftPanelWidthMm : 0) - (hasRightPanel ? rightPanelWidthMm : 0);
  
  // Normalized per-overhead-part widths in mm; fall back to equal parts if missing
  const defaultOverheadPartWidth = Math.floor(doorSectionWidthMm / overheadParts);
  const normalizedOverheadPartWidths =
    overheadPartWidths && overheadPartWidths.length === overheadParts
      ? overheadPartWidths
      : Array(overheadParts).fill(defaultOverheadPartWidth);
  const totalOverheadPartWidth = normalizedOverheadPartWidths.reduce((sum, w) => sum + w, 0) || 1;
  
  // Overhead part window types - default to fixed-sash if not provided
  // Ensure array length matches overheadParts, pad or trim as needed
  const currentOverheadPartWindowTypes = (() => {
    if (!overheadPartWindowTypes || overheadPartWindowTypes.length === 0) {
      return Array(overheadParts).fill('fixed-sash');
    }
    if (overheadPartWindowTypes.length === overheadParts) {
      return overheadPartWindowTypes;
    }
    // If length doesn't match, pad with 'fixed-sash' or trim
    const result = [...overheadPartWindowTypes];
    while (result.length < overheadParts) {
      result.push('fixed-sash');
    }
    return result.slice(0, overheadParts);
  })();

  const getOverheadPartOpening = (index) => {
    if (!hasOverhead || !overheadDividerY) return null;
    const startAccum = normalizedOverheadPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
    const overheadPartWidthMm = normalizedOverheadPartWidths[index];
    const dividerOverlap = overheadParts === 2 ? frameThickness / 2 : 0;
    const overheadPartWidthPx = (overheadPartWidthMm / totalOverheadPartWidth) * doorSectionWidth + dividerOverlap;
    const overheadPartX = index === 0
      ? doorSectionX + (startAccum / totalOverheadPartWidth) * doorSectionWidth
      : doorSectionX + (startAccum / totalOverheadPartWidth) * doorSectionWidth - dividerOverlap;
    const openingY = startY + innerFrameInset;
    const openingH = overheadDividerY - openingY;
    return {
      openingX: overheadPartX + innerFrameInset,
      openingY,
      openingW: overheadPartWidthPx - innerFrameInset * 2,
      openingH,
      windowType: currentOverheadPartWindowTypes[index] || 'fixed-sash',
    };
  };

  const overheadPartHasInnerFrame = (windowType) =>
    windowType === 'fixed-glass' ||
    windowType === 'tilt-only' ||
    windowType === 'tilt-turn-left' ||
    windowType === 'tilt-turn-right';

  const renderOverheadFixedSashOutline = (openingX, openingY, openingW, openingH) => {
    const borderT = 2 * scale;
    return (
      <g pointerEvents="none">
        <rect x={openingX} y={openingY} width={openingW} height={borderT} fill={glazingBeadFill} />
        <rect x={openingX} y={openingY + openingH - borderT} width={openingW} height={borderT} fill={glazingBeadFill} />
        <rect x={openingX} y={openingY + borderT} width={borderT} height={openingH - borderT * 2} fill={glazingBeadFill} />
        <rect x={openingX + openingW - borderT} y={openingY + borderT} width={borderT} height={openingH - borderT * 2} fill={glazingBeadFill} />
      </g>
    );
  };

  const getOverheadInnerFrameMetrics = (openingX, openingY, openingW, openingH) => {
    const frameT = frameThickness * 0.7;
    return {
      frameX: openingX,
      frameY: openingY,
      frameW: openingW,
      frameH: openingH,
      frameT,
      innerPerimeterX: openingX + frameT,
      innerPerimeterY: openingY + frameT,
      innerPerimeterW: openingW - frameT * 2,
      innerPerimeterH: openingH - frameT * 2,
    };
  };

  const renderOverheadInnerSashStiles = (frameX, frameY, frameW, frameH, frameT) => (
    <g pointerEvents="none">
      <rect x={frameX} y={frameY} width={frameW} height={frameT} fill={interiorColorHex} />
      <rect x={frameX} y={frameY} width={frameT} height={frameH} fill={interiorColorHex} />
      <rect x={frameX + frameW - frameT} y={frameY} width={frameT} height={frameH} fill={interiorColorHex} />
      <rect x={frameX} y={frameY + frameH - frameT} width={frameW} height={frameT} fill={interiorColorHex} />
      <rect
        x={frameX + frameT / 2}
        y={frameY + frameT / 2}
        width={frameW - frameT}
        height={frameH - frameT}
        fill={interiorColorHex}
      />
      <line x1={frameX} y1={frameY} x2={frameX + frameW} y2={frameY} stroke={frameEdgeStroke} strokeWidth="0.5" />
      <line x1={frameX} y1={frameY} x2={frameX} y2={frameY + frameH} stroke={frameEdgeStroke} strokeWidth="0.5" />
      <line x1={frameX + frameW} y1={frameY} x2={frameX + frameW} y2={frameY + frameH} stroke={frameEdgeStroke} strokeWidth="0.5" />
    </g>
  );

  const renderOverheadInnerSashBorderlines = (frameX, frameY, frameW, frameH, frameT) => (
    <g pointerEvents="none">
      <line
        x1={frameX + frameT}
        y1={frameY + frameT}
        x2={frameX + frameW - frameT}
        y2={frameY + frameT}
        stroke={frameEdgeStroke}
        strokeWidth="1.2"
      />
      <line
        x1={frameX + frameT}
        y1={frameY + frameT}
        x2={frameX + frameT}
        y2={frameY + frameH - frameT}
        stroke={frameEdgeStroke}
        strokeWidth="0.5"
      />
      <line
        x1={frameX + frameW - frameT}
        y1={frameY + frameT}
        x2={frameX + frameW - frameT}
        y2={frameY + frameH - frameT}
        stroke={frameEdgeStroke}
        strokeWidth="0.5"
      />
    </g>
  );

  const renderOverheadInnerSashBottomBorders = (frameX, frameY, frameW, frameH, frameT) => (
    <g pointerEvents="none">
      <line
        x1={frameX}
        y1={frameY + frameH}
        x2={frameX + frameW}
        y2={frameY + frameH}
        stroke={frameEdgeStroke}
        strokeWidth="0.5"
      />
      <line
        x1={frameX + frameT}
        y1={frameY + frameH - frameT}
        x2={frameX + frameW - frameT}
        y2={frameY + frameH - frameT}
        stroke={frameEdgeStroke}
        strokeWidth="1.2"
      />
    </g>
  );

  const renderSidePanelTransomRailStrokes = (railX, railY, railW, railH, stroke = frameEdgeStroke) => (
    <g pointerEvents="none" fill="none" stroke={stroke}>
      <line x1={railX} y1={railY} x2={railX + railW} y2={railY} strokeWidth="0.5" />
      <line x1={railX} y1={railY + railH} x2={railX + railW} y2={railY + railH} strokeWidth="1.2" />
      <line x1={railX} y1={railY} x2={railX} y2={railY + railH} strokeWidth="0.5" />
      <line x1={railX + railW} y1={railY} x2={railX + railW} y2={railY + railH} strokeWidth="0.5" />
    </g>
  );

  const renderSidePanelTransomRail = (railX, railY, railW, railH) => (
    <g pointerEvents="none">
      <rect x={railX} y={railY} width={railW} height={railH} fill={exteriorColorHex} />
      <rect
        x={railX + frameThickness / 2}
        y={railY + frameThickness / 2}
        width={railW - frameThickness}
        height={Math.max(0, railH - frameThickness / 2)}
        fill={exteriorColorHex}
      />
      {renderSidePanelTransomRailStrokes(railX, railY, railW, railH)}
    </g>
  );

  // Calculate inner frame positions and glass positions (within door section)
  // innerFrameY and innerFrameHeight are already calculated above
  // For sliding doors with slidingPosition === 'right', we need 3 frames
  // For sliding doors with slidingPosition === 'center', we need 4 frames
  const needsThirdFrame = doorType === 'sliding' && slidingPosition === 'right' && isDoublePane;
  const needsCenterFrame = doorType === 'sliding' && slidingPosition === 'center' && isDoublePane;
  const frameCount = needsCenterFrame ? 4 : (needsThirdFrame ? 3 : (isDoublePane ? 2 : 1));
  // For sliding doors: all inner frames touch (no gaps between sashes)
  const firstDividerWidth = (doorType === 'sliding' && isDoublePane) ? 0 : (isDoublePane ? dividerWidth : 0);
  const secondDividerWidth = 0;
  // No third divider - center and third frames should touch
  const totalDividerWidth = firstDividerWidth + secondDividerWidth;
  
  // All frames should be the same width and centered
  const leftInnerFrameWidth = isDoublePane 
    ? (doorSectionWidth - innerFrameInset * 2 - totalDividerWidth) / frameCount
    : (doorSectionWidth - innerFrameInset * 2); // Single pane: full width minus insets
  
  // Calculate total width of all frames and dividers to center them
  const totalFramesWidth = frameCount * leftInnerFrameWidth + totalDividerWidth;
  const framesStartX = doorSectionX + (doorSectionWidth - totalFramesWidth) / 2;
  
  const leftInnerFrameX = framesStartX;
  const rightInnerFrameX = leftInnerFrameX + leftInnerFrameWidth + firstDividerWidth;
  const rightInnerFrameWidth = leftInnerFrameWidth;
  // Left sash: full # frame (incl. right stile). Right sash: ] only (top + right + bottom).
  const leftSashHasRightStile =
    doorType !== 'sliding' ||
    !isDoublePane ||
    (slidingPosition === 'center'
      ? innerFrameSlidingSectionLeft !== 'right'
      : innerFrameSlidingSection !== 'right');
  const rightSashNoLeftStile =
    doorType === 'sliding' &&
    isDoublePane &&
    (((slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left') ||
      (slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'));
  /** Shared center stile X — right edge of left sash allocation (= left edge of right sash). */
  const meetingStileX = leftInnerFrameX + leftInnerFrameWidth;
  /** Left fixed [] + right sliding ] — one shared vertical at meetingStileX (left sash owns it). */
  const isSplitSlidingSashLayout =
    doorType === 'sliding' && isDoublePane && leftSashHasRightStile && rightSashNoLeftStile;
  const rightSashRailX = rightInnerFrameX;
  const rightSashRailWidth = rightInnerFrameWidth;
  // Right sash must not reserve a second left stile — content starts at the shared meeting stile.
  const rightSashContentX = isSplitSlidingSashLayout
    ? meetingStileX
    : rightInnerFrameX + innerFrameThickness;
  const rightSashContentWidth = isSplitSlidingSashLayout
    ? rightInnerFrameWidth - innerFrameThickness
    : rightInnerFrameWidth - innerFrameThickness * 2;
  const rightSashGlassX = rightSashNoLeftStile ? rightSashContentX + innerGlassInset : null;
  /** Junction between frame 2 and frame 3 — both sashes keep their own stile (no shared border). */
  const middleMeetingStileX =
    needsThirdFrame || needsCenterFrame ? rightInnerFrameX + rightInnerFrameWidth : null;
  /** Frame 3 in 4-panel center: full left border vs frame 2; owns shared right stile with frame 4. */
  const centerSashHasRightStile = needsCenterFrame && doorType === 'sliding';
  /** Frame 4 in 4-panel center: ] shape — shares left border with frame 3 at centerMeetingStileX. */
  const fourthSashNoLeftStile = needsCenterFrame && doorType === 'sliding';
  
  // Center frame position (only when slidingPosition === 'center')
  // Center frame should touch the third frame (no divider between them)
  const centerInnerFrameX = needsCenterFrame ? (rightInnerFrameX + rightInnerFrameWidth + secondDividerWidth) : null;
  const centerInnerFrameWidth = needsCenterFrame ? leftInnerFrameWidth : null; // Same width as other frames
  /** Shared vertical between center sash and third sash (4-panel center layout). */
  const centerMeetingStileX =
    needsCenterFrame && centerInnerFrameX !== null
      ? centerInnerFrameX + centerInnerFrameWidth
      : null;
  
  // Third frame position (only when slidingPosition === 'right' or 'center')
  // When center frame exists, third frame touches it directly (no divider)
  const thirdInnerFrameX = (needsThirdFrame || needsCenterFrame) 
    ? (needsCenterFrame 
        ? (centerInnerFrameX + centerInnerFrameWidth) // Touch center frame directly
        : (rightInnerFrameX + rightInnerFrameWidth + secondDividerWidth))
    : null;
  const thirdInnerFrameWidth = (needsThirdFrame || needsCenterFrame) ? leftInnerFrameWidth : null;
  /** Left rail X for frame 4 when it shares a stile with frame 3. */
  const thirdRailX = fourthSashNoLeftStile ? centerMeetingStileX : thirdInnerFrameX;
  
  // Glass positions inside inner frames (door section)
  // For sliding doors, left glass position depends on sliding section:
  // When innerFrameSlidingSection === 'left' (default): normal position (has right border)
  // When innerFrameSlidingSection === 'right': no right border, glass extends right but stays within left frame
  const leftGlassX = leftInnerFrameX + innerFrameThickness + innerGlassInset;
  // For sliding doors, right glass position depends on sliding section:
  // When innerFrameSlidingSection === 'left' (default): no left border, glass extends left
  // When innerFrameSlidingSection === 'right': starts from right frame's left border (which touches left frame's right border)
  // For center section: use innerFrameSlidingSectionLeft for first 2 frames
  const rightGlassX = doorType === 'sliding' && isDoublePane && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
    ? leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness + innerGlassInset  // Starts from right frame's left border (touches left frame's right border)
    : doorType === 'sliding' && isDoublePane && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
    ? rightInnerFrameX + innerFrameThickness + innerGlassInset  // Full borders for center section when "right" is selected
    : doorType === 'sliding' && isDoublePane && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
    ? rightSashGlassX
    : doorType === 'sliding' && isDoublePane && rightSashNoLeftStile
    ? rightSashGlassX
    : doorType === 'sliding' && isDoublePane
    ? rightInnerFrameX + innerGlassInset * 0.3  // Extend left a little, but keep some black border (no left border)
    : rightInnerFrameX + innerFrameThickness + innerGlassInset;
  // Center frame glass - for center section, depends on innerFrameSlidingSectionRight
  // When 'right' (default): no right border, extends to right
  // When 'left': full borders (has left border)
  const centerGlassX = needsCenterFrame && centerInnerFrameX !== null
    ? centerInnerFrameX + innerFrameThickness + innerGlassInset
    : null;
  const thirdGlassX = (needsThirdFrame || needsCenterFrame) && thirdInnerFrameX !== null
    ? (fourthSashNoLeftStile
      ? centerMeetingStileX + innerGlassInset
      : thirdInnerFrameX + innerFrameThickness + innerGlassInset)
    : null;
  const glassY = innerFrameY + innerFrameThickness + innerGlassInset;
  // Left glass width - depends on sliding section:
  // When innerFrameSlidingSection === 'left' (default): normal width within left frame (has right border)
  // When innerFrameSlidingSection === 'right': no right border, glass extends right but stays within left frame boundaries
  // For center section: use innerFrameSlidingSectionLeft for first 2 frames
  const leftGlassWidth = doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
    ? leftInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2  // Normal width within left frame (has right border)
    : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
    ? (leftInnerFrameX + leftInnerFrameWidth - innerGlassInset) - leftGlassX  // Extends to right edge of left frame (no right border, stays within frame)
    : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
    ? (leftInnerFrameX + leftInnerFrameWidth - innerGlassInset) - leftGlassX  // For center section: extends to right edge of left frame (no right border, stays within frame) - same as left/right sections
    : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
    ? (rightInnerFrameX - innerFrameThickness - innerGlassInset) - (leftInnerFrameX + innerFrameThickness + innerGlassInset)  // For center section default (left): centered within extended left frame (from left border to right frame's left edge)
    : leftInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2;  // Default: centered inside borders
  // For sliding doors, right glass width depends on sliding section:
  // When innerFrameSlidingSection === 'left' (default): no left border, glass extends left
  // When innerFrameSlidingSection === 'right': extends from right frame's left border to right frame's right border
  // For center section: use innerFrameSlidingSectionLeft for first 2 frames
  const rightGlassWidth = doorType === 'sliding' && isDoublePane && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
    ? rightInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2  // Full borders: from left border to right border (centered inside all 4 borders)
    : doorType === 'sliding' && isDoublePane && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
    ? rightInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2  // Full borders for center section when "right" is selected
    : doorType === 'sliding' && isDoublePane && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
    ? (rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness - innerGlassInset) - rightSashGlassX
    : doorType === 'sliding' && isDoublePane && rightSashNoLeftStile && (needsThirdFrame || needsCenterFrame)
    ? (rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness - innerGlassInset) - rightSashGlassX
    : doorType === 'sliding' && isDoublePane && rightSashNoLeftStile
    ? rightSashContentWidth - innerGlassInset * 2
    : doorType === 'sliding' && isDoublePane
    ? rightInnerFrameWidth - innerFrameThickness - innerGlassInset * 0.4  // Extend left a little, keep some black border (no left border)
    : rightInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2;
  // Center frame glass width - for center section, depends on innerFrameSlidingSectionRight
  // When 'right' (default): no right border, extends to right
  // When 'left': full borders (has left and right borders)
  const centerGlassWidth = needsCenterFrame && centerInnerFrameWidth !== null
    ? centerInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2
    : null;
  const thirdGlassWidth = (needsThirdFrame || needsCenterFrame) && thirdInnerFrameWidth !== null
    ? (fourthSashNoLeftStile
      ? thirdInnerFrameWidth - innerFrameThickness - innerGlassInset * 2
      : thirdInnerFrameWidth - innerFrameThickness * 2 - innerGlassInset * 2)
    : null;
  const glassHeight = innerFrameHeight - innerFrameThickness * 2 - innerGlassInset * 2;

  // Parapet divider Y — mm is relative to total door height, measured from outer frame bottom.
  // Shared across main door and side panels so equal mm heights align horizontally.
  const doorFrameBottomY = startY + scaledHeight;
  const parapetDividerYFromDoorBottomMm = (parapetHeightMm) => {
    const clamped = Math.max(0, Math.min(height, parapetHeightMm || 0));
    if (height <= 0) return doorFrameBottomY;
    return doorFrameBottomY - (clamped / height) * scaledHeight;
  };

  const clampedParapetHeightMm = Math.max(0, Math.min(height, parapetHeight || 0));
  const parapetTopY = parapetDividerYFromDoorBottomMm(clampedParapetHeightMm);

  // Voordeur style-2, terrasdeur & achterdeur: click inner frame to choose glass or panel
  const supportsInnerFrameGlassPanel =
    (isVoordeur && doorStyle === 'style-2') || isTerrasdeur || isAchterdeur;
  const innerFrameInteractiveHeight =
    hasParapet && parapetTopY > glassY ? parapetTopY - glassY : glassHeight;

  useEffect(() => {
    if (glassTexture !== 'panel') {
      lastGlassTextureRef.current = glassTexture;
    }
  }, [glassTexture]);

  useEffect(() => {
    if (supportsInnerFrameGlassPanel) {
      setStyle2Type(glassTexture === 'panel' ? 'panel' : 'glass');
    }
  }, [glassTexture, supportsInnerFrameGlassPanel]);

  const openInnerFrameTypeDialog = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setSelectedStyle2(true);
    setStyle2SettingsOpen(true);
  };

  const renderInnerFrameGlassPanelFill = (x, y, w, h) => {
    if (style2Type === 'panel') {
      return (
        <>
          <rect x={x} y={y} width={w} height={h} fill={interiorColorHex} />
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            fill="none"
            stroke={frameEdgeStroke}
            strokeWidth="1"
            pointerEvents="none"
          />
        </>
      );
    }
    return (
      <>
        <rect x={x} y={y} width={w} height={h} fill={glassFill} />
        {glassTexturePattern && (
          <rect
            x={x}
            y={y}
            width={w}
            height={h}
            fill={glassTexturePattern}
            opacity="1"
            pointerEvents="none"
          />
        )}
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          fill="none"
          stroke={frameEdgeStroke}
          strokeWidth="1"
          pointerEvents="none"
        />
      </>
    );
  };

  const renderInnerFrameInteractionLayer = (x, y, w, h) => (
    <>
      {hoveredStyle2 && (
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          fill="rgba(255, 165, 0, 0.15)"
          pointerEvents="none"
        />
      )}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        fill="transparent"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredStyle2(true)}
        onMouseLeave={() => setHoveredStyle2(false)}
        onClick={openInnerFrameTypeDialog}
      />
    </>
  );

  // Panel glass positions (for left and right panels)
  // Left panel: no inner frame, glass goes directly in dark spacer area, no right border
  const leftPanelGlassX = startX + frameThickness + innerGlassInset;
  const leftPanelGlassWidth = leftPanelWidth - frameThickness - innerGlassInset * 2; // No right border, so only subtract left border
  const leftPanelGlassY = startY + frameThickness + innerGlassInset;
  const leftPanelGlassHeight = scaledHeight - frameThickness * 2 - innerGlassInset * 2;
  // Right panel: no inner frame, glass goes directly in dark spacer area, no left border
  const rightPanelGlassX = startX + scaledWidth - rightPanelWidth + innerGlassInset;
  const rightPanelGlassWidth = rightPanelWidth - frameThickness - innerGlassInset * 2; // No left border, so only subtract right border
  const rightPanelGlassY = startY + frameThickness + innerGlassInset;
  const rightPanelGlassHeight = scaledHeight - frameThickness * 2 - innerGlassInset * 2;
  
  // Side panel parapet dividers use the same outer-frame-bottom reference as the main door
  const clampedLeftPanelParapetHeightMm = Math.max(0, Math.min(height, leftPanelParapetHeightMm || 250));
  const leftPanelParapetTopY = parapetDividerYFromDoorBottomMm(clampedLeftPanelParapetHeightMm);
  
  const clampedRightPanelParapetHeightMm = Math.max(0, Math.min(height, rightPanelParapetHeightMm || 250));
  const rightPanelParapetTopY = parapetDividerYFromDoorBottomMm(clampedRightPanelParapetHeightMm);

  const panelOverheadVisualDeps = {
    scale,
    frameThickness,
    frameEdgeStroke,
    glazingBeadFill,
    woodJoint,
    glassFill,
    glassTexturePattern,
    hasInnerFrame: overheadPartHasInnerFrame,
    renderOverheadFixedSashOutline,
    renderOverheadInnerSashStiles,
    renderOverheadInnerSashBorderlines,
    renderOverheadInnerSashBottomBorders,
  };

  /** Side-panel lower zone — same dark bead + inset glass as overhead. */
  const renderSidePanelLowerOpeningVisual = (openingX, openingY, openingW, openingH, windowType) => {
    if (openingW <= 0 || openingH <= 0) return null;
    return renderOverheadOpeningVisual({
      ...panelOverheadVisualDeps,
      openingX,
      openingY,
      openingW,
      openingH,
      windowType: windowType || 'fixed-sash',
    });
  };

  const getSidePanelLowerOpening = (
    panelGlassX,
    panelGlassY,
    panelGlassWidth,
    panelGlassHeight,
    hasOverhead,
    renderGlassY,
    renderGlassHeight,
  ) => ({
    openingX: panelGlassX,
    openingY: hasOverhead ? renderGlassY : panelGlassY,
    openingW: panelGlassWidth,
    openingH: hasOverhead ? renderGlassHeight : panelGlassHeight,
  });

  const clampPanelOverheadHeightMm = (requestedMm, panelGlassHeightPx) => {
    const minH = minOverheadHeight ?? MIN_OVERHEAD_HEIGHT;
    const panelHeightMm = panelGlassHeightPx / scale;
    const layoutMaxH = Math.max(minH, panelHeightMm - innerFrameInset / scale - 200);
    const maxH =
      maxSidePanelOverheadHeight != null
        ? Math.min(layoutMaxH, maxSidePanelOverheadHeight)
        : layoutMaxH;
    return Math.max(minH, Math.min(requestedMm, maxH));
  };

  const leftPanelHasOverhead = sidePanelOverheadEnabled && leftPanelOverheadProp === true;
  const rightPanelHasOverhead = sidePanelOverheadEnabled && rightPanelOverheadProp === true;
  const leftPanelOverheadHeightMm = leftPanelHasOverhead
    ? clampPanelOverheadHeightMm(leftPanelOverheadHeightProp ?? defaultOverheadHeight, leftPanelGlassHeight)
    : 0;
  const rightPanelOverheadHeightMm = rightPanelHasOverhead
    ? clampPanelOverheadHeightMm(rightPanelOverheadHeightProp ?? defaultOverheadHeight, rightPanelGlassHeight)
    : 0;
  const leftPanelOverheadDividerY = leftPanelHasOverhead
    ? leftPanelGlassY + leftPanelOverheadHeightMm * scale
    : null;
  const rightPanelOverheadDividerY = rightPanelHasOverhead
    ? rightPanelGlassY + rightPanelOverheadHeightMm * scale
    : null;
  const leftPanelRenderGlassY = leftPanelHasOverhead && leftPanelOverheadDividerY
    ? leftPanelOverheadDividerY + innerFrameInset
    : leftPanelGlassY;
  const rightPanelRenderGlassY = rightPanelHasOverhead && rightPanelOverheadDividerY
    ? rightPanelOverheadDividerY + innerFrameInset
    : rightPanelGlassY;
  const leftPanelRenderGlassHeight = leftPanelHasOverhead
    ? Math.max(0, (leftPanelGlassY + leftPanelGlassHeight) - leftPanelRenderGlassY)
    : leftPanelGlassHeight;
  const rightPanelRenderGlassHeight = rightPanelHasOverhead
    ? Math.max(0, (rightPanelGlassY + rightPanelGlassHeight) - rightPanelRenderGlassY)
    : rightPanelGlassHeight;
  const leftPanelOverheadOpeningH = leftPanelHasOverhead && leftPanelOverheadDividerY
    ? leftPanelOverheadDividerY - leftPanelGlassY
    : 0;
  const rightPanelOverheadOpeningH = rightPanelHasOverhead && rightPanelOverheadDividerY
    ? rightPanelOverheadDividerY - rightPanelGlassY
    : 0;
  // Overhead + transom rail share the same horizontal bounds as the panel glass column
  const leftPanelOverheadOpeningX = leftPanelGlassX;
  const leftPanelOverheadOpeningW = leftPanelGlassWidth;
  const rightPanelOverheadOpeningX = rightPanelGlassX;
  const rightPanelOverheadOpeningW = rightPanelGlassWidth;
  const leftPanelTransomRailX = leftPanelOverheadOpeningX;
  const leftPanelTransomRailW = leftPanelOverheadOpeningW;
  const rightPanelTransomRailX = rightPanelOverheadOpeningX;
  const rightPanelTransomRailW = rightPanelOverheadOpeningW;

  const commitSidePanelOverheadHeight = (parsed, panelGlassHeightPx, onChange, setInput, currentMm) => {
    if (!onChange) {
      setInput(String(Math.round(currentMm)));
      return;
    }
    const minH = minOverheadHeight ?? MIN_OVERHEAD_HEIGHT;
    const panelHeightMm = panelGlassHeightPx / scale;
    const layoutMaxH = Math.max(minH, panelHeightMm - innerFrameInset / scale - 200);
    const maxH =
      maxSidePanelOverheadHeight != null
        ? Math.min(layoutMaxH, maxSidePanelOverheadHeight)
        : layoutMaxH;
    if (!Number.isNaN(parsed) && parsed >= minH && parsed <= maxH) {
      onChange(parsed);
    } else {
      setInput(String(Math.round(currentMm)));
    }
  };

  const renderSidePanelOverheadOnlySubRuler = ({
    rulerLineX,
    tickStartX,
    tickEndX,
    labelX,
    glassY,
    overheadDividerY,
    renderGlassY,
    overheadHeightInput,
    setOverheadHeightInput,
    onOverheadHeightChange,
    panelGlassHeightPx,
    overheadHeightMm,
    zoneBottomY,
  }) => (
    <g>
      <line
        x1={rulerLineX}
        y1={glassY}
        x2={rulerLineX}
        y2={zoneBottomY}
        stroke="#2C2C2C"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <line x1={tickStartX} y1={glassY} x2={tickEndX} y2={glassY} stroke="#2C2C2C" strokeWidth="1.5" strokeLinecap="square" />
      <line
        x1={tickStartX}
        y1={overheadDividerY}
        x2={tickEndX}
        y2={overheadDividerY}
        stroke="#2C2C2C"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <line
        x1={tickStartX}
        y1={renderGlassY}
        x2={tickEndX}
        y2={renderGlassY}
        stroke="#2C2C2C"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <line
        x1={tickStartX}
        y1={zoneBottomY}
        x2={tickEndX}
        y2={zoneBottomY}
        stroke="#2C2C2C"
        strokeWidth="1.5"
        strokeLinecap="square"
      />
      <foreignObject
        x={labelX}
        y={(glassY + overheadDividerY) / 2 - 12}
        width="80"
        height="24"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
          <input
            type="number"
            value={overheadHeightInput}
            onChange={(e) => setOverheadHeightInput(e.target.value)}
            onBlur={() => {
              commitSidePanelOverheadHeight(
                parseInt(overheadHeightInput, 10),
                panelGlassHeightPx,
                onOverheadHeightChange,
                setOverheadHeightInput,
                overheadHeightMm,
              );
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur();
            }}
            style={{
              border: 'none',
              background: 'transparent',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 500,
              color: '#2C2C2C',
              outline: 'none',
              padding: 0,
              margin: 0,
              WebkitAppearance: 'none',
              MozAppearance: 'textfield',
              width: `${Math.max(40, String(overheadHeightInput).length * 8 + 6)}px`,
              maxWidth: '80px',
            }}
          />
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
        </div>
      </foreignObject>
      <foreignObject
        x={labelX}
        y={(renderGlassY + zoneBottomY) / 2 - 12}
        width="80"
        height="24"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C' }}>
            {Math.round((zoneBottomY - renderGlassY) / scale)}
          </span>
          <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
        </div>
      </foreignObject>
    </g>
  );

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4 rounded-lg relative">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        className="max-w-full max-h-full"
        style={{ minHeight: '500px' }}
      >
        <defs>
          {/* Glass gradient: blue at top, fades to white */}
          <linearGradient id="glass-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C7EBFF" stopOpacity="0.95" />
            <stop offset="95%" stopColor="#E6F7FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
          </linearGradient>

          {/* Panel gradient for fixed-panel window type */}
          <linearGradient id="panel-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#E8E8E8" stopOpacity="1" />
            <stop offset="50%" stopColor="#F5F5F5" stopOpacity="1" />
            <stop offset="100%" stopColor="#E8E8E8" stopOpacity="1" />
          </linearGradient>

          {/* Glass texture patterns */}
          <pattern id="glass-pattern-chinchilla" patternUnits="userSpaceOnUse" x="0" y="0" width="40" height="40">
            <image href="/images/chinchilla21.png" x="0" y="0" width="40" height="40" preserveAspectRatio="xMidYMid slice" />
          </pattern>
          <pattern id="glass-pattern-satinato" patternUnits="userSpaceOnUse" x="0" y="0" width="10000" height="10000">
            <image href="/images/santino.png" x="0" y="0" width="10000" height="10000" preserveAspectRatio="none" />
          </pattern>
        </defs>

        {/* Background */}
        <rect width={svgWidth} height={svgHeight} fill="#F5F5F5" />

        {/* Outer Frame - spans full width including panels */}
        <rect
          x={startX}
          y={startY}
          width={scaledWidth}
          height={scaledHeight}
          fill={exteriorColorHex}
        />

        {/* Left Panel Section */}
        {hasLeftPanel && (
          <rect
            x={startX}
            y={startY}
            width={leftPanelWidth}
            height={scaledHeight}
            fill={exteriorColorHex}
          />
        )}

        {/* Right Panel Section */}
        {hasRightPanel && (
          <rect
            x={startX + scaledWidth - rightPanelWidth}
            y={startY}
            width={rightPanelWidth}
            height={scaledHeight}
            fill={exteriorColorHex}
          />
        )}

        {/* Vertical Divider - White border lines at top and bottom where divider meets outer frame */}
        {(hasLeftPanel || hasRightPanel) && (
          <>
            {/* White border lines at top and bottom */}
            <line
              x1={hasLeftPanel ? (startX + leftPanelWidth) : (doorSectionX + doorSectionWidth)}
              y1={startY + frameThickness}
              x2={hasLeftPanel ? (startX + leftPanelWidth + verticalDividerWidth) : (doorSectionX + doorSectionWidth + verticalDividerWidth)}
              y2={startY + frameThickness}
              stroke="#FFFFFF"
              strokeWidth="1.5"
            />
            <line
              x1={hasLeftPanel ? (startX + leftPanelWidth) : (doorSectionX + doorSectionWidth)}
              y1={startY + scaledHeight - frameThickness}
              x2={hasLeftPanel ? (startX + leftPanelWidth + verticalDividerWidth) : (doorSectionX + doorSectionWidth + verticalDividerWidth)}
              y2={startY + scaledHeight - frameThickness}
              stroke="#FFFFFF"
              strokeWidth="1.5"
            />
          </>
        )}

        {/* Outer Frame Bottom Border - lighter gray (from horizontal joints downward) */}
        <rect
          x={startX}
          y={startY + scaledHeight - frameThickness}
          width={scaledWidth}
          height={frameThickness}
          fill="#9CA3AF"
        />

        {/* Inner Frame Reveal - part of external frame (only for door section) */}
        {/* When innerFrameSlidingSection === 'left' (default): extends to touch right frame's left border */}
        {/* When innerFrameSlidingSection === 'right': extends to cover right frame (which extends to touch left frame) */}
        <rect
          x={doorSectionX + frameThickness / 2}
          y={startY + frameThickness / 2}
          width={
            doorType === 'sliding' && isDoublePane && (needsThirdFrame || needsCenterFrame)
              ? thirdInnerFrameX + thirdInnerFrameWidth - (doorSectionX + frameThickness / 2)
              : doorType === 'sliding' && isDoublePane && rightSashNoLeftStile
              ? doorSectionWidth - frameThickness
              : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
              ? rightInnerFrameX - (doorSectionX + frameThickness / 2)
              : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
              ? rightInnerFrameX - (doorSectionX + frameThickness / 2)
              : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left'
              ? thirdInnerFrameX - (doorSectionX + frameThickness / 2)
              : doorSectionWidth - frameThickness
          }
          height={scaledHeight - frameThickness}
          fill={exteriorColorHex}
        />
        
        {/* Inner Frame Reveal Bottom - gray to form U-shape (only for door section) */}
        {/* When innerFrameSlidingSection === 'left' (default): extends to touch right frame's left border */}
        {/* When innerFrameSlidingSection === 'right': extends to cover right frame */}
        <rect
          x={doorSectionX + frameThickness / 2}
          y={startY + scaledHeight - frameThickness}
          width={
            doorType === 'sliding' && isDoublePane && (needsThirdFrame || needsCenterFrame)
              ? thirdInnerFrameX + thirdInnerFrameWidth - doorSectionX - frameThickness / 2
              : doorType === 'sliding' && isDoublePane && rightSashNoLeftStile
              ? doorSectionWidth - frameThickness
              : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
              ? rightInnerFrameX - doorSectionX - frameThickness / 2
              : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
              ? rightInnerFrameX - doorSectionX - frameThickness / 2
              : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left'
              ? thirdInnerFrameX - doorSectionX - frameThickness / 2
              : doorSectionWidth - frameThickness
          }
          height={frameThickness / 2}
          fill="#9CA3AF"
        />

        {/* Spacer / glazing bead removed - no black borders */}

        {/* Left Panel - only bottom border (no inner frame, no right border, no black borders) */}
        {hasLeftPanel && (
          <>
            {/* Left Panel Bottom Border - lighter gray - extends to right edge, overlaps outer bottom frame */}
            <rect
              x={startX + frameThickness}
              y={startY + scaledHeight - frameThickness}
              width={leftPanelWidth - frameThickness}
              height={frameThickness}
              fill="#9CA3AF"
            />
          </>
        )}

        {/* Right Panel - only bottom border (no inner frame, no left border, no black borders) */}
        {hasRightPanel && (
          <>
            {/* Right Panel Bottom Border - lighter gray - extends to left edge, overlaps outer bottom frame */}
            <rect
              x={startX + scaledWidth - rightPanelWidth}
              y={startY + scaledHeight - frameThickness}
              width={rightPanelWidth - frameThickness}
              height={frameThickness}
              fill="#9CA3AF"
            />
          </>
        )}

        {/* Inner Frames */}
        <>
              {/* Left Inner Frame */}
              <g>
                {/* Outer frame (interior color) with borderline */}
                {/* Top border:
                  - When innerFrameSlidingSection === 'left' (default): extend to touch right frame's left border
                  - When innerFrameSlidingSection === 'right': normal width (no extension)
                  - For center section: use innerFrameSlidingSectionLeft */}
                <rect
                  x={leftInnerFrameX}
                  y={innerFrameY}
                  width={
                    isSplitSlidingSashLayout
                      ? leftInnerFrameWidth
                      : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
                      ? rightInnerFrameX - leftInnerFrameX  // Extend to touch right frame's left border
                      : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                      ? rightInnerFrameX - leftInnerFrameX  // Extend to touch right frame's left border for center section
                      : leftInnerFrameWidth  // Normal width
                  }
                  height={innerFrameThickness}
                  fill={interiorColorHex}
                />
                {/* Left border */}
                <rect
                  x={leftInnerFrameX}
                  y={innerFrameY}
                  width={innerFrameThickness}
                  height={innerFrameHeight}
                  fill={interiorColorHex}
                />
                {/* Right border:
                  - When innerFrameSlidingSection === 'left' (default): hide (extends to touch right frame)
                  - When innerFrameSlidingSection === 'right': hide (no right border, glass extends right)
                  - For center section: use innerFrameSlidingSectionLeft
                */}
                {leftSashHasRightStile &&
                 !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                 !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                  <rect
                    x={meetingStileX - innerFrameThickness}
                    y={innerFrameY}
                    width={innerFrameThickness}
                    height={innerFrameHeight}
                    fill={interiorColorHex}
                  />
                )}
                {/* Bottom border:
                  - When innerFrameSlidingSection === 'left' (default): extend to touch right frame's left border
                  - When innerFrameSlidingSection === 'right': normal width (no extension)
                  - For center section: use innerFrameSlidingSectionLeft */}
                <rect
                  x={leftInnerFrameX}
                  y={innerFrameY + innerFrameHeight - innerFrameThickness}
                  width={
                    isSplitSlidingSashLayout
                      ? leftInnerFrameWidth
                      : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
                      ? rightInnerFrameX - leftInnerFrameX  // Extend to touch right frame's left border
                      : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
                      ? rightInnerFrameX - leftInnerFrameX  // For center section: extend to touch right frame's left border when "left" is selected (default)
                      : leftInnerFrameWidth  // Normal width
                  }
                  height={innerFrameThickness}
                  fill={interiorColorHex}
                />
                {/* Borderlines for visible borders */}
                {/* Top border line:
                  - When innerFrameSlidingSection === 'left' (default): extend to touch right frame's left border
                  - When innerFrameSlidingSection === 'right': normal width
                  - For center section: use innerFrameSlidingSectionLeft */}
                <line
                  x1={leftInnerFrameX}
                  y1={innerFrameY}
                  x2={
                    isSplitSlidingSashLayout
                      ? meetingStileX
                      : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'left'
                      ? rightInnerFrameX  // Extend to touch right frame's left border
                      : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
                      ? rightInnerFrameX  // For center section: extend to touch right frame's left border when "left" is selected (default)
                      : leftInnerFrameX + leftInnerFrameWidth  // Normal width
                  }
                  y2={innerFrameY}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                />
                {/* Left border line */}
                <line
                  x1={leftInnerFrameX}
                  y1={innerFrameY}
                  x2={leftInnerFrameX}
                  y2={innerFrameY + innerFrameHeight}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                />
                {/* Right border line:
                  - When innerFrameSlidingSection === 'left' (default): hide (extends to touch right frame)
                  - When innerFrameSlidingSection === 'right': hide (no right border, glass extends right)
                  - For center section: use innerFrameSlidingSectionLeft
                */}
                {leftSashHasRightStile &&
                 !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                 !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                  <line
                    x1={meetingStileX}
                    y1={innerFrameY}
                    x2={meetingStileX}
                    y2={innerFrameY + innerFrameHeight}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                )}
                {/* Bottom border line - extend to touch right frame's left border when innerFrameSlidingSection === 'right' */}
                {/* Bottom border line - removed as requested */}
                {/* Inner frame reveal:
                  - When innerFrameSlidingSection === 'left' (default): extend to touch right frame's left border
                  - When innerFrameSlidingSection === 'right': extend to touch right frame's left border (same as left)
                  - For center section: use innerFrameSlidingSectionLeft
                  - For center section "right": do not render (removed)
                  - For voordeur style-1 and style-3: leave space for dark glazing borders (2 * scale on each side)
                */}
                {!(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                  <rect
                    x={leftInnerFrameX + innerFrameThickness / 2}
                    y={innerFrameY + innerFrameThickness / 2}
                    width={
                      isSplitSlidingSashLayout
                        ? leftInnerFrameWidth - innerFrameThickness * 2
                        : doorType === 'sliding' &&
                          (slidingPosition === 'left' || slidingPosition === 'right') &&
                          (innerFrameSlidingSection === 'left' || innerFrameSlidingSection === 'right')
                          ? rightInnerFrameX - (leftInnerFrameX + innerFrameThickness / 2)  // Extend to touch right frame's left border
                          : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'left'
                          ? rightInnerFrameX - (leftInnerFrameX + innerFrameThickness / 2)  // For center section: extend to touch right frame's left border when "left" is selected (default)
                          : (isVoordeur && (doorStyle === 'style-1' || doorStyle === 'style-3'))
                            ? leftInnerFrameWidth - innerFrameThickness - innerFrameThickness / 2  // Extend to cover the right dark glazing border
                            : leftInnerFrameWidth - innerFrameThickness * 2  // Normal width
                    }
                    height={innerFrameHeight - innerFrameThickness}
                    fill={interiorColorHex}
                  />
                )}
                {/* Corner joints */}
                {/* Hide right-side corner joints when innerFrameSlidingSection === 'left' or 'right' (no right border) */}
                {woodJoint === 'joint-45' ? (
                  <>
                    {/* Top-left corner - horizontal line from inner frame white border to outer frame edge (not for voordeur) */}
                    {doorType !== 'voordeur' && (
                      <line
                        x1={doorSectionX}
                        y1={innerFrameY}
                        x2={leftInnerFrameX}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    )}
                    {/* Top-left corner - diagonal wood joint */}
                    <line
                      x1={leftInnerFrameX}
                      y1={innerFrameY}
                      x2={leftInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Top-right corner - only show when right border exists */}
                    {leftSashHasRightStile &&
                     !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                     !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                      <>
                        {/* Top-right corner - horizontal line to outer frame (not across right sash in split layout) */}
                        {doorType !== 'voordeur' && !isSplitSlidingSashLayout && (
                          <line
                            x1={meetingStileX}
                            y1={innerFrameY}
                            x2={doorSectionX + doorSectionWidth}
                            y2={innerFrameY}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                        )}
                        {/* Top-right corner - diagonal wood joint at meeting stile */}
                        <line
                          x1={meetingStileX}
                          y1={innerFrameY}
                          x2={meetingStileX - innerFrameThickness}
                          y2={innerFrameY + innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                    {/* Bottom-left corner */}
                    <line
                      x1={leftInnerFrameX}
                      y1={innerFrameY + innerFrameHeight}
                      x2={leftInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Bottom-right corner - only show when right border exists */}
                    {leftSashHasRightStile &&
                     !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                     !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                      <line
                        x1={meetingStileX}
                        y1={innerFrameY + innerFrameHeight}
                        x2={meetingStileX - innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Top-left corner - horizontal line from inner frame white border to outer frame edge */}
                    <line
                      x1={doorSectionX}
                      y1={innerFrameY}
                      x2={leftInnerFrameX}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Top-left corner - horizontal wood joint */}
                    <line
                      x1={leftInnerFrameX}
                      y1={innerFrameY + innerFrameThickness}
                      x2={leftInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Top-right corner - only show when right border exists */}
                    {leftSashHasRightStile &&
                     !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                     !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                      <>
                        {!isSplitSlidingSashLayout && (
                          <line
                            x1={meetingStileX}
                            y1={innerFrameY}
                            x2={doorSectionX + doorSectionWidth}
                            y2={innerFrameY}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                        )}
                        <line
                          x1={meetingStileX - innerFrameThickness}
                          y1={innerFrameY + innerFrameThickness}
                          x2={meetingStileX}
                          y2={innerFrameY + innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                    {/* Bottom-left corner */}
                    <>
                      <line
                        x1={leftInnerFrameX}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={leftInnerFrameX + innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      <line
                        x1={leftInnerFrameX}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={leftInnerFrameX}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    </>
                    {/* Bottom-right corner - only show when right border exists */}
                    {leftSashHasRightStile &&
                     !(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                     !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                      <>
                        <line
                          x1={meetingStileX - innerFrameThickness}
                          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                          x2={meetingStileX}
                          y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                        <line
                          x1={meetingStileX}
                          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                          x2={meetingStileX}
                          y2={innerFrameY + innerFrameHeight}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                  </>
                )}
                {/* Spacer / glazing bead for left inner frame
                  - Always show: centered inside all 4 borders
                  - For center section: use innerFrameSlidingSectionLeft
                  - For voordeur style-1 and style-3: use thin dark glazing borders on inside edges (like vertical dividers)
                */}
                {!(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                  isVoordeur && (doorStyle === 'style-1' || doorStyle === 'style-3') ? (
                    <>
                      {/* Top dark glazing border - leave space for left and right borders */}
                      <rect
                        x={leftInnerFrameX + innerFrameThickness + 2 * scale}
                        y={innerFrameY + innerFrameThickness}
                        width={leftInnerFrameWidth - innerFrameThickness * 2 - 4 * scale}
                        height={2 * scale}
                        fill={glazingBeadFill}
                      />
                      {/* Bottom dark glazing border - leave space for left and right borders */}
                      <rect
                        x={leftInnerFrameX + innerFrameThickness + 2 * scale}
                        y={innerFrameY + innerFrameHeight - innerFrameThickness - 2 * scale}
                        width={leftInnerFrameWidth - innerFrameThickness * 2 - 4 * scale}
                        height={2 * scale}
                        fill={glazingBeadFill}
                      />
                      {/* Left dark glazing border */}
                      <rect
                        x={leftInnerFrameX + innerFrameThickness}
                        y={innerFrameY + innerFrameThickness}
                        width={2 * scale}
                        height={innerFrameHeight - innerFrameThickness * 2}
                        fill={glazingBeadFill}
                      />
                      {/* Right dark glazing border - positioned to cover the inner edge of right border, extending inward */}
                      <rect
                        x={leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness - 2 * scale}
                        y={innerFrameY + innerFrameThickness + 2 * scale}
                        width={2 * scale}
                        height={innerFrameHeight - innerFrameThickness * 2 - 4 * scale}
                        fill={glazingBeadFill}
                      />
                      {/* Additional coverage for the inner edge line itself */}
                      <rect
                        x={leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness - 1}
                        y={innerFrameY + innerFrameThickness + 2 * scale}
                        width={1}
                        height={innerFrameHeight - innerFrameThickness * 2 - 4 * scale}
                        fill={glazingBeadFill}
                      />
                    </>
                  ) : (
                    <rect
                      x={leftInnerFrameX + innerFrameThickness}
                      y={innerFrameY + innerFrameThickness}
                      width={
                        isSplitSlidingSashLayout
                          ? leftInnerFrameWidth - innerFrameThickness * 2
                          : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
                          ? (rightInnerFrameX - innerGlassInset) - (leftInnerFrameX + innerFrameThickness)  // Extends to touch right frame's left border when right is selected
                          : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                          ? (rightInnerFrameX - innerGlassInset) - (leftInnerFrameX + innerFrameThickness)  // For center section: extends to touch right frame's left border when right is selected
                          : leftInnerFrameWidth - innerFrameThickness * 2
                      }
                      height={innerFrameHeight - innerFrameThickness * 2}
                      fill={glazingBeadFill}  // Black spacer border
                    />
                  )
                )}
                {/* Horizontal markings at bottom edge - removed as requested */}
                {/* Horizontal line at inner edge of bottom frame — hidden on Schuifpui (sliding) */}
                {doorType !== 'sliding' && (
                <line
                  x1={leftInnerFrameX + innerFrameThickness}
                  y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                  x2={
                    isSplitSlidingSashLayout
                      ? meetingStileX - innerFrameThickness
                      : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
                      ? leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness * 2  // Stop before right edge
                      : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                      ? leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness * 2  // Stop before right edge for center section
                      : leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness
                  }
                  y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                  stroke={frameEdgeStroke}
                  strokeWidth="1.2"
                />
                )}
              </g>

              {/* Right Inner Frame - only render if double pane */}
              {isDoublePane && (
              <g>
                {/* Outer frame (interior color) with borderline */}
                {/* When innerFrameSlidingSection === 'right', right frame extends to touch left frame's right border */}
                {/* For center section: use innerFrameSlidingSectionLeft */}
                {(doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') ||
                 (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') ? (
                  <>
                    {/* When innerFrameSlidingSection === 'right': right frame extends to touch left frame's right border */}
                    {/* Top border - extend to touch left frame's right border */}
                    <rect
                      x={leftInnerFrameX + leftInnerFrameWidth}
                      y={innerFrameY}
                      width={rightInnerFrameX + rightInnerFrameWidth - (leftInnerFrameX + leftInnerFrameWidth)}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Left border - touches left frame's right border */}
                    <rect
                      x={leftInnerFrameX + leftInnerFrameWidth}
                      y={innerFrameY}
                      width={innerFrameThickness}
                      height={innerFrameHeight}
                      fill={interiorColorHex}
                    />
                    {/* Right border - always show when there's a third/center frame (separate frame), or when innerFrameSlidingSection === 'right', or center section left part */}
                    {needsThirdFrame || needsCenterFrame || 
                     (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') ||
                     (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') ? (
                      <rect
                        x={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y={innerFrameY}
                        width={innerFrameThickness}
                        height={innerFrameHeight}
                        fill={interiorColorHex}
                      />
                    ) : null}
                    {/* Bottom border - extend to touch left frame's right border */}
                    <rect
                      x={leftInnerFrameX + leftInnerFrameWidth}
                      y={innerFrameY + innerFrameHeight - innerFrameThickness}
                      width={rightInnerFrameX + rightInnerFrameWidth - (leftInnerFrameX + leftInnerFrameWidth)}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Borderlines for all 4 borders */}
                    {/* Top border line - extend to touch left frame's right border */}
                    <line
                      x1={leftInnerFrameX + leftInnerFrameWidth}
                      y1={innerFrameY}
                      x2={rightInnerFrameX + rightInnerFrameWidth}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Left border line - touches left frame's right border */}
                    <line
                      x1={leftInnerFrameX + leftInnerFrameWidth}
                      y1={innerFrameY}
                      x2={leftInnerFrameX + leftInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Right border line - always show when there's a third/center frame (separate frame), or when innerFrameSlidingSection === 'right', or center section left part */}
                    {needsThirdFrame || needsCenterFrame || 
                     (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') ||
                     (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') ? (
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="0.5"
                      />
                    ) : null}
                    {/* Bottom border line - extend to touch left frame's right border */}
                    <line
                      x1={leftInnerFrameX + leftInnerFrameWidth}
                      y1={innerFrameY + innerFrameHeight}
                      x2={rightInnerFrameX + rightInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal - when "right" is selected, right frame has full borders, so reveal should be centered within right frame */}
                    <rect
                      x={doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                        ? rightInnerFrameX + innerFrameThickness / 2  // For center section "right": centered within right frame
                        : leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness / 2}  // For left/right sections "right": extends from left frame's right border
                      y={innerFrameY + innerFrameThickness / 2}
                      width={rightInnerFrameWidth - innerFrameThickness * 2}
                      height={innerFrameHeight - innerFrameThickness}
                      fill={interiorColorHex}
                    />
                  </>
                ) : (
                  <>
                    {/* For normal doors (terrace doors): show left border like first frame. For sliding doors: no left border (slides to the right) */}
                    {/* Left border - show for normal doors */}
                    {doorType !== 'sliding' && (
                      <rect
                        x={rightInnerFrameX}
                        y={innerFrameY}
                        width={innerFrameThickness}
                        height={innerFrameHeight}
                        fill={interiorColorHex}
                      />
                    )}
                    {/* Top border — ] shape at meeting stile, no left stile */}
                    <rect
                      x={isSplitSlidingSashLayout ? meetingStileX : (rightSashNoLeftStile ? rightSashRailX : rightInnerFrameX)}
                      y={innerFrameY}
                      width={rightSashNoLeftStile ? rightSashRailWidth : rightInnerFrameWidth}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Right border - always show for normal doors, or when there's a third/center frame (separate frame), or when slidingPosition === 'left' and innerFrameSlidingSection === 'left' (default), or when center section and innerFrameSlidingSectionLeft === 'left' (default) */}
                    {(doorType !== 'sliding' || needsThirdFrame || needsCenterFrame || rightSashNoLeftStile) && (
                      <rect
                        x={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y={innerFrameY}
                        width={innerFrameThickness}
                        height={innerFrameHeight}
                        fill={interiorColorHex}
                      />
                    )}
                    {/* Bottom border — ] shape at meeting stile, no left stile */}
                    <rect
                      x={isSplitSlidingSashLayout ? meetingStileX : (rightSashNoLeftStile ? rightSashRailX : rightInnerFrameX)}
                      y={innerFrameY + innerFrameHeight - innerFrameThickness}
                      width={rightSashNoLeftStile ? rightSashRailWidth : rightInnerFrameWidth}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Borderlines for visible borders */}
                    {/* Top border line */}
                    <line
                      x1={isSplitSlidingSashLayout ? meetingStileX : (rightSashNoLeftStile ? rightSashRailX : rightInnerFrameX)}
                      y1={innerFrameY}
                      x2={rightInnerFrameX + rightInnerFrameWidth}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Left border line - show for normal doors */}
                    {doorType !== 'sliding' && (
                      <line
                        x1={rightInnerFrameX}
                        y1={innerFrameY}
                        x2={rightInnerFrameX}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="0.5"
                      />
                    )}
                    {/* Right border line - always show to differentiate from vertical divider */}
                    <line
                      x1={rightInnerFrameX + rightInnerFrameWidth}
                      y1={innerFrameY}
                      x2={rightInnerFrameX + rightInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Bottom border line - removed as requested */}
                    {/* Inner frame reveal — abuts shared meeting stile, no duplicate left rail */}
                    <rect
                      x={isSplitSlidingSashLayout ? meetingStileX + innerFrameThickness / 2 : (doorType !== 'sliding' ? rightInnerFrameX + innerFrameThickness / 2 : rightSashContentX - innerFrameThickness / 2)}
                      y={innerFrameY + innerFrameThickness / 2}
                      width={
                        doorType !== 'sliding' 
                          ? rightInnerFrameWidth - innerFrameThickness * 2 
                          : rightSashNoLeftStile
                          ? (isSplitSlidingSashLayout
                            ? ((needsThirdFrame || needsCenterFrame)
                              ? rightInnerFrameWidth - innerFrameThickness * 2
                              : rightInnerFrameWidth - innerFrameThickness * 1.5)
                            : rightSashContentWidth)
                          : rightInnerFrameWidth - innerFrameThickness / 2
                      }
                      height={innerFrameHeight - innerFrameThickness}
                      fill={interiorColorHex}
                    />
                  </>
                )}
                {/* Corner joints - when innerFrameSlidingSection === 'right', right frame extends to touch left frame */}
                {doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right' ? (
                  // Full corner joints - extends to touch left frame's right border
                  woodJoint === 'joint-45' ? (
                    <>
                      {/* Top-left corner - horizontal line from left frame's right border to outer frame edge */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-left corner - diagonal wood joint */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY}
                        x2={leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-right corner - horizontal line from inner frame white border to outer frame edge */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-right corner - diagonal wood joint */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom-left corner - extends from left frame's right border */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight}
                        x2={leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom border horizontal line - removed as requested (was: horizontal line from rightInnerFrameX to rightInnerFrameX + rightInnerFrameWidth at innerFrameY + innerFrameHeight with strokeWidth="1.2") */}
                      {/* Bottom-right corner */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight}
                        x2={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom border horizontal line at innerFrameY + innerFrameHeight - REMOVED */}
                    </>
                  ) : (
                    <>
                      {/* Top-left corner - horizontal line from left frame's right border to outer frame edge */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-left corner - horizontal wood joint */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY + innerFrameThickness}
                        x2={leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-right corner - horizontal line from inner frame white border to outer frame edge */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-right corner - horizontal wood joint */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y1={innerFrameY + innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom-left corner - extends from left frame's right border */}
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      <line
                        x1={leftInnerFrameX + leftInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={leftInnerFrameX + leftInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom-right corner */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    </>
                  )
                ) : (
                  // For normal doors: full corner joints (left and right). For sliding doors: only right side (no left border)
                  woodJoint === 'joint-45' ? (
                    <>
                      {/* Top-left corner - only for normal doors */}
                      {doorType !== 'sliding' && (
                        <>
                          {/* Top-left corner - horizontal line from inner frame white border to outer frame edge */}
                          <line
                            x1={leftInnerFrameX + leftInnerFrameWidth}
                            y1={innerFrameY}
                            x2={rightInnerFrameX}
                            y2={innerFrameY}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                          {/* Top-left corner - diagonal wood joint */}
                          <line
                            x1={rightInnerFrameX}
                            y1={innerFrameY}
                            x2={rightInnerFrameX + innerFrameThickness}
                            y2={innerFrameY + innerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                          {/* Bottom-left corner */}
                          <line
                            x1={rightInnerFrameX}
                            y1={innerFrameY + innerFrameHeight}
                            x2={rightInnerFrameX + innerFrameThickness}
                            y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                        </>
                      )}
                      {/* Top-right corner - horizontal line from inner frame white border to outer frame edge */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top-right corner - diagonal wood joint */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom-right corner */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight}
                        x2={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    </>
                  ) : (
                    <>
                      {/* Top-left corner - only for normal doors */}
                      {doorType !== 'sliding' && (
                        <>
                          {/* Top-left corner - horizontal line from inner frame white border to outer frame edge */}
                          <line
                            x1={leftInnerFrameX + leftInnerFrameWidth}
                            y1={innerFrameY}
                            x2={rightInnerFrameX}
                            y2={innerFrameY}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                          {/* Top-left corner - horizontal wood joint */}
                          <line
                            x1={rightInnerFrameX}
                            y1={innerFrameY + innerFrameThickness}
                            x2={rightInnerFrameX + innerFrameThickness}
                            y2={innerFrameY + innerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                          {/* Bottom-left corner */}
                          <line
                            x1={rightInnerFrameX}
                            y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                            x2={rightInnerFrameX + innerFrameThickness}
                            y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                          <line
                            x1={rightInnerFrameX}
                            y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                            x2={rightInnerFrameX}
                            y2={innerFrameY + innerFrameHeight}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                          />
                        </>
                      )}
                      {/* Top border joint - horizontal line from inner frame white border to outer frame edge */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY}
                        x2={doorSectionX + doorSectionWidth}
                        y2={innerFrameY}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Top border joint - horizontal wood joint */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y1={innerFrameY + innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      {/* Bottom border joints - right side */}
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                      <line
                        x1={rightInnerFrameX + rightInnerFrameWidth}
                        y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                        x2={rightInnerFrameX + rightInnerFrameWidth}
                        y2={innerFrameY + innerFrameHeight}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    </>
                  )
                )}
                {/* Spacer / glazing bead for right inner frame */}
                {/* Always use black border (#282C30) like left frame */}
                <rect
                  x={
                    isSplitSlidingSashLayout
                      ? meetingStileX
                      : doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
                    ? leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness
                    : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                    ? leftInnerFrameX + leftInnerFrameWidth + innerFrameThickness
                    : doorType !== 'sliding'
                    ? rightInnerFrameX + innerFrameThickness
                    : rightSashNoLeftStile
                    ? rightSashContentX
                    : rightInnerFrameX + innerFrameThickness}
                  y={innerFrameY + innerFrameThickness}
                  width={
                    doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right'
                      ? rightInnerFrameWidth - innerFrameThickness * 2
                      : doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right'
                      ? rightInnerFrameWidth - innerFrameThickness * 2
                      : doorType !== 'sliding'
                      ? rightInnerFrameWidth - innerFrameThickness * 2
                      : rightSashNoLeftStile
                      ? (isSplitSlidingSashLayout
                        ? ((needsThirdFrame || needsCenterFrame)
                          ? rightInnerFrameWidth - innerFrameThickness * 2
                          : rightInnerFrameWidth - innerFrameThickness)
                        : rightSashContentWidth)
                      : rightInnerFrameWidth - innerFrameThickness * 2
                  }
                  height={innerFrameHeight - innerFrameThickness * 2}
                  fill={glazingBeadFill}  // Always black spacer border (like left frame)
                />
                {/* Horizontal line at inner edge of bottom frame — hidden on Schuifpui (sliding) */}
                {doorType !== 'sliding' && (
                <line
                  x1={
                    isSplitSlidingSashLayout
                      ? meetingStileX
                      : doorType !== 'sliding'
                      ? rightInnerFrameX + innerFrameThickness
                      : rightSashNoLeftStile
                      ? rightSashContentX
                      : rightInnerFrameX + innerFrameThickness
                  }
                  y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                  x2={rightInnerFrameX + rightInnerFrameWidth - innerFrameThickness}
                  y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                  stroke={frameEdgeStroke}
                  strokeWidth="1.2"
                />
                )}
              </g>
              )}

              {/* Center Inner Frame (frame 3) — full left border vs frame 2; owns shared right stile with frame 4 */}
              {needsCenterFrame && centerInnerFrameX !== null && centerInnerFrameWidth !== null && (
              <g>
                {/* Left border — full stile vs frame 2 */}
                <rect
                  x={centerInnerFrameX}
                  y={innerFrameY}
                  width={innerFrameThickness}
                  height={innerFrameHeight}
                  fill={interiorColorHex}
                />
                {/* Top border */}
                <rect
                  x={centerInnerFrameX}
                  y={innerFrameY}
                  width={centerInnerFrameWidth}
                  height={innerFrameThickness}
                  fill={interiorColorHex}
                />
                {/* Right border — shared stile with frame 4 */}
                {centerSashHasRightStile && (
                  <rect
                    x={centerInnerFrameX + centerInnerFrameWidth - innerFrameThickness}
                    y={innerFrameY}
                    width={innerFrameThickness}
                    height={innerFrameHeight}
                    fill={interiorColorHex}
                  />
                )}
                {/* Bottom border */}
                <rect
                  x={centerInnerFrameX}
                  y={innerFrameY + innerFrameHeight - innerFrameThickness}
                  width={centerInnerFrameWidth}
                  height={innerFrameThickness}
                  fill={interiorColorHex}
                />
                {/* Borderlines */}
                <line
                  x1={centerInnerFrameX}
                  y1={innerFrameY}
                  x2={centerInnerFrameX + centerInnerFrameWidth}
                  y2={innerFrameY}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                />
                <line
                  x1={centerInnerFrameX}
                  y1={innerFrameY}
                  x2={centerInnerFrameX}
                  y2={innerFrameY + innerFrameHeight}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                />
                {centerSashHasRightStile && (
                  <line
                    x1={centerInnerFrameX + centerInnerFrameWidth}
                    y1={innerFrameY}
                    x2={centerInnerFrameX + centerInnerFrameWidth}
                    y2={innerFrameY + innerFrameHeight}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                )}
                <line
                  x1={centerInnerFrameX}
                  y1={innerFrameY + innerFrameHeight}
                  x2={centerInnerFrameX + centerInnerFrameWidth}
                  y2={innerFrameY + innerFrameHeight}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                />
                {/* Inner frame reveal */}
                <rect
                  x={centerInnerFrameX + innerFrameThickness / 2}
                  y={innerFrameY + innerFrameThickness / 2}
                  width={centerInnerFrameWidth - innerFrameThickness * 2}
                  height={innerFrameHeight - innerFrameThickness}
                  fill={interiorColorHex}
                />
                {/* Corner joints */}
                {woodJoint === 'joint-45' ? (
                  <>
                    <line
                      x1={doorSectionX}
                      y1={innerFrameY}
                      x2={centerInnerFrameX}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={centerInnerFrameX}
                      y1={innerFrameY}
                      x2={centerInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={centerInnerFrameX}
                      y1={innerFrameY + innerFrameHeight}
                      x2={centerInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                  </>
                ) : (
                  <>
                    <line
                      x1={doorSectionX}
                      y1={innerFrameY}
                      x2={centerInnerFrameX}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={centerInnerFrameX}
                      y1={innerFrameY + innerFrameThickness}
                      x2={centerInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={centerInnerFrameX}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={centerInnerFrameX + innerFrameThickness}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={centerInnerFrameX}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={centerInnerFrameX}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                  </>
                )}
                {/* Spacer / glazing bead */}
                <rect
                  x={centerInnerFrameX + innerFrameThickness}
                  y={innerFrameY + innerFrameThickness}
                  width={centerInnerFrameWidth - innerFrameThickness * 2}
                  height={innerFrameHeight - innerFrameThickness * 2}
                  fill={glazingBeadFill}
                />
              </g>
              )}

              {/* Third Inner Frame - only when slidingPosition === 'right' or 'center' */}
              {(needsThirdFrame || needsCenterFrame) && thirdInnerFrameX !== null && thirdInnerFrameWidth !== null && (
              <g>
                {/* Outer frame (interior color) with borderline */}
                {/* Frame 4 (center layout): ] shape shared with frame 3. Frame 3 (right layout): full [] */}
                {fourthSashNoLeftStile ? (
                  <>
                    {/* No left border — abuts shared stile from adjacent sash */}
                    {/* Top border */}
                    <rect
                      x={thirdRailX}
                      y={innerFrameY}
                      width={thirdInnerFrameWidth}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Right border */}
                    <rect
                      x={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y={innerFrameY}
                      width={innerFrameThickness}
                      height={innerFrameHeight}
                      fill={interiorColorHex}
                    />
                    {/* Bottom border */}
                    <rect
                      x={thirdRailX}
                      y={innerFrameY + innerFrameHeight - innerFrameThickness}
                      width={thirdInnerFrameWidth}
                      height={innerFrameThickness}
                      fill={interiorColorHex}
                    />
                    {/* Borderlines */}
                    {/* Top border line */}
                    <line
                      x1={thirdRailX}
                      y1={innerFrameY}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Right border line */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Bottom border line */}
                    {/* Inner frame reveal - no duplicate left stile */}
                    <rect
                      x={thirdRailX}
                      y={innerFrameY + innerFrameThickness / 2}
                      width={thirdInnerFrameWidth - innerFrameThickness / 2}
                      height={innerFrameHeight - innerFrameThickness}
                      fill={interiorColorHex}
                    />
                  </>
                ) : (
                  <>
                    {/* Full borders like left frame (default) */}
                    <rect
                      x={thirdInnerFrameX}
                      y={innerFrameY}
                      width={thirdInnerFrameWidth}
                      height={innerFrameHeight}
                      fill={interiorColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={thirdInnerFrameX + innerFrameThickness / 2}
                      y={innerFrameY + innerFrameThickness / 2}
                      width={thirdInnerFrameWidth - innerFrameThickness}
                      height={innerFrameHeight - innerFrameThickness}
                      fill={interiorColorHex}
                    />
                  </>
                )}
                {/* Corner joints */}
                {woodJoint === 'joint-45' ? (
                  <>
                    {/* Top-left corner - only show when left border exists */}
                    {!(fourthSashNoLeftStile) && (
                      <>
                        {/* Top-left corner - horizontal line from inner frame white border to outer frame edge */}
                        <line
                          x1={doorSectionX}
                          y1={innerFrameY}
                          x2={thirdInnerFrameX}
                          y2={innerFrameY}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                        {/* Top-left corner - diagonal wood joint */}
                        <line
                          x1={thirdInnerFrameX}
                          y1={innerFrameY}
                          x2={thirdInnerFrameX + innerFrameThickness}
                          y2={innerFrameY + innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                    {/* Top-right corner - horizontal line from inner frame white border to outer frame edge */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY}
                      x2={doorSectionX + doorSectionWidth}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Top-right corner - diagonal wood joint */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Bottom-left corner - only show when left border exists */}
                    {!(fourthSashNoLeftStile) && (
                      <line
                        x1={thirdInnerFrameX}
                        y1={innerFrameY + innerFrameHeight}
                        x2={thirdInnerFrameX + innerFrameThickness}
                        y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                        stroke={frameEdgeStroke}
                        strokeWidth="1.2"
                      />
                    )}
                    {/* Bottom-right corner */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY + innerFrameHeight}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                  </>
                ) : (
                  <>
                    {/* Top-left corner - only show when left border exists */}
                    {!(fourthSashNoLeftStile) && (
                      <>
                        {/* Top-left corner - horizontal line from inner frame white border to outer frame edge */}
                        <line
                          x1={doorSectionX}
                          y1={innerFrameY}
                          x2={thirdInnerFrameX}
                          y2={innerFrameY}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                        {/* Top-left corner - horizontal wood joint */}
                        <line
                          x1={thirdInnerFrameX}
                          y1={innerFrameY + innerFrameThickness}
                          x2={thirdInnerFrameX + innerFrameThickness}
                          y2={innerFrameY + innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                    {/* Top-right corner - horizontal line from inner frame white border to outer frame edge */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY}
                      x2={doorSectionX + doorSectionWidth}
                      y2={innerFrameY}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Top-right corner - horizontal wood joint */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y1={innerFrameY + innerFrameThickness}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    {/* Bottom-left corner - only show when left border exists */}
                    {!(fourthSashNoLeftStile) && (
                      <>
                        <line
                          x1={thirdInnerFrameX}
                          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                          x2={thirdInnerFrameX + innerFrameThickness}
                          y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                        <line
                          x1={thirdInnerFrameX}
                          y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                          x2={thirdInnerFrameX}
                          y2={innerFrameY + innerFrameHeight}
                          stroke={frameEdgeStroke}
                          strokeWidth="1.2"
                        />
                      </>
                    )}
                    {/* Bottom-right corner */}
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={thirdInnerFrameX + thirdInnerFrameWidth}
                      y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                      x2={thirdInnerFrameX + thirdInnerFrameWidth}
                      y2={innerFrameY + innerFrameHeight}
                      stroke={frameEdgeStroke}
                      strokeWidth="1.2"
                    />
                  </>
                )}
                {/* Spacer / glazing bead for third inner frame */}
                {/* Spacer / glazing bead for third inner frame */}
                <rect
                  x={
                    fourthSashNoLeftStile
                      ? thirdRailX
                      : thirdInnerFrameX + innerFrameThickness
                  }
                  y={innerFrameY + innerFrameThickness}
                  width={
                    fourthSashNoLeftStile
                      ? thirdInnerFrameWidth - innerFrameThickness
                      : thirdInnerFrameWidth - innerFrameThickness * 2
                  }
                  height={innerFrameHeight - innerFrameThickness * 2}
                  fill={glazingBeadFill}
                />
                {/* Horizontal line at inner edge of bottom frame — hidden on Schuifpui (sliding) */}
                {doorType !== 'sliding' && (
                <line
                  x1={
                    doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left'
                      ? thirdInnerFrameX  // No left border, starts from left edge
                      : thirdInnerFrameX + innerFrameThickness  // Default: has left border
                  }
                  y1={innerFrameY + innerFrameHeight - innerFrameThickness}
                  x2={thirdInnerFrameX + thirdInnerFrameWidth - innerFrameThickness}
                  y2={innerFrameY + innerFrameHeight - innerFrameThickness}
                  stroke={frameEdgeStroke}
                  strokeWidth="1.2"
                />
                )}
              </g>
              )}
        </>

        {/* Corner joints - 45° (diagonal) or 90° (horizontal/vertical) */}
        {woodJoint === 'joint-45' ? (
          <>
            {/* 45° mitered corners - diagonal lines */}
            {/* For voordeur: show diagonal joints at top (like inner frame), otherwise only bottom corners */}
            {doorType === 'voordeur' ? (
              <>
                {/* Top-left corner - diagonal wood joint */}
                <line
                  x1={startX}
                  y1={startY}
                  x2={startX + frameThickness}
                  y2={startY + frameThickness}
                  stroke={frameEdgeStroke}
                  strokeWidth="1.2"
                />
                {/* Top-right corner - diagonal wood joint */}
                <line
                  x1={startX + scaledWidth}
                  y1={startY}
                  x2={startX + scaledWidth - frameThickness}
                  y2={startY + frameThickness}
                  stroke={frameEdgeStroke}
                  strokeWidth="1.2"
                />
              </>
            ) : null}
            {/* Bottom corners - horizontal lines */}
            <line
              x1={startX}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + frameThickness}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
            <line
              x1={startX + scaledWidth - frameThickness}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
          </>
        ) : (
          <>
            {/* 90° corners - horizontal lines at top and bottom */}
            {/* Top-left corner */}
            <line
              x1={startX}
              y1={startY + frameThickness}
              x2={startX + frameThickness}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
            {/* Top-right corner */}
            <line
              x1={startX + scaledWidth - frameThickness}
              y1={startY + frameThickness}
              x2={startX + scaledWidth}
              y2={startY + frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
            {/* Bottom-left corner - horizontal line only */}
            <line
              x1={startX}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + frameThickness}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
            {/* Bottom-right corner - horizontal line only */}
            <line
              x1={startX + scaledWidth - frameThickness}
              y1={startY + scaledHeight - frameThickness}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight - frameThickness}
              stroke={frameEdgeStroke}
              strokeWidth="1.2"
            />
          </>
        )}

        {/* Central Divider (for double-pane) - only for non-sliding doors, and not when we have 3 or 4 frames */}
        {/* For sliding doors, frames touch each other, so no central divider */}
        {isDoublePane && doorType !== 'sliding' && !needsThirdFrame && !needsCenterFrame && (
          <rect
            x={doorSectionX + doorSectionWidth / 2 - dividerWidth / 2}
            y={doorCenterDividerStartY}
            width={dividerWidth}
            height={doorCenterDividerHeight}
            fill={exteriorColorHex}
            stroke={isLightFrameColor(exteriorColorHex) ? FRAME_EDGE_STROKE_DARK : 'none'}
            strokeWidth={0.6}
          />
        )}


        {/* Overhead per-part horizontal dimensions (sub ruler at the top when top-light is active, for 1 or 2 parts) */}
        {hasOverhead && overheadDividerY && overheadParts > 0 && (
          Array.from({ length: overheadParts }, (_, index) => {
            const startAccum = normalizedOverheadPartWidths
              .slice(0, index)
              .reduce((sum, w) => sum + w, 0);
            const overheadPartWidthMm = normalizedOverheadPartWidths[index];
            const endAccum = startAccum + overheadPartWidthMm;
            const segStart = doorSectionX + (startAccum / totalOverheadPartWidth) * doorSectionWidth;
            const segEnd = doorSectionX + (endAccum / totalOverheadPartWidth) * doorSectionWidth;
            // Align with the top of the overhead band (below the outer frame)
            const overheadY = startY - 15;

            return (
              <g key={`overhead-part-dim-${index}`}>
                {/* segment line */}
                <line
                  x1={segStart}
                  y1={overheadY}
                  x2={segEnd}
                  y2={overheadY}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* left tick */}
                <line
                  x1={segStart}
                  y1={overheadY - 5}
                  x2={segStart}
                  y2={overheadY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* right tick */}
                <line
                  x1={segEnd}
                  y1={overheadY - 5}
                  x2={segEnd}
                  y2={overheadY + 5}
                  stroke="#4A4A4A"
                  strokeWidth="1"
                />
                {/* Read-only label (display only, not editable) */}
                <foreignObject
                  x={(segStart + segEnd) / 2 - 25}
                  y={overheadY - 4}
                  width="50"
                  height="22"
                >
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#4A4A4A',
                  }}>
                    {Math.round(overheadPartWidthMm)}
                  </div>
                </foreignObject>
              </g>
            );
          })
        )}

        {/* Overhead Section - parts with windowType features */}
        {hasOverhead && overheadDividerY && (
          Array.from({ length: overheadParts }, (_, index) => {
            const startAccum = normalizedOverheadPartWidths.slice(0, index).reduce((sum, w) => sum + w, 0);
            const overheadPartWidthMm = normalizedOverheadPartWidths[index];
            // When there are 2 parts, extend each glass part towards the middle (divider) to make divider appear smaller
            const dividerOverlap = overheadParts === 2 ? frameThickness / 2 : 0;
            // Left part extends right towards divider, right part extends left towards divider
            const overheadPartWidthPx = (overheadPartWidthMm / totalOverheadPartWidth) * doorSectionWidth + dividerOverlap;
            const overheadPartX = index === 0 
              ? doorSectionX + (startAccum / totalOverheadPartWidth) * doorSectionWidth  // Left part: extend right
              : doorSectionX + (startAccum / totalOverheadPartWidth) * doorSectionWidth - dividerOverlap;  // Right part: extend left
            // Overhead starts at top of door section (after frame)
            const overheadPartY = startY + innerFrameInset;
            // Overhead height goes from top to the horizontal divider (the frame sits on top of inner frames)
            const overheadPartHeight = overheadDividerY - (startY + innerFrameInset);
            const overheadPartWindowType = currentOverheadPartWindowTypes[index] || 'fixed-sash';
            const hasInnerFrame = overheadPartWindowType === 'fixed-glass' ||
                                  overheadPartWindowType === 'tilt-only' || 
                                  overheadPartWindowType === 'tilt-turn-left' || 
                                  overheadPartWindowType === 'tilt-turn-right';
            const isHovered = hoveredOverheadPartIndex === index;
            // Vaste vleugel: glass fills opening with thin dark interior outline only (no interior sash frame)
            const overheadOpeningX = overheadPartX + innerFrameInset;
            const overheadOpeningY = overheadPartY;
            const overheadOpeningW = overheadPartWidthPx - innerFrameInset * 2;
            const overheadOpeningH = overheadPartHeight;
            const overheadBorderT = 2 * scale;
            const overheadGlassX = overheadOpeningX + overheadBorderT;
            const overheadGlassY = overheadOpeningY + overheadBorderT;
            const overheadGlassW = Math.max(0, overheadOpeningW - overheadBorderT * 2);
            const overheadGlassH = Math.max(0, overheadOpeningH - overheadBorderT * 2);
            
            return (
              <g key={`overhead-part-${index}-${overheadPartWindowType}`}>
                {/* Overhead vaste vleugel / vast paneel — glass + thin interior outline, no inner sash */}
                {!hasInnerFrame && (
                  <>
                    <g pointerEvents="none">
                      {renderOverheadFixedSashOutline(overheadOpeningX, overheadOpeningY, overheadOpeningW, overheadOpeningH)}
                    </g>
                    {/* Clickable glass fill */}
                    <rect
                      x={overheadGlassX}
                      y={overheadGlassY}
                      width={overheadGlassW}
                      height={overheadGlassH}
                      fill={overheadPartWindowType === 'fixed-panel' ? "url(#panel-gradient)" : glassFill}
                      pointerEvents="none"
                    />
                    {overheadPartWindowType !== 'fixed-panel' && glassTexturePattern && (
                      <rect
                        x={overheadGlassX}
                        y={overheadGlassY}
                        width={overheadGlassW}
                        height={overheadGlassH}
                        fill={glassTexturePattern}
                        opacity="1"
                        pointerEvents="none"
                      />
                    )}
                    {/* Full opening click target (frame + glass), like Kozijnen */}
                    <rect
                      x={overheadOpeningX}
                      y={overheadOpeningY}
                      width={overheadOpeningW}
                      height={overheadOpeningH}
                      fill="transparent"
                      style={{ cursor: isHovered ? 'pointer' : 'default', pointerEvents: 'auto' }}
                      onMouseEnter={() => setHoveredOverheadPartIndex(index)}
                      onMouseLeave={() => setHoveredOverheadPartIndex(null)}
                      onClick={() => {
                        setSelectedOverheadPartIndex(index);
                        setSettingsOpen(true);
                      }}
                    >
                      <title>Click to configure Overhead Part {index + 1} settings</title>
                    </rect>
                  </>
                )}
                
                {/* Invisible overlay for mouse events when inner frame is present */}
                {hasInnerFrame && (
                  <rect
                    x={overheadPartX + innerFrameInset}
                    y={overheadPartY}
                    width={overheadPartWidthPx - innerFrameInset * 2}
                    height={overheadPartHeight}
                    fill="transparent"
                    style={{ cursor: isHovered ? 'pointer' : 'default', pointerEvents: 'auto' }}
                    onMouseEnter={() => setHoveredOverheadPartIndex(index)}
                    onMouseLeave={() => setHoveredOverheadPartIndex(null)}
                    onClick={() => {
                      setSelectedOverheadPartIndex(index);
                      setSettingsOpen(true);
                    }}
                  >
                    <title>Click to configure Overhead Part {index + 1} settings</title>
                  </rect>
                )}
                
                {/* Inner frame for overhead parts */}
                {hasInnerFrame && (() => {
                  // Calculate inner frame dimensions for this overhead part
                  const overheadInnerFrameX = overheadPartX + innerFrameInset;
                  const overheadInnerFrameY = overheadPartY;
                  const overheadInnerFrameWidth = overheadPartWidthPx - innerFrameInset * 2;
                  // Inner frame height should fill the overhead part area
                  const overheadInnerFrameHeight = overheadPartHeight;
                  
                  // Use smaller inner frame thickness for overhead parts (like DrawingBoard.jsx)
                  const overheadInnerFrameThickness = frameThickness * 0.7;
                  
                  // Glass position accounts for dark borders (2 * scale on each side)
                  const overheadInnerGlassX = overheadInnerFrameX + overheadInnerFrameThickness + 2 * scale;
                  const overheadInnerGlassY = overheadInnerFrameY + overheadInnerFrameThickness + 2 * scale;
                  const overheadInnerGlassWidth = overheadInnerFrameWidth - overheadInnerFrameThickness * 2 - 4 * scale;
                  const overheadInnerGlassHeight = overheadInnerFrameHeight - overheadInnerFrameThickness * 2 - 4 * scale;
                  
                  return (
                    <g style={{ pointerEvents: 'none' }}>
                      {renderOverheadInnerSashStiles(
                        overheadInnerFrameX,
                        overheadInnerFrameY,
                        overheadInnerFrameWidth,
                        overheadInnerFrameHeight,
                        overheadInnerFrameThickness,
                      )}
                      {/* Corner joints */}
                      {woodJoint === 'joint-45' ? (
                        <>
                          <line
                            x1={overheadInnerFrameX}
                            y1={overheadInnerFrameY}
                            x2={overheadInnerFrameX + overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX + overheadInnerFrameWidth}
                            y1={overheadInnerFrameY}
                            x2={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight}
                            x2={overheadInnerFrameX + overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX + overheadInnerFrameWidth}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight}
                            x2={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                        </>
                      ) : (
                        <>
                          <line
                            x1={overheadInnerFrameX}
                            y1={overheadInnerFrameY + overheadInnerFrameThickness}
                            x2={overheadInnerFrameX + overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness}
                            y1={overheadInnerFrameY + overheadInnerFrameThickness}
                            x2={overheadInnerFrameX + overheadInnerFrameWidth}
                            y2={overheadInnerFrameY + overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            x2={overheadInnerFrameX + overheadInnerFrameThickness}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            x2={overheadInnerFrameX}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            x2={overheadInnerFrameX + overheadInnerFrameWidth}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                          <line
                            x1={overheadInnerFrameX + overheadInnerFrameWidth}
                            y1={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness}
                            x2={overheadInnerFrameX + overheadInnerFrameWidth}
                            y2={overheadInnerFrameY + overheadInnerFrameHeight}
                            stroke={frameEdgeStroke}
                            strokeWidth="1.2"
                            pointerEvents="none"
                          />
                        </>
                      )}
                      {renderOverheadInnerSashBorderlines(
                        overheadInnerFrameX,
                        overheadInnerFrameY,
                        overheadInnerFrameWidth,
                        overheadInnerFrameHeight,
                        overheadInnerFrameThickness,
                      )}
                      {/* Dark glazing borders around the glass */}
                      <g pointerEvents="none">
                        {/* Top dark glazing border - leave space for left and right borders */}
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameThickness + 2 * scale}
                          y={overheadInnerFrameY + overheadInnerFrameThickness}
                          width={overheadInnerFrameWidth - overheadInnerFrameThickness * 2 - 4 * scale}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Bottom dark glazing border - leave space for left and right borders */}
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameThickness + 2 * scale}
                          y={overheadInnerFrameY + overheadInnerFrameHeight - overheadInnerFrameThickness - 2 * scale}
                          width={overheadInnerFrameWidth - overheadInnerFrameThickness * 2 - 4 * scale}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Left dark glazing border */}
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameThickness}
                          y={overheadInnerFrameY + overheadInnerFrameThickness}
                          width={2 * scale}
                          height={overheadInnerFrameHeight - overheadInnerFrameThickness * 2}
                          fill={glazingBeadFill}
                        />
                        {/* Right dark glazing border */}
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness - 2 * scale}
                          y={overheadInnerFrameY + overheadInnerFrameThickness + 2 * scale}
                          width={2 * scale}
                          height={overheadInnerFrameHeight - overheadInnerFrameThickness * 2 - 4 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Additional coverage for the inner edge line itself */}
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameWidth - overheadInnerFrameThickness - 1}
                          y={overheadInnerFrameY + overheadInnerFrameThickness + 2 * scale}
                          width={1}
                          height={overheadInnerFrameHeight - overheadInnerFrameThickness * 2 - 4 * scale}
                          fill={glazingBeadFill}
                        />
                      </g>
                      
                      {/* Inner glass with gradient - positioned inside dark borders */}
                      <g pointerEvents="none">
                        <rect
                          x={overheadInnerFrameX + overheadInnerFrameThickness + 2 * scale}
                          y={overheadInnerFrameY + overheadInnerFrameThickness + 2 * scale}
                          width={overheadInnerFrameWidth - overheadInnerFrameThickness * 2 - 4 * scale}
                          height={overheadInnerFrameHeight - overheadInnerFrameThickness * 2 - 4 * scale}
                          fill="url(#glass-gradient)"
                        />
                        {/* Glass texture overlay */}
                        {glassTexturePattern && (
                          <rect
                            x={overheadInnerFrameX + overheadInnerFrameThickness + 2 * scale}
                            y={overheadInnerFrameY + overheadInnerFrameThickness + 2 * scale}
                            width={overheadInnerFrameWidth - overheadInnerFrameThickness * 2 - 4 * scale}
                            height={overheadInnerFrameHeight - overheadInnerFrameThickness * 2 - 4 * scale}
                            fill={glassTexturePattern}
                            opacity="1"
                          />
                        )}
                      </g>
                      {renderOverheadInnerSashBottomBorders(
                        overheadInnerFrameX,
                        overheadInnerFrameY,
                        overheadInnerFrameWidth,
                        overheadInnerFrameHeight,
                        overheadInnerFrameThickness,
                      )}
                      {/* WindowType lines - matching DrawingBoard.jsx patterns */}
                      {overheadPartWindowType === 'tilt-only' && (
                        <g pointerEvents="none">
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={overheadInnerGlassX}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={overheadInnerGlassX + overheadInnerGlassWidth}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                        </g>
                      )}
                      
                      {/* Diagonal lines for Tilt & Turn Left */}
                      {overheadPartWindowType === 'tilt-turn-left' && (
                        <g pointerEvents="none">
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={overheadInnerGlassX}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={overheadInnerGlassX + overheadInnerGlassWidth}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from top-right to middle-left */}
                          <line
                            x1={overheadInnerGlassX + overheadInnerGlassWidth}
                            y1={overheadInnerGlassY}
                            x2={overheadInnerGlassX}
                            y2={overheadInnerGlassY + overheadInnerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from bottom-right to middle-left */}
                          <line
                            x1={overheadInnerGlassX + overheadInnerGlassWidth}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX}
                            y2={overheadInnerGlassY + overheadInnerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                        </g>
                      )}
                      
                      {/* Diagonal lines for Tilt & Turn Right */}
                      {overheadPartWindowType === 'tilt-turn-right' && (
                        <g pointerEvents="none">
                          {/* Line from bottom-left to middle-top */}
                          <line
                            x1={overheadInnerGlassX}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from bottom-right to middle-top */}
                          <line
                            x1={overheadInnerGlassX + overheadInnerGlassWidth}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth / 2}
                            y2={overheadInnerGlassY}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from top-left to middle-right */}
                          <line
                            x1={overheadInnerGlassX}
                            y1={overheadInnerGlassY}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth}
                            y2={overheadInnerGlassY + overheadInnerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                          {/* Line from bottom-left to middle-right */}
                          <line
                            x1={overheadInnerGlassX}
                            y1={overheadInnerGlassY + overheadInnerGlassHeight}
                            x2={overheadInnerGlassX + overheadInnerGlassWidth}
                            y2={overheadInnerGlassY + overheadInnerGlassHeight / 2}
                            stroke="#999999"
                            strokeWidth="3"
                            strokeDasharray="3"
                          />
                        </g>
                      )}
                    </g>
                  );
                })()}
                
                {/* Hover highlight overlay - rendered after inner frame so it appears on top */}
                {isHovered && (
                  <rect
                    x={overheadOpeningX}
                    y={overheadOpeningY}
                    width={overheadOpeningW}
                    height={hasInnerFrame ? overheadPartHeight : overheadOpeningH}
                    fill="rgba(255, 165, 0, 0.15)"
                    stroke="rgba(255, 140, 0, 0.6)"
                    strokeWidth="2"
                    style={{ pointerEvents: 'none', cursor: 'pointer' }}
                  />
                )}
              </g>
            );
          })
        )}


        {/* Vertical Divider for Overhead Parts (when 2 parts) - width must be same as external frame width (frameThickness) */}
        {/* Draw AFTER overhead glass and horizontal frame so it appears on top */}
        {hasOverhead && overheadParts === 2 && overheadDividerY && (
          <rect
            x={doorSectionX + doorSectionWidth / 2 - frameThickness / 2}
            y={startY + frameThickness}
            width={frameThickness}
            height={overheadDividerY - startY - frameThickness}
            fill={exteriorColorHex}
          />
        )}

        {/* Left Pane Glass */}
        {isDoublePane && (
          <g>
            {supportsInnerFrameGlassPanel ? (
              <>
                {renderInnerFrameGlassPanelFill(
                  leftGlassX,
                  glassY,
                  leftGlassWidth,
                  innerFrameInteractiveHeight
                )}
                {renderInnerFrameInteractionLayer(
                  leftGlassX,
                  glassY,
                  leftGlassWidth,
                  innerFrameInteractiveHeight
                )}
              </>
            ) : (
              <>
            {/* Glass base with gradient */}
            <rect
              x={leftGlassX}
              y={glassY}
              width={leftGlassWidth}
              height={glassHeight}
              fill={glassFill}
              style={{ 
                cursor: doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right' || slidingPosition === 'center') ? 'pointer' : 'default',
                pointerEvents: 'auto'
              }}
              onMouseEnter={() => {
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right')) {
                  setHoveredInnerFrame('left');
                } else if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter('left');
                }
              }}
              onMouseLeave={() => {
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right')) {
                  setHoveredInnerFrame(null);
                } else if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter(null);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && selectedInnerFrame === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrame('left');
                  }, 0);
                } else if (doorType === 'sliding' && slidingPosition === 'center' && selectedInnerFrameCenter === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrameCenter('left');
                  }, 0);
                }
              }}
            />
            {/* Glass texture overlay */}
            {(() => {
              // For center section: leftGlassTexture applies to first 2 frames (left + right)
              // For left/right sections: leftGlassTexture applies to left frame only
              let textureToUse = glassTexturePattern;
              if (doorType === 'sliding') {
                if (slidingPosition === 'center') {
                  // Center section: use leftGlassTexture for first 2 frames
                  textureToUse = leftInnerGlassTexturePattern || glassTexturePattern;
                } else {
                  // Left/right sections: use leftGlassTexture for left frame
                  textureToUse = leftInnerGlassTexturePattern || glassTexturePattern;
                }
              }
              return textureToUse;
            })() && (
              <rect
                x={leftGlassX}
                y={glassY}
                width={leftGlassWidth}
                height={glassHeight}
                fill={(() => {
                  if (doorType === 'sliding') {
                    if (slidingPosition === 'center') {
                      return leftInnerGlassTexturePattern || glassTexturePattern;
                    } else {
                      return leftInnerGlassTexturePattern || glassTexturePattern;
                    }
                  }
                  return glassTexturePattern;
                })()}
                opacity="0.8"
                pointerEvents="none"
              />
            )}
            {/* Inner border — omit right edge on left sash when it shares the meeting stile */}
            {isSplitSlidingSashLayout ? (
              <g pointerEvents="none">
                <line x1={leftGlassX} y1={glassY} x2={leftGlassX + leftGlassWidth} y2={glassY} stroke={frameEdgeStroke} strokeWidth="1" />
                <line x1={leftGlassX} y1={glassY} x2={leftGlassX} y2={glassY + glassHeight} stroke={frameEdgeStroke} strokeWidth="1" />
                <line x1={leftGlassX} y1={glassY + glassHeight} x2={leftGlassX + leftGlassWidth} y2={glassY + glassHeight} stroke={frameEdgeStroke} strokeWidth="1" />
              </g>
            ) : (
            <rect
              x={leftGlassX}
              y={glassY}
              width={leftGlassWidth}
              height={glassHeight}
              fill="none"
              stroke={frameEdgeStroke}
              strokeWidth="1"
              pointerEvents="none"
            />
            )}
              </>
            )}
            {/* Ventilator for left frame - sliding doors left/right sections */}
            {doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && ventilatorSlidingLeft && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = leftGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, leftGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-left-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Ventilator for left frame - sliding doors center section */}
            {doorType === 'sliding' && slidingPosition === 'center' && ventilatorSlidingCenterLeft && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = leftGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, leftGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-center-left-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Parapet panel (bottom solid section) */}
            {hasParapet && parapetTopY < glassY + glassHeight && (
              <>
                {/* Frame/border at top of parapet - same design as inner frame */}
                {parapetTopY > glassY && (
                  <g>
                    {/* Outer frame (parapet color) with borderline */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY - innerFrameThickness}
                      width={leftGlassWidth}
                      height={innerFrameThickness}
                      fill={parapetColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={leftGlassX + innerFrameThickness / 2}
                      y={parapetTopY - innerFrameThickness + innerFrameThickness / 2}
                      width={leftGlassWidth - innerFrameThickness}
                      height={innerFrameThickness / 2}
                      fill={parapetColorHex}
                    />
                  </g>
                )}
                {/* Panel fill - based on panelType */}
                {panelType === 'glass' ? (
                  <>
                    {/* Glass texture for parapet */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY}
                      width={leftGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={leftGlassX}
                        y={parapetTopY}
                        width={leftGlassWidth}
                        height={glassY + glassHeight - parapetTopY}
                        fill={glassTexturePattern}
                        opacity="1"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Panel gradient / solid color for sandwich/horizontal/vertical mullions */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY}
                      width={leftGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={parapetColorHex}
                    />
                    {/* Horizontal mullions - matching Kozijnen panel lines style */}
                    {panelType === 'horizontal-mullions' && (() => {
                      const parapetHeight = glassY + glassHeight - parapetTopY;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetHeight / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineY = parapetTopY + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`h-mullion-double-${i}`}
                                x1={leftGlassX}
                                y1={lineY}
                                x2={leftGlassX + leftGlassWidth}
                                y2={lineY}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                    {/* Vertical mullions - matching Kozijnen panel lines style */}
                    {panelType === 'vertical-mullions' && (() => {
                      const parapetWidth = leftGlassWidth;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetWidth / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineX = leftGlassX + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`v-mullion-double-${i}`}
                                x1={lineX}
                                y1={parapetTopY}
                                x2={lineX}
                                y2={parapetTopY + (glassY + glassHeight - parapetTopY)}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Dark border around panel */}
                <rect
                  x={leftGlassX}
                  y={parapetTopY}
                  width={leftGlassWidth}
                  height={glassY + glassHeight - parapetTopY}
                  fill="none"
                  stroke="#4B4B4B"
                  strokeWidth="1"
                />
              </>
            )}
            {/* Turn lines for left pane - removed for sliding doors */}
            {doorType !== 'sliding' && (
              <g>
                {/* Line from top-left to middle-right */}
                <line
                  x1={leftGlassX}
                  y1={glassY}
                  x2={leftGlassX + leftGlassWidth}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
                {/* Line from bottom-left to middle-right */}
                <line
                  x1={leftGlassX}
                  y1={glassY + glassHeight}
                  x2={leftGlassX + leftGlassWidth}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
              </g>
            )}
            {/* Hover highlight overlay for left frame */}
            {doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && hoveredInnerFrame === 'left' && (
              <rect
                x={leftGlassX}
                y={glassY}
                width={leftGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            {/* Hover highlight overlay for left section (left frame) when center */}
            {doorType === 'sliding' && slidingPosition === 'center' && hoveredInnerFrameCenter === 'left' && (
              <rect
                x={leftGlassX}
                y={glassY}
                width={leftGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            {/* Symbol for left frame (sliding door) */}
            {doorType === 'sliding' && isDoublePane && (
              <>
                {/* Cross symbol when innerFrameSlidingSection === 'left' */}
                {/* For center section: hide when innerFrameSlidingSectionLeft === 'right' (cross moves to right frame) */}
                {!((slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
                 !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
                  <g pointerEvents="none">
                    {/* Horizontal line of cross */}
                    <line
                      x1={leftGlassX + leftGlassWidth / 2 - 30 * scale}
                      y1={glassY + glassHeight / 2}
                      x2={leftGlassX + leftGlassWidth / 2 + 30 * scale}
                      y2={glassY + glassHeight / 2}
                      stroke="#666666"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    {/* Vertical line of cross */}
                    <line
                      x1={leftGlassX + leftGlassWidth / 2}
                      y1={glassY + glassHeight / 2 - 30 * scale}
                      x2={leftGlassX + leftGlassWidth / 2}
                      y2={glassY + glassHeight / 2 + 30 * scale}
                      stroke="#666666"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </g>
                )}
                {/* Right-pointing arrow when innerFrameSlidingSection === 'right' */}
                {/* For center section: show when innerFrameSlidingSectionLeft === 'right' */}
                {((doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') ||
                  (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right')) && (
                  <g pointerEvents="none">
                    {/* Horizontal dashed line (shaft) - longer with more dashes */}
                    <line
                      x1={leftGlassX + leftGlassWidth / 2 - 80 * scale}
                      y1={glassY + glassHeight / 2}
                      x2={leftGlassX + leftGlassWidth / 2 + 15 * scale}
                      y2={glassY + glassHeight / 2}
                      stroke="#666666"
                      strokeWidth="2"
                      strokeDasharray="4 3"
                      strokeLinecap="round"
                    />
                    {/* Vertical line at left end */}
                    <line
                      x1={leftGlassX + leftGlassWidth / 2 - 80 * scale}
                      y1={glassY + glassHeight / 2 - 15 * scale}
                      x2={leftGlassX + leftGlassWidth / 2 - 80 * scale}
                      y2={glassY + glassHeight / 2 + 15 * scale}
                      stroke="#666666"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    {/* Arrowhead (pointing right) - tip at right end, V shape opening to the left */}
                    <path
                      d={`M ${leftGlassX + leftGlassWidth / 2 + 35 * scale} ${glassY + glassHeight / 2} L ${leftGlassX + leftGlassWidth / 2 + 15 * scale} ${glassY + glassHeight / 2 - 12 * scale} M ${leftGlassX + leftGlassWidth / 2 + 35 * scale} ${glassY + glassHeight / 2} L ${leftGlassX + leftGlassWidth / 2 + 15 * scale} ${glassY + glassHeight / 2 + 12 * scale}`}
                      stroke="#666666"
                      strokeWidth="2"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </g>
                )}
              </>
            )}
          </g>
        )}

        {/* Single Pane Glass (when singlePane is true) */}
        {!isDoublePane && (
          <g>
            {/* For voordeur style-1 and style-3: render panel with 2 vertical dividers (3 parts) */}
            {isVoordeur && (doorStyle === 'style-1' || doorStyle === 'style-3') ? (() => {
              // Calculate divider positions - 2 vertical dividers dividing into 3 equal parts
              // Use innerFrameThickness to match the inner frame border width
              // Expand panel to cover dark glazing borders (overlap by 2 * scale on top and right)
              const dividerThickness = innerFrameThickness;
              const totalDividerWidth = dividerThickness * 2;
              const panelOverlap = 2 * scale; // Overlap to cover dark glazing borders
              const partWidth = (leftGlassWidth - totalDividerWidth) / 3;
              const part1X = leftGlassX;
              const part2X = part1X + partWidth + dividerThickness;
              const part3X = part2X + partWidth + dividerThickness;
              
              return (
                <>
                  {/* Panel fill - same color as inner frame, expanded to cover dark glazing borders */}
                  <rect
                    x={leftGlassX}
                    y={glassY - panelOverlap}
                    width={leftGlassWidth + panelOverlap}
                    height={glassHeight + panelOverlap}
                    fill={interiorColorHex}
                  />
                  
                  {/* Dark glazing beads for first vertical divider (rendered first, behind the divider) */}
                  {/* Left dark border of first divider */}
                  <rect
                    x={part1X + partWidth}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  {/* Right dark border of first divider */}
                  <rect
                    x={part1X + partWidth + dividerThickness - 2 * scale}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  
                  {/* First vertical divider (rendered on top) */}
                  <rect
                    x={part1X + partWidth}
                    y={glassY - panelOverlap}
                    width={dividerThickness}
                    height={glassHeight + panelOverlap}
                    fill={interiorColorHex}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                  
                  {/* Dark glazing beads for second vertical divider (rendered first, behind the divider) */}
                  {/* Left dark border of second divider */}
                  <rect
                    x={part2X + partWidth}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  {/* Right dark border of second divider */}
                  <rect
                    x={part2X + partWidth + dividerThickness - 2 * scale}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  
                  {/* Second vertical divider (rendered on top) */}
                  <rect
                    x={part2X + partWidth}
                    y={glassY - panelOverlap}
                    width={dividerThickness}
                    height={glassHeight + panelOverlap}
                    fill={interiorColorHex}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                  
                  {/* Middle section (part2) with horizontal dividers and glass texture */}
                  {(() => {
                    // Calculate horizontal glass sections with top/bottom frames and allowances
                    const middleSectionX = part2X;
                    const middleSectionWidth = partWidth;
                    const middleSectionY = glassY - panelOverlap;
                    const middleSectionHeight = glassHeight + panelOverlap;
                    const horizontalDividerThickness = dividerThickness;
                    const topBottomAllowance = 250 * scale; // Increased space at top and bottom to reveal more panel, not touching inner frame borders
                    
                    // For style-3: only top and bottom dividers, one glass/panel section
                    // For style-1: multiple dividers based on middleSectionParts
                    const isStyle3 = isVoordeur && doorStyle === 'style-3';
                    const totalDividerHeight = isStyle3 
                      ? horizontalDividerThickness * 2  // Only top and bottom frames
                      : horizontalDividerThickness * (2 + (middleSectionParts - 1)); // Top + bottom + middle dividers
                    
                    // Top frame comes down same distance as bottom went up
                    const topFrameOffset = topBottomAllowance; // Top frame moved down away from top border
                    const bottomFrameOffset = topBottomAllowance; // Bottom frame moved up away from bottom border
                    const availableHeight = middleSectionHeight - topFrameOffset - bottomFrameOffset - totalDividerHeight;
                    
                    // Calculate positions
                    const topAllowanceY = middleSectionY;
                    const topFrameY = topAllowanceY + topFrameOffset;
                    const glassPart1Y = topFrameY + horizontalDividerThickness;
                    
                    // For style-3: single glass/panel section, no middle dividers
                    // For style-1: multiple equal sections with middle dividers
                    let divider1Y, glassPart2Y, divider2Y, glassPart3Y, divider3Y, glassPart4Y, bottomFrameY;
                    const horizontalPartHeight = isStyle3 
                      ? availableHeight  // Single section takes all available height
                      : availableHeight / middleSectionParts; // Equal glass sections
                    
                    if (isStyle3) {
                      // Style-3: single section, no middle dividers
                      bottomFrameY = glassPart1Y + horizontalPartHeight;
                    } else {
                      // Style-1: multiple sections with middle dividers
                      divider1Y = glassPart1Y + horizontalPartHeight;
                      glassPart2Y = divider1Y + horizontalDividerThickness;
                      divider2Y = glassPart2Y + horizontalPartHeight;
                      glassPart3Y = divider2Y + horizontalDividerThickness;
                      divider3Y = middleSectionParts === 4 ? glassPart3Y + horizontalPartHeight : null;
                      glassPart4Y = middleSectionParts === 4 ? divider3Y + horizontalDividerThickness : null;
                      bottomFrameY = middleSectionParts === 4 
                        ? glassPart4Y + horizontalPartHeight 
                        : glassPart3Y + horizontalPartHeight;
                    }
                    
                    return (
                      <g>
                        {/* Top allowance - panel visible */}
                        <rect
                          x={middleSectionX}
                          y={topAllowanceY}
                          width={middleSectionWidth}
                          height={topBottomAllowance}
                          fill={interiorColorHex}
                        />
                        
                        {/* Top frame divider (divider 1) */}
                        {/* Dark glazing beads for top frame - top border */}
                        <rect
                          x={middleSectionX}
                          y={topFrameY - 2 * scale}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Dark glazing beads for top frame - bottom border */}
                        <rect
                          x={middleSectionX}
                          y={topFrameY + horizontalDividerThickness}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Top frame divider */}
                        <rect
                          x={middleSectionX}
                          y={topFrameY}
                          width={middleSectionWidth}
                          height={horizontalDividerThickness}
                          fill={interiorColorHex}
                          stroke={frameEdgeStroke}
                          strokeWidth="0.5"
                        />
                        
                        {/* Glass or Panel for style-3, or Glass for style-1 (part 1) */}
                        {isStyle3 ? (
                          // Style-3: Single glass/panel section
                          style3Type === 'panel' ? (
                            <rect
                              x={middleSectionX}
                              y={glassPart1Y}
                              width={middleSectionWidth}
                              height={horizontalPartHeight}
                              fill={interiorColorHex}
                            />
                          ) : (
                            <>
                              <rect
                                x={middleSectionX}
                                y={glassPart1Y}
                                width={middleSectionWidth}
                                height={horizontalPartHeight}
                                fill={glassFill}
                              />
                              {glassTexturePattern && (
                                <rect
                                  x={middleSectionX}
                                  y={glassPart1Y}
                                  width={middleSectionWidth}
                                  height={horizontalPartHeight}
                                  fill={glassTexturePattern}
                                  opacity="1"
                                />
                              )}
                            </>
                          )
                        ) : (
                          // Style-1: Glass texture for top horizontal section (part 1)
                          <rect
                            x={middleSectionX}
                            y={glassPart1Y}
                            width={middleSectionWidth}
                            height={horizontalPartHeight}
                            fill={glassFill}
                            style={{ fill: glassTexturePattern || glassFill }}
                          />
                        )}
                        
                        {/* Middle divider 1 (divider 2) - only for style-1 */}
                        {!isStyle3 && (
                          <>
                        {/* Dark glazing beads for middle divider 1 - top border */}
                        <rect
                          x={middleSectionX}
                          y={divider1Y - 2 * scale}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Dark glazing beads for middle divider 1 - bottom border */}
                        <rect
                          x={middleSectionX}
                          y={divider1Y + horizontalDividerThickness}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Middle divider 1 */}
                        <rect
                          x={middleSectionX}
                          y={divider1Y}
                          width={middleSectionWidth}
                          height={horizontalDividerThickness}
                          fill={interiorColorHex}
                          stroke={frameEdgeStroke}
                          strokeWidth="0.5"
                        />
                        
                        {/* Glass texture for middle horizontal section (part 2) - equal size */}
                        <rect
                          x={middleSectionX}
                          y={glassPart2Y}
                          width={middleSectionWidth}
                          height={horizontalPartHeight}
                          fill={glassFill}
                          style={{ fill: glassTexturePattern || glassFill }}
                        />
                        
                        {/* Middle divider 2 (divider 3) */}
                        {/* Dark glazing beads for middle divider 2 - top border */}
                        <rect
                          x={middleSectionX}
                          y={divider2Y - 2 * scale}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Dark glazing beads for middle divider 2 - bottom border */}
                        <rect
                          x={middleSectionX}
                          y={divider2Y + horizontalDividerThickness}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Middle divider 2 */}
                        <rect
                          x={middleSectionX}
                          y={divider2Y}
                          width={middleSectionWidth}
                          height={horizontalDividerThickness}
                          fill={interiorColorHex}
                          stroke={frameEdgeStroke}
                          strokeWidth="0.5"
                        />
                        
                        {/* Glass texture for bottom horizontal section (part 3) - equal size */}
                        <rect
                          x={middleSectionX}
                          y={glassPart3Y}
                          width={middleSectionWidth}
                          height={horizontalPartHeight}
                          fill={glassFill}
                          style={{ fill: glassTexturePattern || glassFill }}
                        />
                        
                        {/* Middle divider 3 (divider 4) - only for 4 parts */}
                        {middleSectionParts === 4 && divider3Y && (
                          <>
                            {/* Dark glazing beads for middle divider 3 - top border */}
                            <rect
                              x={middleSectionX}
                              y={divider3Y - 2 * scale}
                              width={middleSectionWidth}
                              height={2 * scale}
                              fill={glazingBeadFill}
                            />
                            {/* Dark glazing beads for middle divider 3 - bottom border */}
                            <rect
                              x={middleSectionX}
                              y={divider3Y + horizontalDividerThickness}
                              width={middleSectionWidth}
                              height={2 * scale}
                              fill={glazingBeadFill}
                            />
                            {/* Middle divider 3 */}
                            <rect
                              x={middleSectionX}
                              y={divider3Y}
                              width={middleSectionWidth}
                              height={horizontalDividerThickness}
                              fill={interiorColorHex}
                              stroke={frameEdgeStroke}
                              strokeWidth="0.5"
                            />
                            
                            {/* Glass texture for part 4 - equal size */}
                            {glassPart4Y && (
                              <rect
                                x={middleSectionX}
                                y={glassPart4Y}
                                width={middleSectionWidth}
                                height={horizontalPartHeight}
                                fill={glassFill}
                                style={{ fill: glassTexturePattern || glassFill }}
                              />
                            )}
                          </>
                        )}
                        </>
                        )}
                        
                        {/* Bottom frame divider */}
                        {/* Dark glazing beads for bottom frame - top border */}
                        <rect
                          x={middleSectionX}
                          y={bottomFrameY - 2 * scale}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Dark glazing beads for bottom frame - bottom border */}
                        <rect
                          x={middleSectionX}
                          y={bottomFrameY + horizontalDividerThickness}
                          width={middleSectionWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Bottom frame divider */}
                        <rect
                          x={middleSectionX}
                          y={bottomFrameY}
                          width={middleSectionWidth}
                          height={horizontalDividerThickness}
                          fill={interiorColorHex}
                          stroke={frameEdgeStroke}
                          strokeWidth="0.5"
                        />
                        
                        {/* Bottom allowance - panel visible */}
                        <rect
                          x={middleSectionX}
                          y={bottomFrameY + horizontalDividerThickness}
                          width={middleSectionWidth}
                          height={topBottomAllowance}
                          fill={interiorColorHex}
                        />
                        
                        {/* Hover overlay - visible when hovering (rendered on top) */}
                        {hoveredMiddleSection && (
                          <rect
                            x={middleSectionX}
                            y={topAllowanceY}
                            width={middleSectionWidth}
                            height={middleSectionHeight}
                            fill="rgba(255, 165, 0, 0.15)"
                            pointerEvents="none"
                          />
                        )}
                        
                        {/* Invisible overlay for mouse events - covers entire middle section (rendered last to be on top) */}
                        <rect
                          x={middleSectionX}
                          y={topAllowanceY}
                          width={middleSectionWidth}
                          height={middleSectionHeight}
                          fill="transparent"
                          onMouseEnter={() => setHoveredMiddleSection(true)}
                          onMouseLeave={() => setHoveredMiddleSection(false)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMiddleSection(true);
                            setMiddleSectionSettingsOpen(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </g>
                    );
                  })()}
                  
                  {/* Inner border around entire panel */}
                  <rect
                    x={leftGlassX}
                    y={glassY - panelOverlap}
                    width={leftGlassWidth + panelOverlap}
                    height={glassHeight + panelOverlap}
                    fill="none"
                    stroke={frameEdgeStroke}
                    strokeWidth="1"
                    pointerEvents="none"
                  />
                </>
              );
            })() : isVoordeur && doorStyle === 'style-4' ? (() => {
              // Calculate divider position - 1 vertical divider shifted 25% to the left
              // Use innerFrameThickness to match the inner frame border width
              // Expand panel to cover dark glazing borders (overlap by 2 * scale on top and right)
              const dividerThickness = innerFrameThickness;
              const panelOverlap = 2 * scale; // Overlap to cover dark glazing borders
              // The narrow (25%) section holds the selectable glass/panel; the wide
              // (75%) section is the solid door panel. When the handle is set to
              // "Inside Right", mirror the layout so the narrow section sits on the right.
              const isHandleRight = handlePosition === 'right';
              const part1Width = leftGlassWidth * 0.25;
              const part2Width = leftGlassWidth * 0.75 - dividerThickness;
              const part1X = isHandleRight
                ? leftGlassX + leftGlassWidth - part1Width // narrow section on the right
                : leftGlassX; // narrow section on the left (default)
              const dividerX = isHandleRight
                ? part1X - dividerThickness // divider sits just left of the narrow section
                : part1X + part1Width; // divider sits just right of the narrow section
              const part2X = isHandleRight
                ? leftGlassX
                : part1X + part1Width + dividerThickness;
              
              return (
                <>
                  {/* Panel fill - same color as inner frame, expanded to cover dark glazing borders */}
                  <rect
                    x={leftGlassX}
                    y={glassY - panelOverlap}
                    width={leftGlassWidth + panelOverlap}
                    height={glassHeight + panelOverlap}
                    fill={interiorColorHex}
                  />
                  
                  {/* Glass or Panel in the left section (between first frame and vertical divider) */}
                  <g>
                    {/* Glass or Panel based on selection */}
                    {style4LeftType === 'panel' ? (
                      <rect
                        x={part1X}
                        y={glassY - panelOverlap}
                        width={part1Width}
                        height={glassHeight + panelOverlap}
                        fill={interiorColorHex}
                      />
                    ) : (
                      <>
                        <rect
                          x={part1X}
                          y={glassY - panelOverlap}
                          width={part1Width}
                          height={glassHeight + panelOverlap}
                          fill={glassFill}
                        />
                        {/* Glass texture overlay */}
                        {glassTexturePattern && (
                          <rect
                            x={part1X}
                            y={glassY - panelOverlap}
                            width={part1Width}
                            height={glassHeight + panelOverlap}
                            fill={glassTexturePattern}
                            opacity="1"
                          />
                        )}
                      </>
                    )}
                    
                    {/* Hover overlay - rendered on top */}
                    {hoveredStyle4Left && (
                      <rect
                        x={part1X}
                        y={glassY - panelOverlap}
                        width={part1Width}
                        height={glassHeight + panelOverlap}
                        fill="rgba(255, 165, 0, 0.15)"
                        pointerEvents="none"
                      />
                    )}
                    
                    {/* Invisible overlay for mouse events - rendered last to be on top */}
                    <rect
                      x={part1X}
                      y={glassY - panelOverlap}
                      width={part1Width}
                      height={glassHeight + panelOverlap}
                      fill="transparent"
                      onMouseEnter={() => setHoveredStyle4Left(true)}
                      onMouseLeave={() => setHoveredStyle4Left(false)}
                      onClick={() => {
                        setSelectedStyle4Left(true);
                        setStyle4LeftSettingsOpen(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </g>
                  
                  {/* Dark glazing beads for vertical divider (rendered first, behind the divider) */}
                  {/* Left dark border of divider */}
                  <rect
                    x={dividerX}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  {/* Right dark border of divider */}
                  <rect
                    x={dividerX + dividerThickness - 2 * scale}
                    y={glassY - panelOverlap}
                    width={2 * scale}
                    height={glassHeight + panelOverlap}
                    fill={glazingBeadFill}
                  />
                  
                  {/* Vertical divider (rendered on top) */}
                  <rect
                    x={dividerX}
                    y={glassY - panelOverlap}
                    width={dividerThickness}
                    height={glassHeight + panelOverlap}
                    fill={interiorColorHex}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                  
                  {/* Inner border around entire panel */}
                  <rect
                    x={leftGlassX}
                    y={glassY - panelOverlap}
                    width={leftGlassWidth + panelOverlap}
                    height={glassHeight + panelOverlap}
                    fill="none"
                    stroke={frameEdgeStroke}
                    strokeWidth="1"
                    pointerEvents="none"
                  />
                </>
              );
            })() : isVoordeur && doorStyle === 'style-5' ? (() => {
              // Style-5: Horizontal divider in the middle, glass on top (with vertical divider), panel on bottom (with horizontal lines)
              const dividerThickness = innerFrameThickness;
              const panelOverlap = 2 * scale;
              const horizontalDividerY = glassY + (glassHeight / 2) - (dividerThickness / 2);
              const topPartHeight = glassHeight / 2;
              const bottomPartHeight = glassHeight / 2;
              
              // Vertical divider in the middle of the glass part (top part)
              const verticalDividerX = leftGlassX + (leftGlassWidth / 2) - (dividerThickness / 2);
              const topPartLeftWidth = leftGlassWidth / 2;
              // Right part extends to touch the right inner frame border
              // Calculate right edge: leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness (right border)
              const rightInnerFrameEdge = leftInnerFrameX + leftInnerFrameWidth - innerFrameThickness;
              const topPartRightWidth = rightInnerFrameEdge - innerGlassInset - (verticalDividerX + dividerThickness);
              
              // Horizontal lines on bottom panel (7-8 lines)
              const numberOfLines = 8;
              const lineSpacing = bottomPartHeight / (numberOfLines + 1);
              
              return (
                <>
                  {/* Top part - Glass (left side) */}
                  <rect
                    x={leftGlassX}
                    y={glassY}
                    width={topPartLeftWidth}
                    height={topPartHeight}
                    fill={glassFill}
                  />
                  {/* Glass texture overlay for top left part */}
                  {glassTexturePattern && (
                    <rect
                      x={leftGlassX}
                      y={glassY}
                      width={topPartLeftWidth}
                      height={topPartHeight}
                      fill={glassTexturePattern}
                      opacity="1"
                    />
                  )}
                  
                  {/* Dark glazing beads for vertical divider in glass part */}
                  {/* Left dark border of vertical divider */}
                  <rect
                    x={verticalDividerX - 2 * scale}
                    y={glassY}
                    width={2 * scale}
                    height={topPartHeight}
                    fill={glazingBeadFill}
                  />
                  {/* Right dark border of vertical divider */}
                  <rect
                    x={verticalDividerX + dividerThickness}
                    y={glassY}
                    width={2 * scale}
                    height={topPartHeight}
                    fill={glazingBeadFill}
                  />
                  
                  {/* Vertical divider in glass part */}
                  <rect
                    x={verticalDividerX}
                    y={glassY}
                    width={dividerThickness}
                    height={topPartHeight}
                    fill={interiorColorHex}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                  
                  {/* Top part - Glass (right side) - extends to touch right inner frame border */}
                  <rect
                    x={verticalDividerX + dividerThickness}
                    y={glassY}
                    width={topPartRightWidth}
                    height={topPartHeight}
                    fill={glassFill}
                  />
                  {/* Glass texture overlay for top right part */}
                  {glassTexturePattern && (
                    <rect
                      x={verticalDividerX + dividerThickness}
                      y={glassY}
                      width={topPartRightWidth}
                      height={topPartHeight}
                      fill={glassTexturePattern}
                      opacity="1"
                    />
                  )}
                  
                  {/* Dark glazing beads for horizontal divider */}
                  {/* Top dark border of horizontal divider */}
                  <rect
                    x={leftGlassX}
                    y={horizontalDividerY - 2 * scale}
                    width={leftGlassWidth}
                    height={2 * scale}
                    fill={glazingBeadFill}
                  />
                  {/* Bottom dark border of horizontal divider */}
                  <rect
                    x={leftGlassX}
                    y={horizontalDividerY + dividerThickness}
                    width={leftGlassWidth}
                    height={2 * scale}
                    fill={glazingBeadFill}
                  />
                  
                  {/* Horizontal divider */}
                  <rect
                    x={leftGlassX}
                    y={horizontalDividerY}
                    width={leftGlassWidth}
                    height={dividerThickness}
                    fill={interiorColorHex}
                    stroke={frameEdgeStroke}
                    strokeWidth="0.5"
                  />
                  
                  {/* Bottom part - Panel with horizontal lines */}
                  <rect
                    x={leftGlassX}
                    y={horizontalDividerY + dividerThickness}
                    width={leftGlassWidth}
                    height={bottomPartHeight}
                    fill={interiorColorHex}
                  />
                  
                  {/* Horizontal lines on bottom panel */}
                  {Array.from({ length: numberOfLines }, (_, i) => {
                    const lineY = horizontalDividerY + dividerThickness + (lineSpacing * (i + 1));
                    return (
                      <line
                        key={i}
                        x1={leftGlassX}
                        y1={lineY}
                        x2={leftGlassX + leftGlassWidth}
                        y2={lineY}
                        stroke={frameEdgeStroke}
                        strokeWidth="0.5"
                      />
                    );
                  })}
                  
                  {/* Inner border around entire area */}
                  <rect
                    x={leftGlassX}
                    y={glassY}
                    width={leftGlassWidth}
                    height={glassHeight}
                    fill="none"
                    stroke={frameEdgeStroke}
                    strokeWidth="1"
                    pointerEvents="none"
                  />
                </>
              );
            })() : isVoordeur && doorStyle === 'style-6' ? (() => {
              // Style-6: Horizontal dividers dividing inner frame into parts (3 or 4 parts)
              const dividerThickness = innerFrameThickness;
              const numberOfDividers = style6Parts - 1; // 3 parts = 2 dividers, 4 parts = 3 dividers
              const sectionHeight = glassHeight / style6Parts;
              
              return (
                <>
                  {/* Glass fill for entire area */}
                  <rect
                    x={leftGlassX}
                    y={glassY}
                    width={leftGlassWidth}
                    height={glassHeight}
                    fill={glassFill}
                  />
                  {/* Glass texture overlay */}
                  {glassTexturePattern && (
                    <rect
                      x={leftGlassX}
                      y={glassY}
                      width={leftGlassWidth}
                      height={glassHeight}
                      fill={glassTexturePattern}
                      opacity="1"
                    />
                  )}
                  
                  {/* Horizontal dividers */}
                  {Array.from({ length: numberOfDividers }, (_, i) => {
                    const dividerIndex = i + 1; // 1, 2, 3, or 4
                    const dividerY = glassY + (sectionHeight * dividerIndex) - (dividerThickness / 2);
                    
                    return (
                      <g key={i}>
                        {/* Dark glazing beads for horizontal divider */}
                        {/* Top dark border of divider */}
                        <rect
                          x={leftGlassX}
                          y={dividerY - 2 * scale}
                          width={leftGlassWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        {/* Bottom dark border of divider */}
                        <rect
                          x={leftGlassX}
                          y={dividerY + dividerThickness}
                          width={leftGlassWidth}
                          height={2 * scale}
                          fill={glazingBeadFill}
                        />
                        
                        {/* Horizontal divider */}
                        <rect
                          x={leftGlassX}
                          y={dividerY}
                          width={leftGlassWidth}
                          height={dividerThickness}
                          fill={interiorColorHex}
                          stroke={frameEdgeStroke}
                          strokeWidth="0.5"
                        />
                      </g>
                    );
                  })}
                  
                  {/* Hover and click area for dialog */}
                  <g
                    onMouseEnter={() => setHoveredStyle6(true)}
                    onMouseLeave={() => setHoveredStyle6(false)}
                    onClick={() => {
                      setSelectedStyle6(true);
                      setStyle6SettingsOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Hover overlay - rendered on top */}
                    {hoveredStyle6 && (
                      <rect
                        x={leftGlassX}
                        y={glassY}
                        width={leftGlassWidth}
                        height={glassHeight}
                        fill="rgba(255, 165, 0, 0.15)"
                        pointerEvents="none"
                      />
                    )}
                    
                    {/* Invisible overlay for mouse events - rendered last to be on top */}
                    <rect
                      x={leftGlassX}
                      y={glassY}
                      width={leftGlassWidth}
                      height={glassHeight}
                      fill="transparent"
                    />
                  </g>
                  
                  {/* Inner border around entire area */}
                  <rect
                    x={leftGlassX}
                    y={glassY}
                    width={leftGlassWidth}
                    height={glassHeight}
                    fill="none"
                    stroke={frameEdgeStroke}
                    strokeWidth="1"
                    pointerEvents="none"
                  />
                </>
              );
            })() : supportsInnerFrameGlassPanel ? (
              <g>
                {renderInnerFrameGlassPanelFill(
                  leftGlassX,
                  glassY,
                  leftGlassWidth,
                  innerFrameInteractiveHeight
                )}
                {renderInnerFrameInteractionLayer(
                  leftGlassX,
                  glassY,
                  leftGlassWidth,
                  innerFrameInteractiveHeight
                )}
              </g>
            ) : (
              <>
                {/* Glass base with gradient */}
                <rect
                  x={leftGlassX}
                  y={glassY}
                  width={leftGlassWidth}
                  height={glassHeight}
                  fill={glassFill}
                />
                {/* Glass texture overlay */}
                {glassTexturePattern && (
                  <rect
                    x={leftGlassX}
                    y={glassY}
                    width={leftGlassWidth}
                    height={glassHeight}
                    fill={glassTexturePattern}
                    opacity="1"
                  />
                )}
                {/* Inner border */}
                <rect
                  x={leftGlassX}
                  y={glassY}
                  width={leftGlassWidth}
                  height={glassHeight}
                  fill="none"
                  stroke={frameEdgeStroke}
                  strokeWidth="1"
                  pointerEvents="none"
                />
              </>
            )}
            {/* Parapet panel (bottom solid section) - for single pane */}
            {hasParapet && parapetTopY < glassY + glassHeight && (
              <>
                {/* Frame/border at top of parapet - same design as inner frame */}
                {parapetTopY > glassY && (
                  <g>
                    {/* Outer frame (parapet color) with borderline */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY - innerFrameThickness}
                      width={leftGlassWidth}
                      height={innerFrameThickness}
                      fill={parapetColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={leftGlassX + innerFrameThickness / 2}
                      y={parapetTopY - innerFrameThickness + innerFrameThickness / 2}
                      width={leftGlassWidth - innerFrameThickness}
                      height={innerFrameThickness / 2}
                      fill={parapetColorHex}
                    />
                  </g>
                )}
                {/* Panel fill - based on panelType */}
                {panelType === 'glass' ? (
                  <>
                    {/* Glass texture for parapet */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY}
                      width={leftGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={leftGlassX}
                        y={parapetTopY}
                        width={leftGlassWidth}
                        height={glassY + glassHeight - parapetTopY}
                        fill={glassTexturePattern}
                        opacity="1"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Panel gradient / solid color for sandwich/horizontal/vertical mullions */}
                    <rect
                      x={leftGlassX}
                      y={parapetTopY}
                      width={leftGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={parapetColorHex}
                    />
                    {/* Horizontal mullions - matching Kozijnen panel lines style */}
                    {panelType === 'horizontal-mullions' && (() => {
                      const parapetHeight = glassY + glassHeight - parapetTopY;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetHeight / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineY = parapetTopY + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`h-mullion-${i}`}
                                x1={leftGlassX}
                                y1={lineY}
                                x2={leftGlassX + leftGlassWidth}
                                y2={lineY}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                    {/* Vertical mullions - matching Kozijnen panel lines style */}
                    {panelType === 'vertical-mullions' && (() => {
                      const parapetWidth = leftGlassWidth;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetWidth / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineX = leftGlassX + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`v-mullion-${i}`}
                                x1={lineX}
                                y1={parapetTopY}
                                x2={lineX}
                                y2={parapetTopY + (glassY + glassHeight - parapetTopY)}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Dark border around panel */}
                <rect
                  x={leftGlassX}
                  y={parapetTopY}
                  width={leftGlassWidth}
                  height={glassY + glassHeight - parapetTopY}
                  fill="none"
                  stroke="#4B4B4B"
                  strokeWidth="1"
                />
              </>
            )}
            {/* Turn lines for single pane - based on handle position (not for voordeur) */}
            {!isVoordeur && handlePosition === 'left' ? (
              // Left handle: turn-left pattern (< pointing left)
              <g>
                {/* Line from top-right to middle-left */}
                <line
                  x1={leftGlassX + leftGlassWidth}
                  y1={glassY}
                  x2={leftGlassX}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
                {/* Line from bottom-right to middle-left */}
                <line
                  x1={leftGlassX + leftGlassWidth}
                  y1={glassY + glassHeight}
                  x2={leftGlassX}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
              </g>
            ) : !isVoordeur ? (
              // Right handle: turn-right pattern (> pointing right)
              <g>
                {/* Line from top-left to middle-right */}
                <line
                  x1={leftGlassX}
                  y1={glassY}
                  x2={leftGlassX + leftGlassWidth}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
                {/* Line from bottom-left to middle-right */}
                <line
                  x1={leftGlassX}
                  y1={glassY + glassHeight}
                  x2={leftGlassX + leftGlassWidth}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
              </g>
            ) : null}
          </g>
        )}

        {/* Right Pane Glass */}
        {isDoublePane && (
          <g>
            {supportsInnerFrameGlassPanel ? (
              <>
                {renderInnerFrameGlassPanelFill(
                  rightGlassX,
                  glassY,
                  rightGlassWidth,
                  innerFrameInteractiveHeight
                )}
                {renderInnerFrameInteractionLayer(
                  rightGlassX,
                  glassY,
                  rightGlassWidth,
                  innerFrameInteractiveHeight
                )}
              </>
            ) : (
              <>
            {/* Glass base with gradient */}
            <rect
              x={rightGlassX}
              y={glassY}
              width={rightGlassWidth}
              height={glassHeight}
              fill={glassFill}
              style={{ 
                cursor: doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right' || slidingPosition === 'center') ? 'pointer' : 'default',
                pointerEvents: 'auto'
              }}
              onMouseEnter={() => {
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right')) {
                  setHoveredInnerFrame('right');
                } else if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter('left');
                }
              }}
              onMouseLeave={() => {
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right')) {
                  setHoveredInnerFrame(null);
                } else if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter(null);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && selectedInnerFrame === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrame('right');
                  }, 0);
                } else if (doorType === 'sliding' && slidingPosition === 'center' && selectedInnerFrameCenter === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrameCenter('left');
                  }, 0);
                }
              }}
            />
            {/* Glass texture overlay */}
            {(() => {
              // For center section: leftGlassTexture applies to first 2 frames (left + right)
              // For left/right sections: rightGlassTexture applies to right frame only
              let textureToUse = glassTexturePattern;
              if (doorType === 'sliding') {
                if (slidingPosition === 'center') {
                  // Center section: use leftGlassTexture for first 2 frames (right is part of first 2)
                  textureToUse = leftInnerGlassTexturePattern || glassTexturePattern;
                } else {
                  // Left/right sections: use rightGlassTexture for right frame
                  textureToUse = rightInnerGlassTexturePattern || glassTexturePattern;
                }
              }
              return textureToUse;
            })() && (
              <rect
                x={rightGlassX}
                y={glassY}
                width={rightGlassWidth}
                height={glassHeight}
                fill={(() => {
                  if (doorType === 'sliding') {
                    if (slidingPosition === 'center') {
                      return leftInnerGlassTexturePattern || glassTexturePattern;
                    } else {
                      return rightInnerGlassTexturePattern || glassTexturePattern;
                    }
                  }
                  return glassTexturePattern;
                })()}
                opacity="0.8"
                pointerEvents="none"
              />
            )}
            {/* Inner border — omit left edge when right sash has no left stile */}
            {rightSashNoLeftStile ? (
              <g pointerEvents="none">
                <line
                  x1={rightGlassX}
                  y1={glassY}
                  x2={rightGlassX + rightGlassWidth}
                  y2={glassY}
                  stroke={frameEdgeStroke}
                  strokeWidth="1"
                />
                <line
                  x1={rightGlassX + rightGlassWidth}
                  y1={glassY}
                  x2={rightGlassX + rightGlassWidth}
                  y2={glassY + glassHeight}
                  stroke={frameEdgeStroke}
                  strokeWidth="1"
                />
                <line
                  x1={rightGlassX}
                  y1={glassY + glassHeight}
                  x2={rightGlassX + rightGlassWidth}
                  y2={glassY + glassHeight}
                  stroke={frameEdgeStroke}
                  strokeWidth="1"
                />
              </g>
            ) : (
            <rect
              x={rightGlassX}
              y={glassY}
              width={rightGlassWidth}
              height={glassHeight}
              fill="none"
              stroke={frameEdgeStroke}
              strokeWidth="1"
              pointerEvents="none"
            />
            )}
              </>
            )}
            {/* Ventilator for right frame - sliding doors left/right sections */}
            {doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && ventilatorSlidingRight && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = rightGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, rightGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-right-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Ventilator for right frame - sliding doors center section */}
            {doorType === 'sliding' && slidingPosition === 'center' && ventilatorSlidingCenterRight && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = rightGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, rightGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-center-right-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Parapet panel (bottom solid section) */}
            {hasParapet && parapetTopY < glassY + glassHeight && (
              <>
                {/* Frame/border at top of parapet - same design as inner frame */}
                {parapetTopY > glassY && (
                  <g>
                    {/* Outer frame (parapet color) with borderline */}
                    <rect
                      x={rightGlassX}
                      y={parapetTopY - innerFrameThickness}
                      width={rightGlassWidth}
                      height={innerFrameThickness}
                      fill={parapetColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={rightGlassX + innerFrameThickness / 2}
                      y={parapetTopY - innerFrameThickness + innerFrameThickness / 2}
                      width={rightGlassWidth - innerFrameThickness}
                      height={innerFrameThickness / 2}
                      fill={parapetColorHex}
                    />
                  </g>
                )}
                {/* Panel fill - based on panelType */}
                {panelType === 'glass' ? (
                  <>
                    {/* Glass texture for parapet */}
                    <rect
                      x={rightGlassX}
                      y={parapetTopY}
                      width={rightGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={rightGlassX}
                        y={parapetTopY}
                        width={rightGlassWidth}
                        height={glassY + glassHeight - parapetTopY}
                        fill={glassTexturePattern}
                        opacity="1"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Panel gradient / solid color for sandwich/horizontal/vertical mullions */}
                    <rect
                      x={rightGlassX}
                      y={parapetTopY}
                      width={rightGlassWidth}
                      height={glassY + glassHeight - parapetTopY}
                      fill={parapetColorHex}
                    />
                    {/* Horizontal mullions - matching Kozijnen panel lines style */}
                    {panelType === 'horizontal-mullions' && (() => {
                      const parapetHeight = glassY + glassHeight - parapetTopY;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetHeight / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineY = parapetTopY + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`h-mullion-right-${i}`}
                                x1={rightGlassX}
                                y1={lineY}
                                x2={rightGlassX + rightGlassWidth}
                                y2={lineY}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                    {/* Vertical mullions - matching Kozijnen panel lines style */}
                    {panelType === 'vertical-mullions' && (() => {
                      const parapetWidth = rightGlassWidth;
                      const lineColor = '#D0D4DA'; // Same color as Kozijnen style-5 lines
                      const lineWidth = 0.5; // Same stroke width as Kozijnen style-5
                      const numberOfLines = 8; // Same number of lines as Kozijnen style-5
                      const lineSpacing = parapetWidth / (numberOfLines + 1); // Evenly distributed spacing
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineX = rightGlassX + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`v-mullion-right-${i}`}
                                x1={lineX}
                                y1={parapetTopY}
                                x2={lineX}
                                y2={parapetTopY + (glassY + glassHeight - parapetTopY)}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Dark border around panel */}
                <rect
                  x={rightGlassX}
                  y={parapetTopY}
                  width={rightGlassWidth}
                  height={glassY + glassHeight - parapetTopY}
                  fill="none"
                  stroke="#4B4B4B"
                  strokeWidth="1"
                />
              </>
            )}
            {/* Turn lines for right pane - removed for sliding doors */}
            {doorType !== 'sliding' && (
              <g>
                {/* Line from top-right to middle-left */}
                <line
                  x1={rightGlassX + rightGlassWidth}
                  y1={glassY}
                  x2={rightGlassX}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
                {/* Line from bottom-right to middle-left */}
                <line
                  x1={rightGlassX + rightGlassWidth}
                  y1={glassY + glassHeight}
                  x2={rightGlassX}
                  y2={glassY + glassHeight / 2}
                  stroke="#999999"
                  strokeWidth="3"
                  strokeDasharray="3"
                  pointerEvents="none"
                />
              </g>
            )}
            {/* Hover highlight overlay for right frame */}
            {doorType === 'sliding' && (slidingPosition === 'left' || slidingPosition === 'right') && hoveredInnerFrame === 'right' && (
              <rect
                x={rightGlassX}
                y={glassY}
                width={rightGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            {/* Hover highlight overlay for left section (right frame) when center */}
            {doorType === 'sliding' && slidingPosition === 'center' && hoveredInnerFrameCenter === 'left' && (
              <rect
                x={rightGlassX}
                y={glassY}
                width={rightGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            {/* Left-pointing arrow for right frame (sliding door) - only when innerFrameSlidingSection === 'left' */}
            {/* For center section: show when innerFrameSlidingSectionLeft === 'left' (default) */}
            {doorType === 'sliding' && isDoublePane && 
             !((slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') && 
             !(doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right') && (
              <g pointerEvents="none">
                {/* Horizontal dashed line (shaft) - longer with more dashes */}
                <line
                  x1={rightGlassX + rightGlassWidth / 2 + 80 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={rightGlassX + rightGlassWidth / 2 - 15 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  strokeLinecap="round"
                />
                {/* Vertical line at right end */}
                <line
                  x1={rightGlassX + rightGlassWidth / 2 + 80 * scale}
                  y1={glassY + glassHeight / 2 - 15 * scale}
                  x2={rightGlassX + rightGlassWidth / 2 + 80 * scale}
                  y2={glassY + glassHeight / 2 + 15 * scale}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {/* Arrowhead (pointing left) - tip at left end, V shape opening to the right */}
                <path
                  d={`M ${rightGlassX + rightGlassWidth / 2 - 35 * scale} ${glassY + glassHeight / 2} L ${rightGlassX + rightGlassWidth / 2 - 15 * scale} ${glassY + glassHeight / 2 - 12 * scale} M ${rightGlassX + rightGlassWidth / 2 - 35 * scale} ${glassY + glassHeight / 2} L ${rightGlassX + rightGlassWidth / 2 - 15 * scale} ${glassY + glassHeight / 2 + 12 * scale}`}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            )}
            {/* Cross symbol for right frame when innerFrameSlidingSection === 'right' */}
            {/* For center section: show when innerFrameSlidingSectionLeft === 'right' */}
            {doorType === 'sliding' && isDoublePane && 
             (((slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right') ||
              (slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right')) && (
              <g pointerEvents="none">
                {/* Horizontal line of cross */}
                <line
                  x1={rightGlassX + rightGlassWidth / 2 - 30 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={rightGlassX + rightGlassWidth / 2 + 30 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Vertical line of cross */}
                <line
                  x1={rightGlassX + rightGlassWidth / 2}
                  y1={glassY + glassHeight / 2 - 30 * scale}
                  x2={rightGlassX + rightGlassWidth / 2}
                  y2={glassY + glassHeight / 2 + 30 * scale}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </g>
            )}
          </g>
        )}

        {/* Center Pane Glass (for slidingPosition === 'center') */}
        {needsCenterFrame && centerGlassX !== null && centerGlassWidth !== null && (
          <g>
            {/* Glass base with gradient */}
            <rect
              x={centerGlassX}
              y={glassY}
              width={centerGlassWidth}
              height={glassHeight}
              fill={glassFill}
              style={{ 
                cursor: doorType === 'sliding' && slidingPosition === 'center' ? 'pointer' : 'default',
                pointerEvents: 'auto'
              }}
              onMouseEnter={() => {
                if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter('right');
                }
              }}
              onMouseLeave={() => {
                if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter(null);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (doorType === 'sliding' && slidingPosition === 'center' && selectedInnerFrameCenter === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrameCenter('right');
                  }, 0);
                }
              }}
            />
            {/* Glass texture overlay */}
            {(() => {
              // For center section: rightGlassTexture applies to second 2 frames (center + third)
              let textureToUse = glassTexturePattern;
              if (doorType === 'sliding' && slidingPosition === 'center') {
                textureToUse = rightInnerGlassTexturePattern || glassTexturePattern;
              }
              return textureToUse;
            })() && (
              <rect
                x={centerGlassX}
                y={glassY}
                width={centerGlassWidth}
                height={glassHeight}
                fill={(() => {
                  if (doorType === 'sliding' && slidingPosition === 'center') {
                    return rightInnerGlassTexturePattern || glassTexturePattern;
                  }
                  return glassTexturePattern;
                })()}
                opacity="0.8"
                pointerEvents="none"
              />
            )}
            {/* Inner border */}
            <rect
              x={centerGlassX}
              y={glassY}
              width={centerGlassWidth}
              height={glassHeight}
              fill="none"
              stroke={frameEdgeStroke}
              strokeWidth="1"
              pointerEvents="none"
            />
            {/* Ventilator for center frame - sliding doors center section */}
            {doorType === 'sliding' && slidingPosition === 'center' && ventilatorSlidingCenterCenter && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = centerGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, centerGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-center-center-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Right-pointing arrow for center frame (sliding door) - dashed line with arrowhead */}
            {/* Show when innerFrameSlidingSectionRight === 'right' (default), hide when 'left' */}
            {doorType === 'sliding' && isDoublePane && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'right' && (
              <g pointerEvents="none">
                {/* Horizontal dashed line (shaft) - longer with more dashes */}
                <line
                  x1={centerGlassX + centerGlassWidth / 2 - 80 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={centerGlassX + centerGlassWidth / 2 + 15 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  strokeLinecap="round"
                />
                {/* Vertical line at left end */}
                <line
                  x1={centerGlassX + centerGlassWidth / 2 - 80 * scale}
                  y1={glassY + glassHeight / 2 - 15 * scale}
                  x2={centerGlassX + centerGlassWidth / 2 - 80 * scale}
                  y2={glassY + glassHeight / 2 + 15 * scale}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {/* Arrowhead (pointing right) - tip at right end, V shape opening to the left */}
                <path
                  d={`M ${centerGlassX + centerGlassWidth / 2 + 35 * scale} ${glassY + glassHeight / 2} L ${centerGlassX + centerGlassWidth / 2 + 15 * scale} ${glassY + glassHeight / 2 - 12 * scale} M ${centerGlassX + centerGlassWidth / 2 + 35 * scale} ${glassY + glassHeight / 2} L ${centerGlassX + centerGlassWidth / 2 + 15 * scale} ${glassY + glassHeight / 2 + 12 * scale}`}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            )}
            {/* Cross symbol on center frame - show when innerFrameSlidingSectionRight === 'left' */}
            {doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left' && (
              <g pointerEvents="none">
                {/* Horizontal line of cross */}
                <line
                  x1={centerGlassX + centerGlassWidth / 2 - 30 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={centerGlassX + centerGlassWidth / 2 + 30 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Vertical line of cross */}
                <line
                  x1={centerGlassX + centerGlassWidth / 2}
                  y1={glassY + glassHeight / 2 - 30 * scale}
                  x2={centerGlassX + centerGlassWidth / 2}
                  y2={glassY + glassHeight / 2 + 30 * scale}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </g>
            )}
            {/* Hover highlight overlay for right section (center frame) when center */}
            {doorType === 'sliding' && slidingPosition === 'center' && hoveredInnerFrameCenter === 'right' && (
              <rect
                x={centerGlassX}
                y={glassY}
                width={centerGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
          </g>
        )}

        {/* Third Pane Glass (for slidingPosition === 'right' or 'center') */}
        {(needsThirdFrame || needsCenterFrame) && thirdGlassX !== null && thirdGlassWidth !== null && (
          <g>
            {/* Glass base with gradient */}
            <rect
              x={thirdGlassX}
              y={glassY}
              width={thirdGlassWidth}
              height={glassHeight}
              fill={glassFill}
              style={{ 
                cursor: doorType === 'sliding' && slidingPosition === 'center' ? 'pointer' : 'default',
                pointerEvents: 'auto'
              }}
              onMouseEnter={() => {
                if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter('right');
                }
              }}
              onMouseLeave={() => {
                if (doorType === 'sliding' && slidingPosition === 'center') {
                  setHoveredInnerFrameCenter(null);
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (doorType === 'sliding' && slidingPosition === 'center' && selectedInnerFrameCenter === null) {
                  // Use setTimeout to defer state update and prevent immediate re-render
                  setTimeout(() => {
                    setSelectedInnerFrameCenter('right');
                  }, 0);
                }
              }}
            />
            {/* Glass texture overlay */}
            {(() => {
              // For center section: rightGlassTexture applies to second 2 frames (center + third)
              let textureToUse = glassTexturePattern;
              if (doorType === 'sliding' && slidingPosition === 'center') {
                textureToUse = rightInnerGlassTexturePattern || glassTexturePattern;
              }
              return textureToUse;
            })() && (
              <rect
                x={thirdGlassX}
                y={glassY}
                width={thirdGlassWidth}
                height={glassHeight}
                fill={(() => {
                  if (doorType === 'sliding' && slidingPosition === 'center') {
                    return rightInnerGlassTexturePattern || glassTexturePattern;
                  }
                  return glassTexturePattern;
                })()}
                opacity="0.8"
                pointerEvents="none"
              />
            )}
            {/* Inner border */}
            <rect
              x={thirdGlassX}
              y={glassY}
              width={thirdGlassWidth}
              height={glassHeight}
              fill="none"
              stroke={frameEdgeStroke}
              strokeWidth="1"
              pointerEvents="none"
            />
            {/* Ventilator for third frame - sliding doors center section */}
            {doorType === 'sliding' && slidingPosition === 'center' && ventilatorSlidingCenterThird && (
              <g pointerEvents="none">
                {(() => {
                  const ventilatorMarginX = 0;
                  const ventilatorHeight = innerFrameThickness * 0.8;
                  const ventilatorX = thirdGlassX + ventilatorMarginX;
                  const ventilatorY = glassY;
                  const ventilatorWidth = Math.max(0, thirdGlassWidth - ventilatorMarginX * 2);
                  const slatCount = 13;
                  const slatGap = (ventilatorWidth - 6 * scale) / (slatCount - 1 || 1);
                  const slats = [];
                  for (let i = 0; i < slatCount; i += 1) {
                    const x = ventilatorX + 3 * scale + i * slatGap;
                    slats.push(
                      <line
                        key={`sliding-center-third-vent-slat-${i}`}
                        x1={x}
                        y1={ventilatorY + 2 * scale}
                        x2={x}
                        y2={ventilatorY + ventilatorHeight - 2 * scale}
                        stroke="#000000"
                        strokeWidth={1.4 * scale}
                        strokeLinecap="round"
                      />
                    );
                  }
                  return (
                    <>
                      {/* Ventilator body */}
                      <rect
                        x={ventilatorX}
                        y={ventilatorY}
                        width={ventilatorWidth}
                        height={ventilatorHeight}
                        fill="#D5DADF"
                        stroke="#8A929A"
                        strokeWidth={1 * scale}
                        rx={2 * scale}
                        ry={2 * scale}
                      />
                      {/* Dark bottom outline under ventilator */}
                      <line
                        x1={ventilatorX}
                        y1={ventilatorY + ventilatorHeight}
                        x2={ventilatorX + ventilatorWidth}
                        y2={ventilatorY + ventilatorHeight}
                        stroke="#000000"
                        strokeWidth={1.6 * scale}
                      />
                      {/* Slats */}
                      {slats}
                    </>
                  );
                })()}
              </g>
            )}
            {/* Cross symbol on third frame - show when slidingPosition === 'right' or (center and innerFrameSlidingSectionRight === 'right') */}
            {doorType === 'sliding' && (
              (slidingPosition === 'right') || 
              (slidingPosition === 'center' && innerFrameSlidingSectionRight === 'right')
            ) && (
              <g pointerEvents="none">
                {/* Horizontal line of cross */}
                <line
                  x1={thirdGlassX + thirdGlassWidth / 2 - 30 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={thirdGlassX + thirdGlassWidth / 2 + 30 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                {/* Vertical line of cross */}
                <line
                  x1={thirdGlassX + thirdGlassWidth / 2}
                  y1={glassY + glassHeight / 2 - 30 * scale}
                  x2={thirdGlassX + thirdGlassWidth / 2}
                  y2={glassY + glassHeight / 2 + 30 * scale}
                  stroke="#666666"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </g>
            )}
            {/* Left-pointing arrow for third frame (sliding door) - only when innerFrameSlidingSectionRight === 'left' */}
            {doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left' && (
              <g pointerEvents="none">
                {/* Horizontal dashed line (shaft) - longer with more dashes */}
                <line
                  x1={thirdGlassX + thirdGlassWidth / 2 + 80 * scale}
                  y1={glassY + glassHeight / 2}
                  x2={thirdGlassX + thirdGlassWidth / 2 - 15 * scale}
                  y2={glassY + glassHeight / 2}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeDasharray="4 3"
                  strokeLinecap="round"
                />
                {/* Vertical line at right end */}
                <line
                  x1={thirdGlassX + thirdGlassWidth / 2 + 80 * scale}
                  y1={glassY + glassHeight / 2 - 15 * scale}
                  x2={thirdGlassX + thirdGlassWidth / 2 + 80 * scale}
                  y2={glassY + glassHeight / 2 + 15 * scale}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                {/* Arrowhead (pointing left) - tip at left end, V shape opening to the right */}
                <path
                  d={`M ${thirdGlassX + thirdGlassWidth / 2 - 35 * scale} ${glassY + glassHeight / 2} L ${thirdGlassX + thirdGlassWidth / 2 - 15 * scale} ${glassY + glassHeight / 2 - 12 * scale} M ${thirdGlassX + thirdGlassWidth / 2 - 35 * scale} ${glassY + glassHeight / 2} L ${thirdGlassX + thirdGlassWidth / 2 - 15 * scale} ${glassY + glassHeight / 2 + 12 * scale}`}
                  stroke="#666666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            )}
            {/* Hover highlight overlay for right section (third frame) when center */}
            {doorType === 'sliding' && slidingPosition === 'center' && hoveredInnerFrameCenter === 'right' && (
              <rect
                x={thirdGlassX}
                y={glassY}
                width={thirdGlassWidth}
                height={glassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
          </g>
        )}

        {/* Left Panel Glass */}
        {hasLeftPanel && (
          <g>
            {leftPanelHasOverhead && leftPanelOverheadOpeningH > 0 && (
              <g pointerEvents="none">
                {renderOverheadOpeningVisual({
                  ...panelOverheadVisualDeps,
                  openingX: leftPanelOverheadOpeningX,
                  openingY: leftPanelGlassY,
                  openingW: leftPanelOverheadOpeningW,
                  openingH: leftPanelOverheadOpeningH,
                  windowType: leftPanelOverheadWindowTypeProp,
                })}
              </g>
            )}
            {leftPanelHasOverhead && leftPanelOverheadDividerY && (
              renderSidePanelTransomRail(
                leftPanelTransomRailX,
                leftPanelOverheadDividerY,
                leftPanelTransomRailW,
                innerFrameInset,
              )
            )}
            {/* Side panel lower zone — all window types (bead + inset glass / inner sash) */}
            {(() => {
              const { openingX, openingY, openingW, openingH } = getSidePanelLowerOpening(
                leftPanelGlassX,
                leftPanelGlassY,
                leftPanelGlassWidth,
                leftPanelGlassHeight,
                leftPanelHasOverhead,
                leftPanelRenderGlassY,
                leftPanelRenderGlassHeight,
              );
              if (openingW <= 0 || openingH <= 0) return null;
              return (
                <>
                  <g pointerEvents="none">
                    {renderSidePanelLowerOpeningVisual(
                      openingX,
                      openingY,
                      openingW,
                      openingH,
                      leftPanelWindowType,
                    )}
                  </g>
                  <rect
                    x={openingX}
                    y={openingY}
                    width={openingW}
                    height={openingH}
                    fill="transparent"
                    style={{ cursor: hoveredLeftPanel ? 'pointer' : 'default', pointerEvents: 'auto' }}
                    onMouseEnter={() => setHoveredLeftPanel(true)}
                    onMouseLeave={() => setHoveredLeftPanel(false)}
                    onClick={() => {
                      setSelectedLeftPanel(true);
                      setLeftPanelSettingsOpen(true);
                    }}
                  >
                    <title>Click to configure Left Panel window type</title>
                  </rect>
                </>
              );
            })()}
            
            {/* Hover highlight overlay - rendered after inner frame so it appears on top */}
            {hoveredLeftPanel && (
              <rect
                x={leftPanelGlassX}
                y={leftPanelGlassY}
                width={leftPanelGlassWidth}
                height={leftPanelGlassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            
            {/* Left Panel Parapet */}
            {leftPanelHasParapet && leftPanelParapetTopY < leftPanelGlassY + leftPanelGlassHeight && (
              <>
                {/* Frame/border at top of parapet */}
                {leftPanelParapetTopY > leftPanelGlassY && (
                  <g>
                    {/* Outer frame (parapet color) with borderline */}
                    <rect
                      x={leftPanelGlassX}
                      y={leftPanelParapetTopY - innerFrameThickness}
                      width={leftPanelGlassWidth}
                      height={innerFrameThickness}
                      fill={leftPanelParapetColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={leftPanelGlassX + innerFrameThickness / 2}
                      y={leftPanelParapetTopY - innerFrameThickness + innerFrameThickness / 2}
                      width={leftPanelGlassWidth - innerFrameThickness}
                      height={innerFrameThickness / 2}
                      fill={leftPanelParapetColorHex}
                    />
                  </g>
                )}
                {/* Panel fill - based on leftPanelParapetPanelType */}
                {leftPanelParapetPanelType === 'glass' ? (
                  <>
                    {/* Glass texture for parapet */}
                    <rect
                      x={leftPanelGlassX}
                      y={leftPanelParapetTopY}
                      width={leftPanelGlassWidth}
                      height={leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={leftPanelGlassX}
                        y={leftPanelParapetTopY}
                        width={leftPanelGlassWidth}
                        height={leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY}
                        fill={glassTexturePattern}
                        opacity="1"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Panel gradient / solid color for sandwich/horizontal/vertical mullions */}
                    <rect
                      x={leftPanelGlassX}
                      y={leftPanelParapetTopY}
                      width={leftPanelGlassWidth}
                      height={leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY}
                      fill={leftPanelParapetColorHex}
                    />
                    {/* Horizontal mullions */}
                    {leftPanelParapetPanelType === 'horizontal-mullions' && (() => {
                      const parapetHeight = leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY;
                      const lineColor = '#D0D4DA';
                      const lineWidth = 0.5;
                      const numberOfLines = 8;
                      const lineSpacing = parapetHeight / (numberOfLines + 1);
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineY = leftPanelParapetTopY + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`h-mullion-left-panel-${i}`}
                                x1={leftPanelGlassX}
                                y1={lineY}
                                x2={leftPanelGlassX + leftPanelGlassWidth}
                                y2={lineY}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                    {/* Vertical mullions */}
                    {leftPanelParapetPanelType === 'vertical-mullions' && (() => {
                      const parapetWidth = leftPanelGlassWidth;
                      const lineColor = '#D0D4DA';
                      const lineWidth = 0.5;
                      const numberOfLines = 8;
                      const lineSpacing = parapetWidth / (numberOfLines + 1);
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineX = leftPanelGlassX + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`v-mullion-left-panel-${i}`}
                                x1={lineX}
                                y1={leftPanelParapetTopY}
                                x2={lineX}
                                y2={leftPanelParapetTopY + (leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY)}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Dark border around panel */}
                <rect
                  x={leftPanelGlassX}
                  y={leftPanelParapetTopY}
                  width={leftPanelGlassWidth}
                  height={leftPanelGlassY + leftPanelGlassHeight - leftPanelParapetTopY}
                  fill="none"
                  stroke="#4B4B4B"
                  strokeWidth="1"
                />
              </>
            )}
            
            {/* Left Panel Parapet Height Measurement Ruler */}
            {leftPanelHasParapet && leftPanelParapetTopY < doorFrameBottomY && (
              <g>
                {/* Vertical ruler line */}
                <line
                  x1={leftPanelGlassX - 30}
                  y1={leftPanelParapetTopY}
                  x2={leftPanelGlassX - 30}
                  y2={doorFrameBottomY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {/* Top tick mark */}
                <line
                  x1={leftPanelGlassX - 35}
                  y1={leftPanelParapetTopY}
                  x2={leftPanelGlassX - 25}
                  y2={leftPanelParapetTopY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {/* Bottom tick mark */}
                <line
                  x1={leftPanelGlassX - 35}
                  y1={doorFrameBottomY}
                  x2={leftPanelGlassX - 25}
                  y2={doorFrameBottomY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {/* Input field for parapet height */}
                <foreignObject 
                  x={leftPanelGlassX - 120} 
                  y={(leftPanelParapetTopY + doorFrameBottomY) / 2 - 12} 
                  width="80" 
                  height="24"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    <input
                      type="number"
                      value={leftPanelParapetHeightInput}
                      onChange={(e) => setLeftPanelParapetHeightInput(e.target.value)}
                      onBlur={() => {
                        if (!onLeftPanelParapetHeightChange) return;
                        const parsed = parseInt(leftPanelParapetHeightInput, 10);
                        const MIN_PARAPET_HEIGHT = 0;
                        const MAX_PARAPET_HEIGHT = height;
                        if (!Number.isNaN(parsed) && parsed >= MIN_PARAPET_HEIGHT && parsed <= MAX_PARAPET_HEIGHT) {
                          onLeftPanelParapetHeightChange(parsed);
                        } else {
                          setLeftPanelParapetHeightInput(String(Math.round(leftPanelParapetHeightMm)));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#2C2C2C',
                        outline: 'none',
                        padding: 0,
                        margin: 0,
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield',
                        width: `${Math.max(40, String(leftPanelParapetHeightInput).length * 8 + 6)}px`,
                        maxWidth: '80px',
                      }}
                    />
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                  </div>
                </foreignObject>
              </g>
            )}
            
            {/* Left Panel Top Glass + Overhead Measurement Ruler (above parapet) */}
            {leftPanelHasParapet && leftPanelParapetTopY > leftPanelGlassY && (
              <g>
                {/* Vertical ruler line — full upper zone */}
                <line
                  x1={leftPanelGlassX - 30}
                  y1={leftPanelGlassY}
                  x2={leftPanelGlassX - 30}
                  y2={leftPanelParapetTopY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {/* Top tick mark */}
                <line
                  x1={leftPanelGlassX - 35}
                  y1={leftPanelGlassY}
                  x2={leftPanelGlassX - 25}
                  y2={leftPanelGlassY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {/* Bottom tick mark (parapet top) */}
                <line
                  x1={leftPanelGlassX - 35}
                  y1={leftPanelParapetTopY}
                  x2={leftPanelGlassX - 25}
                  y2={leftPanelParapetTopY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                  strokeLinecap="square"
                />
                {leftPanelHasOverhead && leftPanelOverheadDividerY && (
                  <>
                    <line
                      x1={leftPanelGlassX - 35}
                      y1={leftPanelOverheadDividerY}
                      x2={leftPanelGlassX - 25}
                      y2={leftPanelOverheadDividerY}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                      strokeLinecap="square"
                    />
                    <line
                      x1={leftPanelGlassX - 35}
                      y1={leftPanelRenderGlassY}
                      x2={leftPanelGlassX - 25}
                      y2={leftPanelRenderGlassY}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                      strokeLinecap="square"
                    />
                    <foreignObject
                      x={leftPanelGlassX - 120}
                      y={(leftPanelGlassY + leftPanelOverheadDividerY) / 2 - 12}
                      width="80"
                      height="24"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                        <input
                          type="number"
                          value={leftPanelOverheadHeightInput}
                          onChange={(e) => setLeftPanelOverheadHeightInput(e.target.value)}
                          onBlur={() => {
                            commitSidePanelOverheadHeight(
                              parseInt(leftPanelOverheadHeightInput, 10),
                              leftPanelGlassHeight,
                              onLeftPanelOverheadHeightChange,
                              setLeftPanelOverheadHeightInput,
                              leftPanelOverheadHeightMm,
                            );
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur();
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#2C2C2C',
                            outline: 'none',
                            padding: 0,
                            margin: 0,
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                            width: `${Math.max(40, String(leftPanelOverheadHeightInput).length * 8 + 6)}px`,
                            maxWidth: '80px',
                          }}
                        />
                        <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                      </div>
                    </foreignObject>
                  </>
                )}
                {/* Main glass height above parapet (below overhead when enabled) */}
                <foreignObject
                  x={leftPanelGlassX - 120}
                  y={((leftPanelHasOverhead ? leftPanelRenderGlassY : leftPanelGlassY) + leftPanelParapetTopY) / 2 - 12}
                  width="80"
                  height="24"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C' }}>
                      {Math.round((leftPanelParapetTopY - (leftPanelHasOverhead ? leftPanelRenderGlassY : leftPanelGlassY)) / scale)}
                    </span>
                    <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                  </div>
                </foreignObject>
              </g>
            )}

            {/* Left Panel Overhead-only sub ruler (no parapet) */}
            {sidePanelOverheadEnabled && leftPanelHasOverhead && !leftPanelHasParapet && leftPanelOverheadDividerY &&
              renderSidePanelOverheadOnlySubRuler({
                rulerLineX: leftPanelGlassX - 30,
                tickStartX: leftPanelGlassX - 35,
                tickEndX: leftPanelGlassX - 25,
                labelX: leftPanelGlassX - 120,
                glassY: leftPanelGlassY,
                overheadDividerY: leftPanelOverheadDividerY,
                renderGlassY: leftPanelRenderGlassY,
                overheadHeightInput: leftPanelOverheadHeightInput,
                setOverheadHeightInput: setLeftPanelOverheadHeightInput,
                onOverheadHeightChange: onLeftPanelOverheadHeightChange,
                panelGlassHeightPx: leftPanelGlassHeight,
                overheadHeightMm: leftPanelOverheadHeightMm,
                zoneBottomY: doorFrameBottomY,
              })}
          </g>
        )}

        {/* Right Panel Glass */}
        {hasRightPanel && (
          <g>
            {rightPanelHasOverhead && rightPanelOverheadOpeningH > 0 && (
              <g pointerEvents="none">
                {renderOverheadOpeningVisual({
                  ...panelOverheadVisualDeps,
                  openingX: rightPanelOverheadOpeningX,
                  openingY: rightPanelGlassY,
                  openingW: rightPanelOverheadOpeningW,
                  openingH: rightPanelOverheadOpeningH,
                  windowType: rightPanelOverheadWindowTypeProp,
                })}
              </g>
            )}
            {rightPanelHasOverhead && rightPanelOverheadDividerY && (
              renderSidePanelTransomRail(
                rightPanelTransomRailX,
                rightPanelOverheadDividerY,
                rightPanelTransomRailW,
                innerFrameInset,
              )
            )}
            {/* Side panel lower zone — all window types (bead + inset glass / inner sash) */}
            {(() => {
              const { openingX, openingY, openingW, openingH } = getSidePanelLowerOpening(
                rightPanelGlassX,
                rightPanelGlassY,
                rightPanelGlassWidth,
                rightPanelGlassHeight,
                rightPanelHasOverhead,
                rightPanelRenderGlassY,
                rightPanelRenderGlassHeight,
              );
              if (openingW <= 0 || openingH <= 0) return null;
              return (
                <>
                  <g pointerEvents="none">
                    {renderSidePanelLowerOpeningVisual(
                      openingX,
                      openingY,
                      openingW,
                      openingH,
                      rightPanelWindowType,
                    )}
                  </g>
                  <rect
                    x={openingX}
                    y={openingY}
                    width={openingW}
                    height={openingH}
                    fill="transparent"
                    style={{ cursor: hoveredRightPanel ? 'pointer' : 'default', pointerEvents: 'auto' }}
                    onMouseEnter={() => setHoveredRightPanel(true)}
                    onMouseLeave={() => setHoveredRightPanel(false)}
                    onClick={() => {
                      setSelectedRightPanel(true);
                      setRightPanelSettingsOpen(true);
                    }}
                  >
                    <title>Click to configure Right Panel window type</title>
                  </rect>
                </>
              );
            })()}
            
            {/* Hover highlight overlay - rendered after inner frame so it appears on top */}
            {hoveredRightPanel && (
              <rect
                x={rightPanelGlassX}
                y={rightPanelGlassY}
                width={rightPanelGlassWidth}
                height={rightPanelGlassHeight}
                fill="rgba(255, 165, 0, 0.15)"
                stroke="rgba(255, 140, 0, 0.6)"
                strokeWidth="2"
                style={{ pointerEvents: 'none', cursor: 'pointer' }}
              />
            )}
            
            {/* Right Panel Parapet */}
            {rightPanelHasParapet && rightPanelParapetTopY < rightPanelGlassY + rightPanelGlassHeight && (
              <>
                {/* Frame/border at top of parapet */}
                {rightPanelParapetTopY > rightPanelGlassY && (
                  <g>
                    {/* Outer frame (parapet color) with borderline */}
                    <rect
                      x={rightPanelGlassX}
                      y={rightPanelParapetTopY - innerFrameThickness}
                      width={rightPanelGlassWidth}
                      height={innerFrameThickness}
                      fill={rightPanelParapetColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                    />
                    {/* Inner frame reveal */}
                    <rect
                      x={rightPanelGlassX + innerFrameThickness / 2}
                      y={rightPanelParapetTopY - innerFrameThickness + innerFrameThickness / 2}
                      width={rightPanelGlassWidth - innerFrameThickness}
                      height={innerFrameThickness / 2}
                      fill={rightPanelParapetColorHex}
                    />
                  </g>
                )}
                {/* Panel fill - based on rightPanelParapetPanelType */}
                {rightPanelParapetPanelType === 'glass' ? (
                  <>
                    {/* Glass texture for parapet */}
                    <rect
                      x={rightPanelGlassX}
                      y={rightPanelParapetTopY}
                      width={rightPanelGlassWidth}
                      height={rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY}
                      fill={glassFill}
                    />
                    {glassTexturePattern && (
                      <rect
                        x={rightPanelGlassX}
                        y={rightPanelParapetTopY}
                        width={rightPanelGlassWidth}
                        height={rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY}
                        fill={glassTexturePattern}
                        opacity="1"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Panel gradient / solid color for sandwich/horizontal/vertical mullions */}
                    <rect
                      x={rightPanelGlassX}
                      y={rightPanelParapetTopY}
                      width={rightPanelGlassWidth}
                      height={rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY}
                      fill={rightPanelParapetColorHex}
                    />
                    {/* Horizontal mullions */}
                    {rightPanelParapetPanelType === 'horizontal-mullions' && (() => {
                      const parapetHeight = rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY;
                      const lineColor = '#D0D4DA';
                      const lineWidth = 0.5;
                      const numberOfLines = 8;
                      const lineSpacing = parapetHeight / (numberOfLines + 1);
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineY = rightPanelParapetTopY + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`h-mullion-right-panel-${i}`}
                                x1={rightPanelGlassX}
                                y1={lineY}
                                x2={rightPanelGlassX + rightPanelGlassWidth}
                                y2={lineY}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                    {/* Vertical mullions */}
                    {rightPanelParapetPanelType === 'vertical-mullions' && (() => {
                      const parapetWidth = rightPanelGlassWidth;
                      const lineColor = '#D0D4DA';
                      const lineWidth = 0.5;
                      const numberOfLines = 8;
                      const lineSpacing = parapetWidth / (numberOfLines + 1);
                      
                      return (
                        <g pointerEvents="none">
                          {Array.from({ length: numberOfLines }, (_, i) => {
                            const lineX = rightPanelGlassX + (lineSpacing * (i + 1));
                            return (
                              <line
                                key={`v-mullion-right-panel-${i}`}
                                x1={lineX}
                                y1={rightPanelParapetTopY}
                                x2={lineX}
                                y2={rightPanelParapetTopY + (rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY)}
                                stroke={lineColor}
                                strokeWidth={lineWidth}
                              />
                            );
                          })}
                        </g>
                      );
                    })()}
                  </>
                )}
                {/* Dark border around panel */}
                <rect
                  x={rightPanelGlassX}
                  y={rightPanelParapetTopY}
                  width={rightPanelGlassWidth}
                  height={rightPanelGlassY + rightPanelGlassHeight - rightPanelParapetTopY}
                  fill="none"
                  stroke="#4B4B4B"
                  strokeWidth="1"
                />
              </>
            )}
          </g>
        )}

        {/* Handle and Lock Knob on Inner Frame */}
        {(() => {
          // Check if this is a sliding door
          const isSlidingDoor = doorType === 'sliding';
          
          if (isSlidingDoor) {
            // Sliding door handle - vertical pull handle
            const handleWidth = 16 * scale; // Expanded width of the vertical handle bar
            const handleHeight = 120 * scale; // Height of the vertical handle
            
            // When innerFrameSlidingSection === 'right', handle is on left border of first (left) inner frame
            // Otherwise, handle is on right border of second (right) inner frame
            // For center section: use innerFrameSlidingSectionLeft for first 2 frames
            const handleX = ((slidingPosition === 'left' || slidingPosition === 'right') && innerFrameSlidingSection === 'right')
              ? leftInnerFrameX - handleWidth / 2 + innerFrameThickness / 2 // Left border of left frame
              : (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionLeft === 'right')
              ? leftInnerFrameX - handleWidth / 2 + innerFrameThickness / 2 // Left border of left frame for center section
              : rightInnerFrameX + rightInnerFrameWidth - handleWidth / 2 - innerFrameThickness / 2; // Right border of right frame
            
            const handleY = innerFrameY + (innerFrameHeight - handleHeight) / 2; // Center vertically
            
            // Center frame handle (when slidingPosition === 'center')
            // When innerFrameSlidingSectionRight === 'right' (default): on left border of center frame
            // When innerFrameSlidingSectionRight === 'left': on right border of third frame
            const centerHandleX = needsCenterFrame && centerInnerFrameX !== null
              ? (doorType === 'sliding' && slidingPosition === 'center' && innerFrameSlidingSectionRight === 'left')
                ? (thirdInnerFrameX !== null && thirdInnerFrameWidth !== null
                  ? thirdInnerFrameX + thirdInnerFrameWidth - handleWidth / 2 - innerFrameThickness / 2 // Right border of third frame
                  : centerInnerFrameX - handleWidth / 2 + innerFrameThickness / 2) // Fallback: left border of center frame
                : centerInnerFrameX - handleWidth / 2 + innerFrameThickness / 2 // Default: left border of center frame
              : null;
            const centerHandleY = needsCenterFrame ? handleY : null;
            
            return (
              <g>
                {/* Sliding door handle - vertical recessed pull bar */}
                {/* Outer frame/recess */}
                <rect
                  x={handleX - 2 * scale}
                  y={handleY - 2 * scale}
                  width={handleWidth + 4 * scale}
                  height={handleHeight + 4 * scale}
                  fill={interiorColorHex}
                  stroke={frameEdgeStroke}
                  strokeWidth="0.5"
                  rx={4 * scale}
                  ry={4 * scale}
                />
                {/* Inner handle bar */}
                <rect
                  x={handleX}
                  y={handleY}
                  width={handleWidth}
                  height={handleHeight}
                  fill="#4B4B4B"
                  rx={3 * scale}
                  ry={3 * scale}
                />
                {/* Highlight on handle */}
                <rect
                  x={handleX + handleWidth * 0.2}
                  y={handleY + handleHeight * 0.1}
                  width={handleWidth * 0.3}
                  height={handleHeight * 0.8}
                  fill="#6B6B6B"
                  rx={2 * scale}
                  ry={2 * scale}
                />
                
                {/* Center frame handle - on left border of center (3rd) inner frame */}
                {needsCenterFrame && centerHandleX !== null && centerHandleY !== null && (
                  <>
                    {/* Outer frame/recess */}
                    <rect
                      x={centerHandleX - 2 * scale}
                      y={centerHandleY - 2 * scale}
                      width={handleWidth + 4 * scale}
                      height={handleHeight + 4 * scale}
                      fill={interiorColorHex}
                      stroke={frameEdgeStroke}
                      strokeWidth="0.5"
                      rx={4 * scale}
                      ry={4 * scale}
                    />
                    {/* Inner handle bar */}
                    <rect
                      x={centerHandleX}
                      y={centerHandleY}
                      width={handleWidth}
                      height={handleHeight}
                      fill="#4B4B4B"
                      rx={3 * scale}
                      ry={3 * scale}
                    />
                    {/* Highlight on handle */}
                    <rect
                      x={centerHandleX + handleWidth * 0.2}
                      y={centerHandleY + handleHeight * 0.1}
                      width={handleWidth * 0.3}
                      height={handleHeight * 0.8}
                      fill="#6B6B6B"
                      rx={2 * scale}
                      ry={2 * scale}
                    />
                  </>
                )}
              </g>
            );
          } else {
            // Regular door handle - check if RVS Bar (H-shaped) or Standard Door Knob
            const isRVSBar = parapet === 'rvs-bar' && isVoordeur;
            
            if (isRVSBar) {
              // Single long vertical stainless steel pull handle (simplified H-shape)
              const handleWidth = 16 * scale; // Width of the vertical bar
              const handleHeight = 280 * scale; // Height of the vertical bar (extra tall)
              
              // Position handle - works for both single and double pane
              const isLeftHandle = handlePosition === 'left';
              let handleX;
              
              if (isDoublePane) {
                // Double pane: position on border between frames, then nudge slightly left to sit centered on the border
                handleX = isLeftHandle 
                  ? (leftInnerFrameX + leftInnerFrameWidth) - handleWidth / 2 - innerFrameThickness / 2  // Right edge of left inner frame
                  : rightInnerFrameX + handleWidth / 2 + innerFrameThickness / 2;                        // Left edge of right inner frame
              } else {
                // Single pane: position on left or right side of the single frame
                handleX = isLeftHandle 
                  ? leftInnerFrameX + handleWidth / 2 + innerFrameThickness / 2  // Left side of frame
                  : leftInnerFrameX + leftInnerFrameWidth - handleWidth / 2 - innerFrameThickness / 2;  // Right side of frame
              }

              // Nudge a few pixels to the left so the bar visually centers on the frame border
              handleX -= 3 * scale;
              
              const handleY = innerFrameY + (innerFrameHeight - handleHeight) / 2; // Center vertically
              
              return (
                <g>
                  {/* Single long vertical pull handle */}
                  <rect
                    x={handleX - handleWidth / 2}
                    y={handleY}
                    width={handleWidth}
                    height={handleHeight}
                    fill="#C0C0C0"
                    stroke="#808080"
                    strokeWidth="1"
                    rx={handleWidth / 2}
                    ry={handleWidth / 2}
                  />
                  {/* Highlight/shine on handle for metallic effect */}
                  <rect
                    x={handleX - handleWidth / 2 + handleWidth * 0.2}
                    y={handleY + handleHeight * 0.1}
                    width={handleWidth * 0.3}
                    height={handleHeight * 0.8}
                    fill="#E0E0E0"
                    rx={handleWidth * 0.15}
                    ry={handleWidth * 0.15}
                  />
                  {/* Top mounting flange/washer */}
                  <circle
                    cx={handleX}
                    cy={handleY}
                    r={handleWidth / 2 + 3 * scale}
                    fill="#A0A0A0"
                    stroke="#606060"
                    strokeWidth="0.5"
                  />
                  {/* Bottom mounting flange/washer */}
                  <circle
                    cx={handleX}
                    cy={handleY + handleHeight}
                    r={handleWidth / 2 + 3 * scale}
                    fill="#A0A0A0"
                    stroke="#606060"
                    strokeWidth="0.5"
                  />
                </g>
              );
            } else {
              // Standard door handle - circular with lever
              // Position handle - works for both single and double pane
              const isLeftHandle = handlePosition === 'left';
              const handleBaseRadius = 20 * scale;
              let handleX;
              
              if (isDoublePane) {
                // Double pane: position on border between frames
                handleX = isLeftHandle 
                  ? (leftInnerFrameX + leftInnerFrameWidth) - handleBaseRadius * 1.5  // Right edge of left inner frame, shifted left
                  : rightInnerFrameX + handleBaseRadius * 1.5;                        // Left edge of right inner frame, shifted right
              } else {
                // Single pane: position on left or right side of the single frame
                handleX = isLeftHandle 
                  ? leftInnerFrameX + handleBaseRadius * 1.5  // Left side of frame
                  : leftInnerFrameX + leftInnerFrameWidth - handleBaseRadius * 1.5;  // Right side of frame
              }
              
              const handleY = innerFrameY + innerFrameHeight / 2; // Center vertically
              
              // For Achterdeur and Voordeur, swap handle directions:
              // - When "Inside Left" is selected, use the direction that "Inside Right" normally has (extend right)
              // - When "Inside Right" is selected, use the direction that "Inside Left" normally has (extend left)
              const shouldSwapHandleDirection = isVoordeur || isAchterdeur;
              
              return (
                <g>
                  {/* Handle base - circular */}
                  <circle
                    cx={handleX}
                    cy={handleY}
                    r={20 * scale}
                    fill="#1A5F7A"
                  />
                  {/* Handle lever - horizontal line extending opposite direction */}
                  {/* For Terrasdeur: Left handle extends left, Right handle extends right */}
                  {/* For Achterdeur/Voordeur: Left handle extends right (swapped), Right handle extends left (swapped) */}
                  <line
                    x1={handleX}
                    y1={handleY}
                    x2={shouldSwapHandleDirection
                      ? (isLeftHandle 
                          ? handleX + 50 * scale  // Left handle: extend right (swapped - like right handle normally does)
                          : handleX - 50 * scale) // Right handle: extend left (swapped - like left handle normally does)
                      : (isLeftHandle 
                          ? handleX - 50 * scale  // Left handle: extend left (normal)
                          : handleX + 50 * scale) // Right handle: extend right (normal)
                    }
                    y2={handleY}
                    stroke="#1A5F7A"
                    strokeWidth={12 * scale}
                    strokeLinecap="round"
                  />
                  {/* Lock knob - larger circle below handle with gap */}
                  <circle
                    cx={handleX}
                    cy={handleY + 50 * scale}
                    r={14 * scale}
                    fill="#1A5F7A"
                  />
                  {/* Lock cylinder dot - white dot in center of lock knob */}
                  <circle
                    cx={handleX}
                    cy={handleY + 50 * scale}
                    r={7 * scale}
                    fill="#FFFFFF"
                  />
                </g>
              );
            }
          }
        })()}

        {/* Overhead outline — redraw on top when later layers cover transom edges */}
        {hasOverhead && overheadDividerY && (
          <g pointerEvents="none">
            {Array.from({ length: overheadParts }, (_, index) => {
              const opening = getOverheadPartOpening(index);
              if (!opening) return null;
              if (overheadPartHasInnerFrame(opening.windowType)) {
                const m = getOverheadInnerFrameMetrics(opening.openingX, opening.openingY, opening.openingW, opening.openingH);
                return (
                  <g key={`overhead-inner-outline-top-${index}`}>
                    {renderOverheadInnerSashBottomBorders(m.frameX, m.frameY, m.frameW, m.frameH, m.frameT)}
                  </g>
                );
              }
              if (!doorHasSidePanels) return null;
              return (
                <g key={`overhead-outline-top-${index}`}>
                  {renderOverheadFixedSashOutline(opening.openingX, opening.openingY, opening.openingW, opening.openingH)}
                </g>
              );
            })}
          </g>
        )}

        {/* Side-panel overhead — redraw on top so window-type visuals stay visible */}
        {sidePanelOverheadEnabled && (
          <g pointerEvents="none">
            {leftPanelHasOverhead && leftPanelOverheadOpeningH > 0 && (
              <g key={`left-panel-overhead-${leftPanelOverheadWindowTypeProp}`}>
                {renderOverheadOpeningVisual({
                  ...panelOverheadVisualDeps,
                  openingX: leftPanelOverheadOpeningX,
                  openingY: leftPanelGlassY,
                  openingW: leftPanelOverheadOpeningW,
                  openingH: leftPanelOverheadOpeningH,
                  windowType: leftPanelOverheadWindowTypeProp,
                })}
              </g>
            )}
            {rightPanelHasOverhead && rightPanelOverheadOpeningH > 0 && (
              <g key={`right-panel-overhead-${rightPanelOverheadWindowTypeProp}`}>
                {renderOverheadOpeningVisual({
                  ...panelOverheadVisualDeps,
                  openingX: rightPanelOverheadOpeningX,
                  openingY: rightPanelGlassY,
                  openingW: rightPanelOverheadOpeningW,
                  openingH: rightPanelOverheadOpeningH,
                  windowType: rightPanelOverheadWindowTypeProp,
                })}
              </g>
            )}
          </g>
        )}

        {/* Side-panel transom rail outlines — redraw on top */}
        {sidePanelOverheadEnabled && (
          <g pointerEvents="none">
            {leftPanelHasOverhead && leftPanelOverheadDividerY &&
              renderSidePanelTransomRailStrokes(
                leftPanelTransomRailX,
                leftPanelOverheadDividerY,
                leftPanelTransomRailW,
                innerFrameInset,
              )}
            {rightPanelHasOverhead && rightPanelOverheadDividerY &&
              renderSidePanelTransomRailStrokes(
                rightPanelTransomRailX,
                rightPanelOverheadDividerY,
                rightPanelTransomRailW,
                innerFrameInset,
              )}
          </g>
        )}

        {/* Side-panel lower zone — redraw after transom rail (all window types incl. fixed-glass) */}
        {sidePanelOverheadEnabled && (
          <g pointerEvents="none">
            {leftPanelHasOverhead && leftPanelOverheadDividerY && leftPanelRenderGlassHeight > 0 && (
              <g key={`left-panel-lower-${leftPanelWindowType}`}>
                {renderSidePanelLowerOpeningVisual(
                  leftPanelGlassX,
                  leftPanelRenderGlassY,
                  leftPanelGlassWidth,
                  leftPanelRenderGlassHeight,
                  leftPanelWindowType,
                )}
              </g>
            )}
            {rightPanelHasOverhead && rightPanelOverheadDividerY && rightPanelRenderGlassHeight > 0 && (
              <g key={`right-panel-lower-${rightPanelWindowType}`}>
                {renderSidePanelLowerOpeningVisual(
                  rightPanelGlassX,
                  rightPanelRenderGlassY,
                  rightPanelGlassWidth,
                  rightPanelRenderGlassHeight,
                  rightPanelWindowType,
                )}
              </g>
            )}
          </g>
        )}

        {/* Printable frame edges — thin dark outlines when frame colors are light (e.g. white on paper) */}
        {usePrintableFrameEdges && (
          <g pointerEvents="none" fill="none" stroke={FRAME_EDGE_STROKE_DARK}>
            {isLightFrameColor(exteriorColorHex) && (
              <>
                {/* Outer frame outlines — top/left/right only (skip bottom line under drawing) */}
                <line x1={startX} y1={startY} x2={startX + scaledWidth} y2={startY} strokeWidth={0.6} />
                <line x1={startX} y1={startY} x2={startX} y2={startY + scaledHeight} strokeWidth={0.6} />
                <line x1={startX + scaledWidth} y1={startY} x2={startX + scaledWidth} y2={startY + scaledHeight} strokeWidth={0.6} />
                <line
                  x1={startX + frameThickness}
                  y1={startY + frameThickness}
                  x2={startX + scaledWidth - frameThickness}
                  y2={startY + frameThickness}
                  strokeWidth={0.5}
                />
                <line
                  x1={startX + frameThickness}
                  y1={startY + frameThickness}
                  x2={startX + frameThickness}
                  y2={startY + scaledHeight - frameThickness}
                  strokeWidth={0.5}
                />
                <line
                  x1={startX + scaledWidth - frameThickness}
                  y1={startY + frameThickness}
                  x2={startX + scaledWidth - frameThickness}
                  y2={startY + scaledHeight - frameThickness}
                  strokeWidth={0.5}
                />
                {hasLeftPanel && (
                  <rect
                    x={leftPanelGlassX}
                    y={leftPanelGlassY}
                    width={leftPanelGlassWidth}
                    height={leftPanelGlassHeight}
                    strokeWidth={0.5}
                  />
                )}
                {hasRightPanel && (
                  <rect
                    x={rightPanelGlassX}
                    y={rightPanelGlassY}
                    width={rightPanelGlassWidth}
                    height={rightPanelGlassHeight}
                    strokeWidth={0.5}
                  />
                )}
                {sidePanelOverheadEnabled && leftPanelHasOverhead && leftPanelOverheadDividerY &&
                  renderSidePanelTransomRailStrokes(
                    leftPanelTransomRailX,
                    leftPanelOverheadDividerY,
                    leftPanelTransomRailW,
                    innerFrameInset,
                    FRAME_EDGE_STROKE_DARK,
                  )}
                {sidePanelOverheadEnabled && rightPanelHasOverhead && rightPanelOverheadDividerY &&
                  renderSidePanelTransomRailStrokes(
                    rightPanelTransomRailX,
                    rightPanelOverheadDividerY,
                    rightPanelTransomRailW,
                    innerFrameInset,
                    FRAME_EDGE_STROKE_DARK,
                  )}
                {isDoublePane && doorType !== 'sliding' && !needsThirdFrame && !needsCenterFrame && dividerWidth > 0 && (
                  <>
                    <line
                      x1={doorSectionX + doorSectionWidth / 2 - dividerWidth / 2}
                      y1={doorCenterDividerStartY}
                      x2={doorSectionX + doorSectionWidth / 2 - dividerWidth / 2}
                      y2={doorCenterDividerEndY}
                      strokeWidth={0.5}
                    />
                    <line
                      x1={doorSectionX + doorSectionWidth / 2 + dividerWidth / 2}
                      y1={doorCenterDividerStartY}
                      x2={doorSectionX + doorSectionWidth / 2 + dividerWidth / 2}
                      y2={doorCenterDividerEndY}
                      strokeWidth={0.5}
                    />
                  </>
                )}
              </>
            )}
            {/* Inner sash outlines for print — terras/voordeur only; Schuifpui skips these (caused false bottom line on sashes) */}
            {isLightFrameColor(interiorColorHex) && doorType !== 'sliding' && (
              <g stroke={FRAME_EDGE_STROKE_DEFAULT}>
                <rect
                  x={leftGlassX}
                  y={glassY}
                  width={leftGlassWidth}
                  height={glassHeight}
                  strokeWidth={0.5}
                />
                {isDoublePane && (
                  <rect
                    x={rightGlassX}
                    y={glassY}
                    width={rightGlassWidth}
                    height={glassHeight}
                    strokeWidth={0.5}
                  />
                )}
                {needsCenterFrame && centerGlassX !== null && centerGlassWidth !== null && (
                  <rect
                    x={centerGlassX}
                    y={glassY}
                    width={centerGlassWidth}
                    height={glassHeight}
                    strokeWidth={0.5}
                  />
                )}
                {(needsThirdFrame || needsCenterFrame) && thirdGlassX !== null && thirdGlassWidth !== null && (
                  <rect
                    x={thirdGlassX}
                    y={glassY}
                    width={thirdGlassWidth}
                    height={glassHeight}
                    strokeWidth={0.5}
                  />
                )}
                {/* Overhead interior outline in door section (needed when side panels are present) */}
                {hasOverhead && overheadDividerY &&
                  Array.from({ length: overheadParts }, (_, index) => {
                    const opening = getOverheadPartOpening(index);
                    if (!opening) return null;
                    if (overheadPartHasInnerFrame(opening.windowType)) {
                      const m = getOverheadInnerFrameMetrics(opening.openingX, opening.openingY, opening.openingW, opening.openingH);
                      return (
                        <rect
                          key={`printable-overhead-inner-outline-${index}`}
                          x={m.innerPerimeterX}
                          y={m.innerPerimeterY}
                          width={m.innerPerimeterW}
                          height={m.innerPerimeterH}
                          strokeWidth={0.5}
                        />
                      );
                    }
                    return (
                      <rect
                        key={`printable-overhead-outline-${index}`}
                        x={opening.openingX}
                        y={opening.openingY}
                        width={opening.openingW}
                        height={opening.openingH}
                        strokeWidth={0.5}
                      />
                    );
                  })}
              </g>
            )}
          </g>
        )}

        {/* Dimension Lines - Width */}
        <g>
          <line
            x1={startX}
            y1={startY + scaledHeight + 50}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + 50}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX}
            y1={startY + scaledHeight + 45}
            x2={startX}
            y2={startY + scaledHeight + 55}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth}
            y1={startY + scaledHeight + 45}
            x2={startX + scaledWidth}
            y2={startY + scaledHeight + 55}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <foreignObject
            x={startX + scaledWidth / 2 - mainDimLabelWidth / 2}
            y={startY + scaledHeight + 55}
            width={mainDimLabelWidth}
            height={24}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <input
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                onBlur={() => {
                  if (!onWidthChange) return;
                  const parsed = parseInt(widthInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
                    setDimensionError(null);
                    onWidthChange(parsed);
                    // Also update parapet height to match width when parapet is active
                    if (hasParapet && onParapetHeightChange) {
                      const clampedHeight = Math.max(0, Math.min(height, parsed));
                      onParapetHeightChange(clampedHeight);
                      setParapetHeightInput(String(clampedHeight));
                    }
                  } else {
                    setWidthInput(String(width));
                    setDimensionError(`Width must be between ${MIN_WIDTH} mm and ${MAX_WIDTH} mm.`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    if (!onWidthChange) return;
                    const parsed = parseInt(widthInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
                      setDimensionError(null);
                      onWidthChange(parsed);
                      // Also update parapet height to match width when parapet is active
                      if (hasParapet && onParapetHeightChange) {
                        const clampedHeight = Math.max(0, Math.min(height, parsed));
                        onParapetHeightChange(clampedHeight);
                        setParapetHeightInput(String(clampedHeight));
                      }
                    } else {
                      setWidthInput(String(width));
                      setDimensionError(`Width must be between ${MIN_WIDTH} mm and ${MAX_WIDTH} mm.`);
                    }
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: getDimensionInputWidth(widthInput, mainDimFontScale),
                  minWidth: getDimensionInputWidth(widthInput, mainDimFontScale),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
            </div>
          </foreignObject>
        </g>

        {/* Dimension Lines - Height */}
        <g>
          <line
            x1={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset - 5}
            y1={startY}
            x2={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset - 5}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset - 10}
            y1={startY}
            x2={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset}
            y2={startY}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <line
            x1={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset - 10}
            y1={startY + scaledHeight}
            x2={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset}
            y2={startY + scaledHeight}
            stroke="#2C2C2C"
            strokeWidth="1.5"
          />
          <foreignObject
            x={startX + scaledWidth + resolvedMainHeightRulerOffset + achterdeurRulerOffset + terrasdeurRulerOffset + 5}
            y={startY + scaledHeight / 2 - 12}
            width={heightDimLabelWidth}
            height={24}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
              <input
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                onBlur={() => {
                  if (!onHeightChange) return;
                  const parsed = parseInt(heightInput, 10);
                  if (!Number.isNaN(parsed) && parsed >= MIN_HEIGHT && parsed <= MAX_HEIGHT) {
                    setDimensionError(null);
                    onHeightChange(parsed);
                  } else {
                    setHeightInput(String(height));
                    setDimensionError(`Height must be between ${MIN_HEIGHT} mm and ${MAX_HEIGHT} mm.`);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    if (!onHeightChange) return;
                    const parsed = parseInt(heightInput, 10);
                    if (!Number.isNaN(parsed) && parsed >= MIN_HEIGHT && parsed <= MAX_HEIGHT) {
                      setDimensionError(null);
                      onHeightChange(parsed);
                    } else {
                      setHeightInput(String(height));
                      setDimensionError(`Height must be between ${MIN_HEIGHT} mm and ${MAX_HEIGHT} mm.`);
                    }
                  }
                }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2C2C2C',
                  outline: 'none',
                  padding: 0,
                  margin: 0,
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                  width: getDimensionInputWidth(heightInput, mainDimFontScale),
                  minWidth: getDimensionInputWidth(heightInput, mainDimFontScale),
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
            </div>
          </foreignObject>
        </g>

        {/* Per-overhead and inner frame vertical dimensions (only when overhead is active) */}
        {hasOverhead && overheadDividerY && (() => {
          // Calculate heights correctly to match main ruler
          // Overhead height: use prop directly (should match overheadHeightMm)
          // Inner frame height: from divider to bottom (should equal height - overheadHeight)
          const innerFrameHeightMm = height - overheadHeightMm;
          const x = startX + scaledWidth + resolvedOverheadSubRulerOffset; // Both overhead and inner frame rulers aligned at same position, shifted right

          // Editing the main door section height keeps the total height fixed and
          // adjusts the overhead (top light). The door section is capped at MAX_DOOR_HEIGHT
          // and the resulting overhead must stay within its allowed bounds.
          const commitInnerFrameHeight = () => {
            if (!onOverheadHeightChange) {
              setInnerFrameHeightInput(String(Math.round(innerFrameHeightMm)));
              return;
            }
            const parsed = parseInt(innerFrameHeightInput, 10);
            if (Number.isNaN(parsed)) {
              setInnerFrameHeightInput(String(Math.round(innerFrameHeightMm)));
              return;
            }
            if (parsed > maxDoorHeightForTotal) {
              setDimensionError(`Main door height must not exceed ${maxDoorHeightForTotal} mm (top light minimum is ${effectiveMinOverhead} mm).`);
              setInnerFrameHeightInput(String(Math.round(innerFrameHeightMm)));
              return;
            }
            if (MIN_DOOR_HEIGHT > 0 && parsed < MIN_DOOR_HEIGHT) {
              setDimensionError(`Main door height must be at least ${MIN_DOOR_HEIGHT} mm.`);
              setInnerFrameHeightInput(String(Math.round(innerFrameHeightMm)));
              return;
            }
            const newOverhead = Math.round(height - parsed);
            if (newOverhead < effectiveMinOverhead || newOverhead > effectiveMaxOverhead) {
              const minDoor = useDynamicOverheadBounds
                ? MIN_DOOR_HEIGHT
                : Math.round(height - MAX_OVERHEAD_HEIGHT);
              const maxDoor = useDynamicOverheadBounds
                ? maxDoorHeightForTotal
                : Math.min(MAX_DOOR_HEIGHT, Math.round(height - MIN_OVERHEAD_HEIGHT));
              setDimensionError(`Main door height must be between ${minDoor} mm and ${maxDoor} mm for the current total height.`);
              setInnerFrameHeightInput(String(Math.round(innerFrameHeightMm)));
              return;
            }
            setDimensionError(null);
            onOverheadHeightChange(newOverhead);
          };

          return (
            <>
              {/* Overhead height measurement */}
              <g>
                {/* segment line - from top of drawing (startY) to divider to match main ruler */}
                <line
                  x1={x}
                  y1={startY}
                  x2={x}
                  y2={overheadDividerY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* top tick */}
                <line
                  x1={x - 5}
                  y1={startY}
                  x2={x + 5}
                  y2={startY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* bottom tick */}
                <line
                  x1={x - 5}
                  y1={overheadDividerY}
                  x2={x + 5}
                  y2={overheadDividerY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* Editable label */}
                <foreignObject
                  x={x + 8}
                  y={(startY + overheadDividerY) / 2 - 12}
                  width="100"
                  height="24"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <input
                      type="number"
                      value={overheadHeightInput}
                      onChange={(e) => setOverheadHeightInput(e.target.value)}
                      onBlur={() => {
                        if (!onOverheadHeightChange) return;
                        const parsed = parseInt(overheadHeightInput, 10);
                        if (Number.isNaN(parsed)) {
                          setOverheadHeightInput(String(Math.round(overheadHeightMm)));
                          return;
                        }
                        if (parsed < effectiveMinOverhead) {
                          setDimensionError(`Top light height must be at least ${effectiveMinOverhead} mm. Adjust the main door panel instead.`);
                          setOverheadHeightInput(String(Math.round(overheadHeightMm)));
                          return;
                        }
                        if (parsed > effectiveMaxOverhead) {
                          setDimensionError(`Top light height must not exceed ${effectiveMaxOverhead} mm for the current total height.`);
                          setOverheadHeightInput(String(Math.round(overheadHeightMm)));
                          return;
                        }
                        setDimensionError(null);
                        onOverheadHeightChange(parsed);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#2C2C2C',
                        outline: 'none',
                        padding: 0,
                        margin: 0,
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield',
                        width: `${String(overheadHeightInput).length * 8 + 10}px`,
                        maxWidth: '56px',
                      }}
                    />
                  </div>
                </foreignObject>
              </g>
              
              {/* Inner frame height measurement */}
              <g>
                {/* segment line */}
                <line
                  x1={x}
                  y1={overheadDividerY}
                  x2={x}
                  y2={startY + scaledHeight}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* top tick */}
                <line
                  x1={x - 5}
                  y1={overheadDividerY}
                  x2={x + 5}
                  y2={overheadDividerY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* bottom tick */}
                <line
                  x1={x - 5}
                  y1={startY + scaledHeight}
                  x2={x + 5}
                  y2={startY + scaledHeight}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                {/* Editable label for the main door section height (total - overhead) */}
                <foreignObject
                  x={x + 8}
                  y={(overheadDividerY + startY + scaledHeight) / 2 - 12}
                  width="100"
                  height="24"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <input
                      type="number"
                      value={innerFrameHeightInput}
                      onChange={(e) => setInnerFrameHeightInput(e.target.value)}
                      onBlur={commitInnerFrameHeight}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'left',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#2C2C2C',
                        outline: 'none',
                        padding: 0,
                        margin: 0,
                        WebkitAppearance: 'none',
                        MozAppearance: 'textfield',
                        width: `${String(innerFrameHeightInput).length * 8 + 10}px`,
                        maxWidth: '56px',
                      }}
                    />
                  </div>
                </foreignObject>
              </g>
            </>
          );
        })()}

        {/* Parapet height vertical dimension — achterdeur/terrasdeur: no side panels only */}
        {showMainParapetHeightRuler && (() => {
          const x = startX + scaledWidth + parapetHeightRulerOffset;
          const yBottom = startY + scaledHeight;
          const yTop = parapetTopY;

          return (
            <g>
              {/* segment line from bottom of frame up to parapet top */}
              <line
                x1={x}
                y1={yBottom}
                x2={x}
                y2={yTop}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              {/* bottom tick */}
              <line
                x1={x - 5}
                y1={yBottom}
                x2={x + 5}
                y2={yBottom}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              {/* top tick */}
              <line
                x1={x - 5}
                y1={yTop}
                x2={x + 5}
                y2={yTop}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              {/* Editable input controlling parapet height in mm (no "mm" label) */}
              <foreignObject
                x={x + 8}
                y={(yTop + yBottom) / 2 - 12}
                width={getDimensionLabelBoxWidth(parapetHeightInput, 1)}
                height="24"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', overflow: 'visible' }}>
                  <input
                    type="number"
                    value={parapetHeightInput}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setParapetHeightInput(newValue);
                    }}
                    onBlur={(e) => {
                      if (!onParapetHeightChange) {
                        return;
                      }
                      const parsed = parseInt(parapetHeightInput, 10);
                      if (Number.isNaN(parsed)) {
                        // Reset to last valid value from props
                        const resetValue = Math.round(parapetHeight || 250);
                        setParapetHeightInput(String(resetValue));
                        onParapetHeightChange(resetValue);
                        return;
                      }
                      // Clamp between 0 and door height (in mm)
                      const clamped = Math.max(0, Math.min(height, parsed));
                      // Update local state immediately for responsive UI
                      setParapetHeightInput(String(clamped));
                      // Call the parent handler to update the actual parapet height
                      // This will update the config, which will update the prop, triggering a re-render
                      onParapetHeightChange(clamped);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (!onParapetHeightChange) {
                          return;
                        }
                        const parsed = parseInt(parapetHeightInput, 10);
                        if (Number.isNaN(parsed)) {
                          // Reset to last valid value from props
                          const resetValue = Math.round(parapetHeight || 250);
                          setParapetHeightInput(String(resetValue));
                          onParapetHeightChange(resetValue);
                          e.target.blur();
                          return;
                        }
                        // Clamp between 0 and door height (in mm)
                        const clamped = Math.max(0, Math.min(height, parsed));
                        // Update local state immediately for responsive UI
                        setParapetHeightInput(String(clamped));
                        // Call the parent handler to update the actual parapet height
                        // This will update the config, which will update the prop, triggering a re-render
                        onParapetHeightChange(clamped);
                        e.target.blur();
                      }
                    }}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#2C2C2C',
                      outline: 'none',
                      padding: 0,
                      margin: 0,
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield',
                      width: getDimensionInputWidth(parapetHeightInput, 1),
                      minWidth: getDimensionInputWidth(parapetHeightInput, 1),
                    }}
                  />
                </div>
              </foreignObject>
            </g>
          );
        })()}

        {/* Sub Measurement Rulers for Panels - above main measurement */}
        {hasLeftPanel && (
          <g>
            {/* Left Panel Width Measurement */}
            <line
              x1={startX}
              y1={startY + scaledHeight + 10}
              x2={startX + leftPanelWidth}
              y2={startY + scaledHeight + 10}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={startX}
              y1={startY + scaledHeight + 5}
              x2={startX}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={startX + leftPanelWidth}
              y1={startY + scaledHeight + 5}
              x2={startX + leftPanelWidth}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <foreignObject
              x={startX + leftPanelWidth / 2 - getDimensionLabelBoxWidth(leftPanelWidthInput, subDimFontScale) / 2}
              y={startY + scaledHeight + 15}
              width={getDimensionLabelBoxWidth(leftPanelWidthInput, subDimFontScale)}
              height="24"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                <input
                  type="number"
                  value={leftPanelWidthInput}
                  onChange={(e) => setLeftPanelWidthInput(e.target.value)}
                  onBlur={() => {
                    if (!onLeftPanelWidthChange) return;
                    const parsed = parseInt(leftPanelWidthInput, 10);
                    if (Number.isNaN(parsed)) {
                      setLeftPanelWidthInput(String(Math.round(leftPanelWidthMm)));
                      return;
                    }
                    const otherPanel = hasRightPanel ? Math.round(rightPanelWidthMm) : 0;
                    const resultingDoor = Math.round(width) - parsed - otherPanel;
                    if (parsed < MIN_PANEL_WIDTH) {
                      setLeftPanelWidthInput(String(Math.round(leftPanelWidthMm)));
                      setDimensionError(`Side panel width must be at least ${MIN_PANEL_WIDTH} mm.`);
                      return;
                    }
                    if (resultingDoor > MAX_DOOR_LEAF) {
                      setLeftPanelWidthInput(String(Math.round(leftPanelWidthMm)));
                      setDimensionError(`Main door panel must not exceed ${MAX_DOOR_LEAF} mm. Increase the side panel width.`);
                      return;
                    }
                    if (resultingDoor < MIN_DOOR_LEAF) {
                      setLeftPanelWidthInput(String(Math.round(leftPanelWidthMm)));
                      setDimensionError(`Main door panel must be at least ${MIN_DOOR_LEAF} mm. Decrease the side panel width.`);
                      return;
                    }
                    setDimensionError(null);
                    onLeftPanelWidthChange(parsed);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#2C2C2C',
                    outline: 'none',
                    padding: 0,
                    margin: 0,
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                    width: getDimensionInputWidth(leftPanelWidthInput, subDimFontScale),
                    minWidth: getDimensionInputWidth(leftPanelWidthInput, subDimFontScale),
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
              </div>
            </foreignObject>
          </g>
        )}

        {hasRightPanel && (
          <g>
            {/* Right Panel Width Measurement */}
            <line
              x1={startX + scaledWidth - rightPanelWidth}
              y1={startY + scaledHeight + 10}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight + 10}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={startX + scaledWidth - rightPanelWidth}
              y1={startY + scaledHeight + 5}
              x2={startX + scaledWidth - rightPanelWidth}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={startX + scaledWidth}
              y1={startY + scaledHeight + 5}
              x2={startX + scaledWidth}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <foreignObject
              x={startX + scaledWidth - rightPanelWidth / 2 - getDimensionLabelBoxWidth(rightPanelWidthInput, subDimFontScale) / 2}
              y={startY + scaledHeight + 15}
              width={getDimensionLabelBoxWidth(rightPanelWidthInput, subDimFontScale)}
              height="24"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                <input
                  type="number"
                  value={rightPanelWidthInput}
                  onChange={(e) => setRightPanelWidthInput(e.target.value)}
                  onBlur={() => {
                    if (!onRightPanelWidthChange) return;
                    const parsed = parseInt(rightPanelWidthInput, 10);
                    if (Number.isNaN(parsed)) {
                      setRightPanelWidthInput(String(Math.round(rightPanelWidthMm)));
                      return;
                    }
                    const otherPanel = hasLeftPanel ? Math.round(leftPanelWidthMm) : 0;
                    const resultingDoor = Math.round(width) - parsed - otherPanel;
                    if (parsed < MIN_PANEL_WIDTH) {
                      setRightPanelWidthInput(String(Math.round(rightPanelWidthMm)));
                      setDimensionError(`Side panel width must be at least ${MIN_PANEL_WIDTH} mm.`);
                      return;
                    }
                    if (resultingDoor > MAX_DOOR_LEAF) {
                      setRightPanelWidthInput(String(Math.round(rightPanelWidthMm)));
                      setDimensionError(`Main door panel must not exceed ${MAX_DOOR_LEAF} mm. Increase the side panel width.`);
                      return;
                    }
                    if (resultingDoor < MIN_DOOR_LEAF) {
                      setRightPanelWidthInput(String(Math.round(rightPanelWidthMm)));
                      setDimensionError(`Main door panel must be at least ${MIN_DOOR_LEAF} mm. Decrease the side panel width.`);
                      return;
                    }
                    setDimensionError(null);
                    onRightPanelWidthChange(parsed);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#2C2C2C',
                    outline: 'none',
                    padding: 0,
                    margin: 0,
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                    width: getDimensionInputWidth(rightPanelWidthInput, subDimFontScale),
                    minWidth: getDimensionInputWidth(rightPanelWidthInput, subDimFontScale),
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
              </div>
            </foreignObject>
          </g>
        )}

        {/* Door Section Width Measurement - when panels are present */}
        {(hasLeftPanel || hasRightPanel) && (
          <g>
            <line
              x1={doorSectionX}
              y1={startY + scaledHeight + 10}
              x2={doorSectionX + doorSectionWidth}
              y2={startY + scaledHeight + 10}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={doorSectionX}
              y1={startY + scaledHeight + 5}
              x2={doorSectionX}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <line
              x1={doorSectionX + doorSectionWidth}
              y1={startY + scaledHeight + 5}
              x2={doorSectionX + doorSectionWidth}
              y2={startY + scaledHeight + 15}
              stroke="#2C2C2C"
              strokeWidth="1.5"
            />
            <foreignObject
              x={doorSectionX + doorSectionWidth / 2 - getDimensionLabelBoxWidth(doorSectionWidthInput, subDimFontScale) / 2}
              y={startY + scaledHeight + 15}
              width={getDimensionLabelBoxWidth(doorSectionWidthInput, subDimFontScale)}
              height="24"
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                <input
                  type="number"
                  value={doorSectionWidthInput}
                  onChange={(e) => setDoorSectionWidthInput(e.target.value)}
                  onBlur={() => {
                    if (!onDoorSectionWidthChange) return;
                    const parsed = parseInt(doorSectionWidthInput, 10);
                    if (Number.isNaN(parsed)) {
                      setDoorSectionWidthInput(String(Math.round(doorSectionWidthMm)));
                      return;
                    }
                    if (parsed > MAX_DOOR_LEAF) {
                      setDoorSectionWidthInput(String(Math.round(doorSectionWidthMm)));
                      setDimensionError(`Main door panel must not exceed ${MAX_DOOR_LEAF} mm.`);
                      return;
                    }
                    if (parsed < MIN_DOOR_LEAF) {
                      setDoorSectionWidthInput(String(Math.round(doorSectionWidthMm)));
                      setDimensionError(`Main door panel must be at least ${MIN_DOOR_LEAF} mm.`);
                      return;
                    }
                    setDimensionError(null);
                    onDoorSectionWidthChange(parsed);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#2C2C2C',
                    outline: 'none',
                    padding: 0,
                    margin: 0,
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                    width: getDimensionInputWidth(doorSectionWidthInput, subDimFontScale),
                    minWidth: getDimensionInputWidth(doorSectionWidthInput, subDimFontScale),
                    flexShrink: 0,
                  }}
                />
                <span style={{ fontSize: '12px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7, flexShrink: 0 }}>mm</span>
              </div>
            </foreignObject>
          </g>
        )}

        {/* Sub Rulers for Voordeur Style 1 (vertical section heights) */}
        {isVoordeur && doorStyle === 'style-1' && (() => {
          const verticalSubRulerCount = middleSectionParts;
          const subRulerSpanTop = innerFrameY;
          const subRulerSpanBottom = innerFrameY + innerFrameHeight;
          const subRulerSegmentHeight = (subRulerSpanBottom - subRulerSpanTop) / verticalSubRulerCount;
          const subRulerSegmentHeightMm = subRulerSegmentHeight / scale;
          
          return (
            <g>
              {/* Vertical sub rulers — equally spaced along inner frame height */}
              {Array.from({ length: verticalSubRulerCount }, (_, i) => {
                const sectionY = subRulerSpanTop + subRulerSegmentHeight * i;
                const sectionYEnd = subRulerSpanTop + subRulerSegmentHeight * (i + 1);
                
                return (
                  <g key={i}>
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                      y1={sectionY}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                      y2={sectionYEnd}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                      y1={sectionY}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                      y2={sectionY}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                      y1={sectionYEnd}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                      y2={sectionYEnd}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <foreignObject x={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5} y={(sectionY + sectionYEnd) / 2 - 12} width="60" height="24">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '14px', fontWeight: 600, color: '#2C2C2C' }}>
                        {Math.round(subRulerSegmentHeightMm)}
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </g>
          );
        })()}
        
        {isVoordeur && doorStyle === 'style-5' && (() => {
          const horizontalDividerY = glassY + (glassHeight / 2) - (innerFrameThickness / 2);
          const topPartHeight = glassHeight / 2;
          const topPartHeightMm = topPartHeight / scale;
          
          return (
            <g>
              {/* Top part height ruler (glass section) - positioned at same position as style-1/3 sub rulers */}
              <line
                x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                y1={glassY}
                x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                y2={horizontalDividerY}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              <line
                x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                y1={glassY}
                x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                y2={glassY}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              <line
                x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                y1={horizontalDividerY}
                x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                y2={horizontalDividerY}
                stroke="#2C2C2C"
                strokeWidth="1.5"
              />
              <foreignObject x={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5} y={(glassY + horizontalDividerY) / 2 - 12} width="60" height="24">
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '14px', fontWeight: 600, color: '#2C2C2C' }}>
                  {Math.round(topPartHeightMm)}
                </div>
              </foreignObject>
            </g>
          );
        })()}
        
        {isVoordeur && doorStyle === 'style-6' && (() => {
          // Style-6: Horizontal dividers creating 3 or 4 parts
          const dividerThickness = innerFrameThickness;
          const numberOfDividers = style6Parts - 1;
          const sectionHeight = glassHeight / style6Parts;
          const sectionHeightMm = (sectionHeight / scale);
          
          return (
            <g>
              {Array.from({ length: style6Parts }, (_, i) => {
                const sectionY = glassY + (sectionHeight * i);
                const sectionYEnd = sectionY + sectionHeight;
                
                return (
                  <g key={i}>
                    {/* Section height ruler - positioned at same position as style-1/3 sub rulers, label after ruler */}
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                      y1={sectionY}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset}
                      y2={sectionYEnd}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                      y1={sectionY}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                      y2={sectionY}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <line
                      x1={startX + scaledWidth + voordeurSubRulersSubRulerOffset - 5}
                      y1={sectionYEnd}
                      x2={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5}
                      y2={sectionYEnd}
                      stroke="#2C2C2C"
                      strokeWidth="1.5"
                    />
                    <foreignObject x={startX + scaledWidth + voordeurSubRulersSubRulerOffset + 5} y={(sectionY + sectionYEnd) / 2 - 12} width="60" height="24">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '14px', fontWeight: 600, color: '#2C2C2C' }}>
                        {Math.round(sectionHeightMm)}
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </g>
          );
        })()}

        {/* Sub Rulers for Inner Frames (for sliding doors) */}
        {doorType === 'sliding' && isDoublePane && leftInnerFrameX !== null && leftInnerFrameWidth !== null && (() => {
          // Calculate inner frame width in mm
          const innerFrameWidthMm = Math.round(leftInnerFrameWidth / scale);
          const subRulerY = startY + scaledHeight + 25; // Position above main width ruler (which is at +50)
          
          return (
            <>
              {/* Left Inner Frame Width Measurement */}
              <g>
                <line
                  x1={leftInnerFrameX}
                  y1={subRulerY}
                  x2={leftInnerFrameX + leftInnerFrameWidth}
                  y2={subRulerY}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                <line
                  x1={leftInnerFrameX}
                  y1={subRulerY - 5}
                  x2={leftInnerFrameX}
                  y2={subRulerY + 5}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                <line
                  x1={leftInnerFrameX + leftInnerFrameWidth}
                  y1={subRulerY - 5}
                  x2={leftInnerFrameX + leftInnerFrameWidth}
                  y2={subRulerY + 5}
                  stroke="#2C2C2C"
                  strokeWidth="1.5"
                />
                <foreignObject
                  x={leftInnerFrameX + leftInnerFrameWidth / 2 - getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7) / 2}
                  y={subRulerY - 20}
                  width={getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7)}
                  height="20"
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C' }}>
                      {innerFrameWidthMm}
                    </span>
                    <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                  </div>
                </foreignObject>
              </g>

              {/* Right Inner Frame Width Measurement */}
              {rightInnerFrameX !== null && rightInnerFrameWidth !== null && (
                <g>
                  <line
                    x1={rightInnerFrameX}
                    y1={subRulerY}
                    x2={rightInnerFrameX + rightInnerFrameWidth}
                    y2={subRulerY}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={rightInnerFrameX}
                    y1={subRulerY - 5}
                    x2={rightInnerFrameX}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={rightInnerFrameX + rightInnerFrameWidth}
                    y1={subRulerY - 5}
                    x2={rightInnerFrameX + rightInnerFrameWidth}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <foreignObject
                    x={rightInnerFrameX + rightInnerFrameWidth / 2 - getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7) / 2}
                    y={subRulerY - 20}
                    width={getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7)}
                    height="20"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C' }}>
                        {innerFrameWidthMm}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                    </div>
                  </foreignObject>
                </g>
              )}

              {/* Center Inner Frame Width Measurement (only when slidingPosition === 'center') */}
              {needsCenterFrame && centerInnerFrameX !== null && centerInnerFrameWidth !== null && (
                <g>
                  <line
                    x1={centerInnerFrameX}
                    y1={subRulerY}
                    x2={centerInnerFrameX + centerInnerFrameWidth}
                    y2={subRulerY}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={centerInnerFrameX}
                    y1={subRulerY - 5}
                    x2={centerInnerFrameX}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={centerInnerFrameX + centerInnerFrameWidth}
                    y1={subRulerY - 5}
                    x2={centerInnerFrameX + centerInnerFrameWidth}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <foreignObject
                    x={centerInnerFrameX + centerInnerFrameWidth / 2 - getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7) / 2}
                    y={subRulerY - 20}
                    width={getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7)}
                    height="20"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C' }}>
                        {innerFrameWidthMm}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                    </div>
                  </foreignObject>
                </g>
              )}

              {/* Third Inner Frame Width Measurement (only when slidingPosition === 'right' or 'center') */}
              {(needsThirdFrame || needsCenterFrame) && thirdInnerFrameX !== null && thirdInnerFrameWidth !== null && (
                <g>
                  <line
                    x1={thirdInnerFrameX}
                    y1={subRulerY}
                    x2={thirdInnerFrameX + thirdInnerFrameWidth}
                    y2={subRulerY}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={thirdInnerFrameX}
                    y1={subRulerY - 5}
                    x2={thirdInnerFrameX}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <line
                    x1={thirdInnerFrameX + thirdInnerFrameWidth}
                    y1={subRulerY - 5}
                    x2={thirdInnerFrameX + thirdInnerFrameWidth}
                    y2={subRulerY + 5}
                    stroke="#2C2C2C"
                    strokeWidth="1.5"
                  />
                  <foreignObject
                    x={thirdInnerFrameX + thirdInnerFrameWidth / 2 - getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7) / 2}
                    y={subRulerY - 20}
                    width={getDimensionLabelBoxWidth(innerFrameWidthMm, 0.7)}
                    height="20"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C' }}>
                        {innerFrameWidthMm}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 500, color: '#2C2C2C', opacity: 0.7 }}>mm</span>
                    </div>
                  </foreignObject>
                </g>
              )}
            </>
          );
        })()}
      </svg>
      
      {/* Settings Dialog for Overhead Parts */}
      {(selectedOverheadPartIndex !== null) && (
        <Dialog open={settingsOpen} onOpenChange={(open) => {
          setSettingsOpen(open);
          if (!open) {
            setSelectedOverheadPartIndex(null); // Reset when closing
          }
        }}>
          <DialogContent className="max-w-md max-h-[60vh]" style={{ overflowY: 'auto' }}>
            <DialogHeader>
              <DialogTitle>
                {t('drawing.overheadPartSettings', { number: selectedOverheadPartIndex + 1 })}
              </DialogTitle>
              <DialogDescription>
                {t('drawing.configureOverheadPart', { number: selectedOverheadPartIndex + 1 })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor={`overhead-part-${selectedOverheadPartIndex}`} className="text-sm font-semibold text-gray-700">
                  {t('drawing.windowType')}
                </Label>
                <Select
                  value={currentOverheadPartWindowTypes[selectedOverheadPartIndex] || 'fixed-sash'}
                  onValueChange={(value) => {
                    if (onOverheadPartWindowTypeChange) {
                      onOverheadPartWindowTypeChange(selectedOverheadPartIndex, value);
                    }
                    setSettingsOpen(false); // Close modal after selection
                  }}
                >
                  <SelectTrigger id={`overhead-part-${selectedOverheadPartIndex}`} className="w-full">
                    <SelectValue placeholder={t('drawing.selectWindowType')}>
                      {currentOverheadPartWindowTypes[selectedOverheadPartIndex] ? translateWindowTypeName(currentOverheadPartWindowTypes[selectedOverheadPartIndex] || 'fixed-sash') : t('drawing.selectWindowType')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {raamtypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center space-x-2">
                          {type.image && (
                            <img 
                              src={type.image} 
                              alt={translateWindowTypeName(type.id)}
                              className="w-6 h-6 object-contain"
                            />
                          )}
                          <span>{translateWindowTypeName(type.id)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings Dialog for Left Panel */}
      {selectedLeftPanel && (
        <Dialog open={leftPanelSettingsOpen} onOpenChange={(open) => {
          setLeftPanelSettingsOpen(open);
          if (!open) {
            setSelectedLeftPanel(false); // Reset when closing
          }
        }}>
          <DialogContent className="max-w-md max-h-[60vh]" style={{ overflowY: 'auto' }}>
            <DialogHeader>
              <DialogTitle>
                {t('drawing.leftPanelSettings')}
              </DialogTitle>
              <DialogDescription>
                {t('drawing.configureLeftPanel')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="left-panel-window-type" className="text-sm font-semibold text-gray-700">
                  {t('drawing.windowType')}
                </Label>
                <Select
                  value={leftPanelWindowType}
                  onValueChange={(value) => {
                    if (onLeftPanelWindowTypeChange) {
                      onLeftPanelWindowTypeChange(value);
                    }
                    setLeftPanelSettingsOpen(false); // Close modal after selection
                  }}
                >
                  <SelectTrigger id="left-panel-window-type" className="w-full">
                    <SelectValue placeholder={t('drawing.selectWindowType')}>
                      {leftPanelWindowType ? translateWindowTypeName(leftPanelWindowType) : t('drawing.selectWindowType')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {raamtypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center space-x-2">
                          {type.image && (
                            <img 
                              src={type.image} 
                              alt={translateWindowTypeName(type.id)}
                              className="w-6 h-6 object-contain"
                            />
                          )}
                          <span>{translateWindowTypeName(type.id)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sidePanelOverheadEnabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="left-panel-overhead" className="text-sm font-semibold text-gray-700">
                      {t('drawing.overhead')}
                    </Label>
                    <Select
                      value={leftPanelHasOverhead ? 'with-overhead' : 'no-overhead'}
                      onValueChange={(value) => {
                        if (onLeftPanelOverheadChange) {
                          onLeftPanelOverheadChange(value === 'with-overhead');
                        }
                      }}
                    >
                      <SelectTrigger id="left-panel-overhead" className="w-full">
                        <SelectValue>
                          {leftPanelHasOverhead ? t('common.yes') : t('options.noOverhead')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-overhead">{t('options.noOverhead')}</SelectItem>
                        <SelectItem value="with-overhead">{t('common.yes')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {leftPanelHasOverhead && (
                    <div className="space-y-2">
                      <Label htmlFor="left-panel-overhead-window-type" className="text-sm font-semibold text-gray-700">
                        {t('drawing.windowType')} ({t('drawing.overhead')})
                      </Label>
                      <Select
                        value={leftPanelOverheadWindowTypeProp}
                        onValueChange={(value) => {
                          if (onLeftPanelOverheadWindowTypeChange) {
                            onLeftPanelOverheadWindowTypeChange(value);
                          }
                        }}
                      >
                        <SelectTrigger id="left-panel-overhead-window-type" className="w-full">
                          <SelectValue>
                            {translateWindowTypeName(leftPanelOverheadWindowTypeProp)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {raamtypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              <div className="flex items-center space-x-2">
                                {type.image && (
                                  <img
                                    src={type.image}
                                    alt={translateWindowTypeName(type.id)}
                                    className="w-6 h-6 object-contain"
                                  />
                                )}
                                <span>{translateWindowTypeName(type.id)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="left-panel-parapet" className="text-sm font-semibold text-gray-700">
                  {t('drawing.parapet') || 'Parapet'}
                </Label>
                <Select
                  value={leftPanelHasParapet ? 'with-parapet' : 'no-parapet'}
                  onValueChange={(value) => {
                    if (onLeftPanelParapetChange) {
                      onLeftPanelParapetChange(value === 'with-parapet');
                    }
                  }}
                >
                  <SelectTrigger id="left-panel-parapet" className="w-full">
                    <SelectValue>
                      {leftPanelHasParapet ? t('common.yes') : t('common.no')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-parapet">{t('common.no')}</SelectItem>
                    <SelectItem value="with-parapet">{t('common.yes')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {leftPanelHasParapet && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">
                      {t('drawing.parapetColor') || 'Parapet Color'}
                    </Label>
                    <ColorPicker
                      selectedColor={leftPanelParapetColorProp || parapetColor || interiorColor}
                      onColorChange={(colorId) => {
                        if (onLeftPanelParapetColorChange) {
                          onLeftPanelParapetColorChange(colorId);
                        }
                      }}
                      showSearch={true}
                      maxVisible={8}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">
                      {t('options.panelType') || 'Panel Type'}
                    </Label>
                    <CardDescription className="text-xs mt-0.5 mb-2">
                      {t('options.panelTypeDescription') || 'Which type of panel do you want to apply? Note that the inside is always smooth.'}
                    </CardDescription>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'sandwich-no-mullions', name: 'Sandwich Panel (No Mullions)' },
                        { id: 'glass', name: 'Glass' },
                        { id: 'horizontal-mullions', name: 'Horizontal Mullions' },
                        { id: 'vertical-mullions', name: 'Vertical Mullions' }
                      ].map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => {
                            if (onLeftPanelParapetPanelTypeChange) {
                              onLeftPanelParapetPanelTypeChange(type.id);
                            }
                          }}
                          className={`p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                            leftPanelParapetPanelType === type.id
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {type.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Settings Dialog for Right Panel */}
      {selectedRightPanel && (
        <Dialog open={rightPanelSettingsOpen} onOpenChange={(open) => {
          setRightPanelSettingsOpen(open);
          if (!open) {
            setSelectedRightPanel(false); // Reset when closing
          }
        }}>
          <DialogContent className="max-w-md max-h-[60vh]" style={{ overflowY: 'auto' }}>
            <DialogHeader>
              <DialogTitle>
                {t('drawing.rightPanelSettings')}
              </DialogTitle>
              <DialogDescription>
                {t('drawing.configureRightPanel')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="right-panel-window-type" className="text-sm font-semibold text-gray-700">
                  {t('drawing.windowType')}
                </Label>
                <Select
                  value={rightPanelWindowType}
                  onValueChange={(value) => {
                    if (onRightPanelWindowTypeChange) {
                      onRightPanelWindowTypeChange(value);
                    }
                    setRightPanelSettingsOpen(false); // Close modal after selection
                  }}
                >
                  <SelectTrigger id="right-panel-window-type" className="w-full">
                    <SelectValue placeholder={t('drawing.selectWindowType')}>
                      {rightPanelWindowType ? translateWindowTypeName(rightPanelWindowType) : t('drawing.selectWindowType')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {raamtypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center space-x-2">
                          {type.image && (
                            <img 
                              src={type.image} 
                              alt={translateWindowTypeName(type.id)}
                              className="w-6 h-6 object-contain"
                            />
                          )}
                          <span>{translateWindowTypeName(type.id)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sidePanelOverheadEnabled && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="right-panel-overhead" className="text-sm font-semibold text-gray-700">
                      {t('drawing.overhead')}
                    </Label>
                    <Select
                      value={rightPanelHasOverhead ? 'with-overhead' : 'no-overhead'}
                      onValueChange={(value) => {
                        if (onRightPanelOverheadChange) {
                          onRightPanelOverheadChange(value === 'with-overhead');
                        }
                      }}
                    >
                      <SelectTrigger id="right-panel-overhead" className="w-full">
                        <SelectValue>
                          {rightPanelHasOverhead ? t('common.yes') : t('options.noOverhead')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no-overhead">{t('options.noOverhead')}</SelectItem>
                        <SelectItem value="with-overhead">{t('common.yes')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {rightPanelHasOverhead && (
                    <div className="space-y-2">
                      <Label htmlFor="right-panel-overhead-window-type" className="text-sm font-semibold text-gray-700">
                        {t('drawing.windowType')} ({t('drawing.overhead')})
                      </Label>
                      <Select
                        value={rightPanelOverheadWindowTypeProp}
                        onValueChange={(value) => {
                          if (onRightPanelOverheadWindowTypeChange) {
                            onRightPanelOverheadWindowTypeChange(value);
                          }
                        }}
                      >
                        <SelectTrigger id="right-panel-overhead-window-type" className="w-full">
                          <SelectValue>
                            {translateWindowTypeName(rightPanelOverheadWindowTypeProp)}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {raamtypes.map(type => (
                            <SelectItem key={type.id} value={type.id}>
                              <div className="flex items-center space-x-2">
                                {type.image && (
                                  <img
                                    src={type.image}
                                    alt={translateWindowTypeName(type.id)}
                                    className="w-6 h-6 object-contain"
                                  />
                                )}
                                <span>{translateWindowTypeName(type.id)}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="space-y-2">
                <Label htmlFor="right-panel-parapet" className="text-sm font-semibold text-gray-700">
                  {t('drawing.parapet') || 'Parapet'}
                </Label>
                <Select
                  value={rightPanelHasParapet ? 'with-parapet' : 'no-parapet'}
                  onValueChange={(value) => {
                    if (onRightPanelParapetChange) {
                      onRightPanelParapetChange(value === 'with-parapet');
                    }
                  }}
                >
                  <SelectTrigger id="right-panel-parapet" className="w-full">
                    <SelectValue>
                      {rightPanelHasParapet ? t('common.yes') : t('common.no')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-parapet">{t('common.no')}</SelectItem>
                    <SelectItem value="with-parapet">{t('common.yes')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {rightPanelHasParapet && (
                <>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">
                      {t('drawing.parapetColor') || 'Parapet Color'}
                    </Label>
                    <ColorPicker
                      selectedColor={rightPanelParapetColorProp || parapetColor || interiorColor}
                      onColorChange={(colorId) => {
                        if (onRightPanelParapetColorChange) {
                          onRightPanelParapetColorChange(colorId);
                        }
                      }}
                      showSearch={true}
                      maxVisible={8}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-gray-700">
                      {t('options.panelType') || 'Panel Type'}
                    </Label>
                    <CardDescription className="text-xs mt-0.5 mb-2">
                      {t('options.panelTypeDescription') || 'Which type of panel do you want to apply? Note that the inside is always smooth.'}
                    </CardDescription>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'sandwich-no-mullions', name: 'Sandwich Panel (No Mullions)' },
                        { id: 'glass', name: 'Glass' },
                        { id: 'horizontal-mullions', name: 'Horizontal Mullions' },
                        { id: 'vertical-mullions', name: 'Vertical Mullions' }
                      ].map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => {
                            if (onRightPanelParapetPanelTypeChange) {
                              onRightPanelParapetPanelTypeChange(type.id);
                            }
                          }}
                          className={`p-2 rounded-lg border-2 transition-all text-xs font-medium ${
                            rightPanelParapetPanelType === type.id
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          {type.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Inner Frame Sliding Section Modal - using native select to avoid ref issues */}
      {selectedInnerFrame !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedInnerFrame(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {selectedInnerFrame === 'left' ? t('options.leftInnerFrameSettings') : t('options.rightInnerFrameSettings')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedInnerFrame === 'left' 
                    ? t('options.configureSlidingSectionLeftInnerFrame')
                    : t('options.configureSlidingSectionRightInnerFrame')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="inner-frame-select-native" className="block text-sm font-semibold text-gray-700">
                  {t('options.slidingSectionPosition')}
                </label>
                <select
                  id="inner-frame-select-native"
                  value={innerFrameSlidingSection}
                  onChange={(e) => {
                    setInnerFrameSlidingSection(e.target.value);
                    setSelectedInnerFrame(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="left">{t('options.leftPart')}</option>
                  <option value="right">{t('options.rightPart')}</option>
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="ventilator-sliding-select" className="block text-sm font-semibold text-gray-700">
                  {t('options.ventilator')}
                </label>
                <select
                  id="ventilator-sliding-select"
                  value={selectedInnerFrame === 'left' ? (ventilatorSlidingLeft ? 'with' : 'none') : (ventilatorSlidingRight ? 'with' : 'none')}
                  onChange={(e) => {
                    if (selectedInnerFrame === 'left') {
                      setVentilatorSlidingLeft(e.target.value === 'with');
                    } else {
                      setVentilatorSlidingRight(e.target.value === 'with');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">{t('options.noVentilator')}</option>
                  <option value="with">{t('options.withVentilator')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inner Frame Sliding Section Modal for Center - using native select to avoid ref issues */}
      {selectedInnerFrameCenter !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setSelectedInnerFrameCenter(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {selectedInnerFrameCenter === 'left' ? t('options.leftSectionSettings') : t('options.rightSectionSettings')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedInnerFrameCenter === 'left' 
                    ? t('options.configureSlidingSectionLeftSection')
                    : t('options.configureSlidingSectionRightSection')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="inner-frame-select-center-native" className="block text-sm font-semibold text-gray-700">
                  {t('options.slidingSectionPosition')}
                </label>
                <select
                  id="inner-frame-select-center-native"
                  value={selectedInnerFrameCenter === 'left' ? innerFrameSlidingSectionLeft : innerFrameSlidingSectionRight}
                  onChange={(e) => {
                    if (selectedInnerFrameCenter === 'left') {
                      setInnerFrameSlidingSectionLeft(e.target.value);
                    } else {
                      setInnerFrameSlidingSectionRight(e.target.value);
                    }
                    setSelectedInnerFrameCenter(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="left">{t('options.leftPart')}</option>
                  <option value="right">{t('options.rightPart')}</option>
                </select>
              </div>
              {selectedInnerFrameCenter === 'left' && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="ventilator-sliding-center-left-select" className="block text-sm font-semibold text-gray-700">
                      {t('options.ventilatorLeftFrame')}
                    </label>
                    <select
                      id="ventilator-sliding-center-left-select"
                      value={ventilatorSlidingCenterLeft ? 'with' : 'none'}
                      onChange={(e) => {
                        setVentilatorSlidingCenterLeft(e.target.value === 'with');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">{t('options.noVentilator')}</option>
                      <option value="with">{t('options.withVentilator')}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="ventilator-sliding-center-right-select" className="block text-sm font-semibold text-gray-700">
                      {t('options.ventilatorRightFrame')}
                    </label>
                    <select
                      id="ventilator-sliding-center-right-select"
                      value={ventilatorSlidingCenterRight ? 'with' : 'none'}
                      onChange={(e) => {
                        setVentilatorSlidingCenterRight(e.target.value === 'with');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">{t('options.noVentilator')}</option>
                      <option value="with">{t('options.withVentilator')}</option>
                    </select>
                  </div>
                </>
              )}
              {selectedInnerFrameCenter === 'right' && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="ventilator-sliding-center-center-select" className="block text-sm font-semibold text-gray-700">
                      {t('options.ventilatorCenterFrame')}
                    </label>
                    <select
                      id="ventilator-sliding-center-center-select"
                      value={ventilatorSlidingCenterCenter ? 'with' : 'none'}
                      onChange={(e) => {
                        setVentilatorSlidingCenterCenter(e.target.value === 'with');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">{t('options.noVentilator')}</option>
                      <option value="with">{t('options.withVentilator')}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="ventilator-sliding-center-third-select" className="block text-sm font-semibold text-gray-700">
                      {t('options.ventilatorThirdFrame')}
                    </label>
                    <select
                      id="ventilator-sliding-center-third-select"
                      value={ventilatorSlidingCenterThird ? 'with' : 'none'}
                      onChange={(e) => {
                        setVentilatorSlidingCenterThird(e.target.value === 'with');
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">{t('options.noVentilator')}</option>
                      <option value="with">{t('options.withVentilator')}</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Middle Section Parts Dialog - for voordeur style-1 and style-3 - using native select to avoid ref issues */}
      {selectedMiddleSection && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            setSelectedMiddleSection(false);
            setMiddleSectionSettingsOpen(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">
                  {isVoordeur && doorStyle === 'style-3' ? t('drawing.style3Type') : t('drawing.middleSectionParts')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {isVoordeur && doorStyle === 'style-3' 
                    ? t('drawing.style3TypeDesc')
                    : t('drawing.middleSectionPartsDesc')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="middle-section-select" className="block text-sm font-semibold text-gray-700">
                  {isVoordeur && doorStyle === 'style-3' ? t('drawing.type') : t('drawing.numberOfParts')}
                </label>
                {isVoordeur && doorStyle === 'style-3' ? (
                  <select
                    key={`style3-type-${language}`}
                    id="middle-section-select"
                    value={style3Type}
                    onChange={(e) => {
                      setStyle3Type(e.target.value);
                      setSelectedMiddleSection(false);
                      setMiddleSectionSettingsOpen(false);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="glass">{t('drawing.glass')}</option>
                    <option value="panel">{t('drawing.panel')}</option>
                  </select>
                ) : (
                  <select
                    key={`middle-section-parts-${language}`}
                    id="middle-section-select"
                    value={middleSectionParts}
                    onChange={(e) => {
                      const parts = parseInt(e.target.value, 10);
                      setMiddleSectionParts(parts);
                      onNumberOfPartsChange?.(parts);
                      setSelectedMiddleSection(false);
                      setMiddleSectionSettingsOpen(false);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="3">3 {t('drawing.parts')}</option>
                    <option value="4">4 {t('drawing.parts')}</option>
                  </select>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inner frame type dialog – voordeur style-2, terrasdeur & achterdeur */}
      {selectedStyle2 && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            setSelectedStyle2(false);
            setStyle2SettingsOpen(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">{t('drawing.style2Type')}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('drawing.style2TypeDesc')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="style2-type-select" className="block text-sm font-semibold text-gray-700">
                  {t('drawing.type')}
                </label>
                <select
                  key={`style2-type-${language}`}
                  id="style2-type-select"
                  value={style2Type}
                  onChange={(e) => {
                    const val = e.target.value;
                    setStyle2Type(val);
                    if (onGlassTextureChange) {
                      onGlassTextureChange(
                        val === 'panel' ? 'panel' : lastGlassTextureRef.current
                      );
                    }
                    setSelectedStyle2(false);
                    setStyle2SettingsOpen(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="glass">{t('drawing.glass')}</option>
                  <option value="panel">{t('drawing.panel')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Style 4 Left Section Type Dialog - for voordeur style-4 - using native select to avoid ref issues */}
      {selectedStyle4Left && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            setSelectedStyle4Left(false);
            setStyle4LeftSettingsOpen(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">{t('drawing.style4LeftSectionType')}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('drawing.style4LeftSectionTypeDesc')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="style4-left-type-select" className="block text-sm font-semibold text-gray-700">
                  {t('drawing.type')}
                </label>
                <select
                  key={`style4-left-type-${language}`}
                  id="style4-left-type-select"
                  value={style4LeftType}
                  onChange={(e) => {
                    setStyle4LeftType(e.target.value);
                    setSelectedStyle4Left(false);
                    setStyle4LeftSettingsOpen(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="glass">{t('drawing.glass')}</option>
                  <option value="panel">{t('drawing.panel')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Style 6 Parts Dialog - for voordeur style-6 - using native select to avoid ref issues */}
      {selectedStyle6 && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            setSelectedStyle6(false);
            setStyle6SettingsOpen(false);
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold">{t('drawing.style6Parts')}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('drawing.style6PartsDesc')}
                </p>
              </div>
              <div className="space-y-2">
                <label htmlFor="style6-parts-select" className="block text-sm font-semibold text-gray-700">
                  {t('drawing.numberOfParts')}
                </label>
                <select
                  key={`style6-parts-${language}`}
                  id="style6-parts-select"
                  value={style6Parts}
                  onChange={(e) => {
                    const parts = parseInt(e.target.value, 10);
                    setStyle6Parts(parts);
                    onNumberOfPartsChange?.(parts);
                    setSelectedStyle6(false);
                    setStyle6SettingsOpen(false);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="3">3 {t('drawing.parts')}</option>
                  <option value="4">4 {t('drawing.parts')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dimension validation toast */}
      {dimensionError && (
        <div className="fixed bottom-6 right-6 z-[60]">
          <div className="bg-slate-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 text-sm">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
              !
            </div>
            <span>{dimensionError}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoorDrawingBoard;
